{
  "version": "0.1.38",
  "components": [
    {
      "name": "Alert",
      "description": "Contextual feedback messages for user actions and system status",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "variant",
          "type": "'default' | 'destructive' | 'error' | 'success' | 'warning' | 'info'",
          "default": "'default'",
          "required": false,
          "description": "Alert style variant"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "variant",
          "values": [
            "default",
            "destructive",
            "error",
            "success",
            "warning",
            "info"
          ],
          "default": "default"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Alert>\n  <AlertTitle>Heads up!</AlertTitle>\n  <AlertDescription>This is a default alert message.</AlertDescription>\n</Alert>"
        },
        {
          "title": "Default",
          "description": "Neutral information message:",
          "code": "<Alert variant=\"default\">\n  <AlertTitle>Note</AlertTitle>\n  <AlertDescription>This is a neutral informational message.</AlertDescription>\n</Alert>"
        },
        {
          "title": "Success",
          "description": "Positive feedback for completed actions:",
          "code": "<Alert variant=\"success\">\n  <AlertTitle>Success!</AlertTitle>\n  <AlertDescription>Your changes have been saved successfully.</AlertDescription>\n</Alert>"
        },
        {
          "title": "Warning",
          "description": "Caution about potential issues:",
          "code": "<Alert variant=\"warning\">\n  <AlertTitle>Warning</AlertTitle>\n  <AlertDescription>Your session will expire in 5 minutes.</AlertDescription>\n</Alert>"
        },
        {
          "title": "Error / Destructive",
          "description": "Error messages and critical alerts:",
          "code": "<Alert variant=\"error\">\n  <AlertTitle>Error</AlertTitle>\n  <AlertDescription>There was a problem processing your request.</AlertDescription>\n</Alert>"
        },
        {
          "title": "Info",
          "description": "Helpful information or tips:",
          "code": "<Alert variant=\"info\">\n  <AlertTitle>Did you know?</AlertTitle>\n  <AlertDescription>You can use keyboard shortcuts for faster navigation.</AlertDescription>\n</Alert>"
        },
        {
          "title": "Form Validation Error",
          "code": "{#if formErrors.length > 0}\n  <Alert variant=\"error\">\n    <AlertTitle>Please fix the following errors:</AlertTitle>\n    <AlertDescription>\n      <ul class=\"list-disc pl-4 mt-2\">\n        {#each formErrors as error}\n          <li>{error}</li>\n        {/each}\n      </ul>\n    </AlertDescription>\n  </Alert>\n{/if}"
        },
        {
          "title": "Dismissible Alert",
          "code": "<script>\n  let showAlert = $state(true);\n</script>\n\n{#if showAlert}\n  <Alert variant=\"info\" class=\"relative\">\n    <AlertTitle>New Feature</AlertTitle>\n    <AlertDescription>Check out our new dashboard!</AlertDescription>\n    <button\n      class=\"absolute top-4 right-4 text-muted-foreground hover:text-foreground\"\n      onclick={() => (showAlert = false)}\n      aria-label=\"Dismiss\"\n    >\n      <svg class=\"h-4 w-4\" fill=\"none\" viewBox=\"0 0 24 24\" stroke=\"currentColor\">\n        <path\n          stroke-linecap=\"round\"\n          stroke-linejoin=\"round\"\n          stroke-width=\"2\"\n          d=\"M6 18L18 6M6 6l12 12\"\n        />\n      </svg>\n    </button>\n  </Alert>\n{/if}"
        },
        {
          "title": "System Status",
          "code": "<Alert variant=\"warning\">\n  <svg class=\"h-4 w-4\" fill=\"none\" viewBox=\"0 0 24 24\" stroke=\"currentColor\">\n    <path\n      stroke-linecap=\"round\"\n      stroke-linejoin=\"round\"\n      stroke-width=\"2\"\n      d=\"M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z\"\n    />\n  </svg>\n  <AlertTitle>Maintenance Scheduled</AlertTitle>\n  <AlertDescription>\n    The system will be unavailable on Sunday from 2:00 AM to 4:00 AM EST.\n  </AlertDescription>\n</Alert>"
        },
        {
          "title": "Success with Action",
          "code": "<Alert variant=\"success\">\n  <AlertTitle>Order Confirmed</AlertTitle>\n  <AlertDescription>\n    <p>Your order #12345 has been placed successfully.</p>\n    <a href=\"/orders/12345\" class=\"underline font-medium mt-2 inline-block\"> View order details </a>\n  </AlertDescription>\n</Alert>"
        },
        {
          "title": "Compact Alert (Title Only)",
          "code": "<Alert variant=\"info\">\n  <AlertDescription>Pro tip: Press Ctrl+K to open the command palette.</AlertDescription>\n</Alert>"
        }
      ],
      "relatedComponents": [
        "Toast",
        "Badge",
        "Card"
      ]
    },
    {
      "name": "AlertDescription",
      "description": "Main content text component for Alert messages",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Alert>\n  <AlertTitle>Notice</AlertTitle>\n  <AlertDescription>This is the alert message content.</AlertDescription>\n</Alert>"
        },
        {
          "title": "Simple Message",
          "code": "<AlertDescription>Your session will expire in 5 minutes.</AlertDescription>"
        },
        {
          "title": "With Link",
          "code": "<AlertDescription>\n  Your account needs verification.\n  <a href=\"/verify\" class=\"underline font-medium\">Verify now</a>\n</AlertDescription>"
        },
        {
          "title": "Error List",
          "code": "<AlertDescription>\n  <ul class=\"list-disc pl-4 mt-2\">\n    <li>Email is required</li>\n    <li>Password must be at least 8 characters</li>\n  </ul>\n</AlertDescription>"
        },
        {
          "title": "Rich Content",
          "code": "<AlertDescription>\n  <p>Your order #12345 has been placed successfully.</p>\n  <a href=\"/orders/12345\" class=\"underline font-medium mt-2 inline-block\"> View order details </a>\n</AlertDescription>"
        },
        {
          "title": "Without Title",
          "code": "<Alert variant=\"info\">\n  <AlertDescription>Pro tip: Press Ctrl+K to open the command palette.</AlertDescription>\n</Alert>"
        }
      ],
      "relatedComponents": [
        "Alert",
        "AlertTitle"
      ]
    },
    {
      "name": "AlertDialog",
      "description": "Modal dialog for confirmations and important actions",
      "category": "overlay",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "open",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Open state (bindable)"
        },
        {
          "name": "onOpenChange",
          "type": "(open: boolean) => void",
          "required": false,
          "description": "Open change callback"
        },
        {
          "name": "title",
          "type": "string",
          "required": false,
          "description": "Dialog title"
        },
        {
          "name": "description",
          "type": "string",
          "required": false,
          "description": "Description text"
        },
        {
          "name": "cancelText",
          "type": "string",
          "default": "'Cancel'",
          "required": false,
          "description": "Cancel button text"
        },
        {
          "name": "confirmText",
          "type": "string",
          "default": "'Continue'",
          "required": false,
          "description": "Confirm button text"
        },
        {
          "name": "confirmVariant",
          "type": "'default' | 'destructive' | 'outline' | 'ghost'",
          "default": "'default'",
          "required": false,
          "description": "Confirm button style"
        },
        {
          "name": "onConfirm",
          "type": "() => void",
          "required": false,
          "description": "Confirm callback"
        },
        {
          "name": "onCancel",
          "type": "() => void",
          "required": false,
          "description": "Cancel callback"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional classes"
        },
        {
          "name": "trigger",
          "type": "Snippet",
          "required": false,
          "description": "Optional trigger element"
        },
        {
          "name": "children",
          "type": "Snippet",
          "required": false,
          "description": "Custom content"
        }
      ],
      "variants": [
        {
          "name": "confirmVariant",
          "values": [
            "default",
            "destructive",
            "outline",
            "ghost"
          ],
          "default": "default"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let showDialog = $state(false);\n</script>\n\n<Button onclick={() => (showDialog = true)}>Delete Item</Button>\n\n<AlertDialog\n  open={showDialog}\n  onOpenChange={(open) => (showDialog = open)}\n  title=\"Delete Item?\"\n  description=\"This action cannot be undone. This will permanently delete the item.\"\n  confirmText=\"Delete\"\n  confirmVariant=\"destructive\"\n  onConfirm={() => deleteItem()}\n/>"
        },
        {
          "title": "Delete Confirmation",
          "code": "<AlertDialog\n  title=\"Delete Project\"\n  description={`Are you sure you want to delete \"${project.name}\"? All project data including files, tasks, and comments will be permanently removed.`}\n  confirmText=\"Delete Project\"\n  confirmVariant=\"destructive\"\n  onConfirm={() => deleteProject(project.id)}\n>\n  {#snippet trigger()}\n    <Button variant=\"destructive\" size=\"sm\">\n      <TrashIcon class=\"mr-2 h-4 w-4\" />\n      Delete\n    </Button>\n  {/snippet}\n</AlertDialog>"
        },
        {
          "title": "Unsaved Changes",
          "code": "<AlertDialog\n  title=\"Unsaved Changes\"\n  description=\"You have unsaved changes. Do you want to leave without saving?\"\n  cancelText=\"Stay\"\n  confirmText=\"Leave\"\n  confirmVariant=\"destructive\"\n  onConfirm={navigateAway}\n>\n  {#snippet trigger()}\n    <Button variant=\"outline\">Cancel</Button>\n  {/snippet}\n</AlertDialog>"
        },
        {
          "title": "Subscription Cancellation",
          "code": "<AlertDialog\n  title=\"Cancel Subscription\"\n  description=\"Your subscription will remain active until the end of the current billing period. You can resubscribe at any time.\"\n  cancelText=\"Keep Subscription\"\n  confirmText=\"Cancel Subscription\"\n  onConfirm={cancelSubscription}\n>\n  {#snippet trigger()}\n    <Button variant=\"ghost\" class=\"text-destructive\">Cancel Subscription</Button>\n  {/snippet}\n</AlertDialog>"
        },
        {
          "title": "Bulk Action",
          "code": "<AlertDialog\n  title={`Delete ${selectedItems.length} items?`}\n  description=\"This will permanently delete all selected items. This action cannot be undone.\"\n  confirmText={`Delete ${selectedItems.length} items`}\n  confirmVariant=\"destructive\"\n  onConfirm={deleteSelectedItems}\n>\n  {#snippet trigger()}\n    <Button variant=\"destructive\" disabled={selectedItems.length === 0}>Delete Selected</Button>\n  {/snippet}\n</AlertDialog>"
        }
      ],
      "relatedComponents": [
        "Dialog",
        "Button",
        "DropdownMenu"
      ]
    },
    {
      "name": "AlertTitle",
      "description": "Bold heading component for Alert messages",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Alert>\n  <AlertTitle>Heads up!</AlertTitle>\n  <AlertDescription>This is an important message.</AlertDescription>\n</Alert>"
        },
        {
          "title": "Success Alert",
          "code": "<Alert variant=\"success\">\n  <AlertTitle>Success!</AlertTitle>\n  <AlertDescription>Your changes have been saved.</AlertDescription>\n</Alert>"
        },
        {
          "title": "Error Alert",
          "code": "<Alert variant=\"error\">\n  <AlertTitle>Error</AlertTitle>\n  <AlertDescription>Something went wrong. Please try again.</AlertDescription>\n</Alert>"
        },
        {
          "title": "With Icon",
          "code": "<Alert variant=\"info\">\n  <Icon name=\"info\" class=\"h-4 w-4\" />\n  <AlertTitle>Did you know?</AlertTitle>\n  <AlertDescription>You can use keyboard shortcuts.</AlertDescription>\n</Alert>"
        },
        {
          "title": "Custom Styling",
          "code": "<AlertTitle class=\"text-lg\">Important Notice</AlertTitle>"
        }
      ],
      "relatedComponents": [
        "Alert",
        "AlertDescription"
      ]
    },
    {
      "name": "AppShell",
      "description": "Base wrapper component for applications with accessibility features",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "skipToId",
          "type": "string",
          "default": "'main-content'",
          "required": false,
          "description": "Target ID for skip link"
        },
        {
          "name": "skipToText",
          "type": "string",
          "default": "'Skip to main content'",
          "required": false,
          "description": "Skip link text"
        },
        {
          "name": "toastPosition",
          "type": "'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'",
          "default": "'bottom-right'",
          "required": false,
          "description": "Toast container position"
        },
        {
          "name": "children",
          "type": "Snippet",
          "required": false,
          "description": "Main content"
        }
      ],
      "variants": [
        {
          "name": "toastPosition",
          "values": [
            "top-right",
            "top-left",
            "bottom-right",
            "bottom-left",
            "top-center",
            "bottom-center"
          ],
          "default": "bottom-right"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<AppShell>\n  <main>Your application content</main>\n</AppShell>"
        }
      ],
      "relatedComponents": [
        "DashboardLayout",
        "PublicLayout",
        "Toast"
      ]
    },
    {
      "name": "AreaChart",
      "description": "Line chart with gradient fill and stacking support",
      "category": "chart",
      "importPath": "@classic-homes/charts-svelte",
      "props": [
        {
          "name": "data",
          "type": "AreaChartData",
          "required": true,
          "description": "Chart data with categories and series"
        },
        {
          "name": "smooth",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Use smooth curved lines"
        },
        {
          "name": "stacked",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Stack areas to show total composition"
        },
        {
          "name": "gradient",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Apply gradient fill to areas"
        },
        {
          "name": "onClick",
          "type": "(params: DataPointEventParams) => void",
          "required": false,
          "description": "Callback when clicked"
        },
        {
          "name": "title",
          "type": "string",
          "required": true,
          "description": "Chart title for accessibility"
        },
        {
          "name": "height",
          "type": "number | string",
          "default": "400",
          "required": false,
          "description": "Chart height"
        },
        {
          "name": "theme",
          "type": "'light' | 'dark' | 'auto'",
          "default": "'auto'",
          "required": false,
          "description": "Theme preference"
        },
        {
          "name": "showLegend",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show chart legend"
        },
        {
          "name": "showTooltip",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show tooltip on hover"
        },
        {
          "name": "showGrid",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show grid lines"
        }
      ],
      "variants": [
        {
          "name": "theme",
          "values": [
            "light",
            "dark",
            "auto"
          ],
          "default": "auto"
        }
      ],
      "slots": [],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { AreaChart } from '@classic-homes/charts-svelte';\n\n  const data = {\n    categories: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],\n    series: [\n      { name: 'Email', data: [120, 132, 101, 134, 90, 230, 210] },\n      { name: 'Ads', data: [220, 182, 191, 234, 290, 330, 310] },\n      { name: 'Video', data: [150, 232, 201, 154, 190, 330, 410] },\n    ],\n  };\n</script>\n\n<AreaChart title=\"Weekly Traffic\" {data} stacked smooth gradient height={400} />"
        },
        {
          "title": "Stacked Area with Gradient",
          "code": "<AreaChart title=\"Traffic Sources\" {data} stacked smooth gradient height={400} />"
        },
        {
          "title": "Simple Area Chart",
          "code": "<AreaChart\n  title=\"Single Series\"\n  data={{\n    categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],\n    series: [{ name: 'Revenue', data: [100, 150, 120, 180, 200] }],\n  }}\n  smooth\n/>"
        }
      ],
      "relatedComponents": [
        "LineChart",
        "BarChart"
      ]
    },
    {
      "name": "AuthLayout",
      "description": "Centered card layout for authentication pages",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "logo",
          "type": "Snippet",
          "required": false,
          "description": "Custom logo"
        },
        {
          "name": "logoSubtitle",
          "type": "string",
          "required": false,
          "description": "Subtitle for default logo"
        },
        {
          "name": "logoEnvironment",
          "type": "'local' | 'dev' | 'demo'",
          "required": false,
          "description": "Environment indicator"
        },
        {
          "name": "footerLinks",
          "type": "FooterLink[]",
          "default": "[]",
          "required": false,
          "description": "Footer links"
        },
        {
          "name": "footer",
          "type": "Snippet",
          "required": false,
          "description": "Custom footer content"
        },
        {
          "name": "showBackground",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Show decorative background"
        },
        {
          "name": "backgroundVariant",
          "type": "'default' | 'gradient' | 'pattern'",
          "default": "'default'",
          "required": false,
          "description": "Background style"
        },
        {
          "name": "maxWidth",
          "type": "'sm' | 'md' | 'lg'",
          "default": "'sm'",
          "required": false,
          "description": "Maximum card width"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional classes"
        },
        {
          "name": "children",
          "type": "Snippet",
          "required": false,
          "description": "Form content"
        }
      ],
      "variants": [
        {
          "name": "logoEnvironment",
          "values": [
            "local",
            "dev",
            "demo"
          ]
        },
        {
          "name": "backgroundVariant",
          "values": [
            "default",
            "gradient",
            "pattern"
          ],
          "default": "default"
        },
        {
          "name": "maxWidth",
          "values": [
            "sm",
            "md",
            "lg"
          ],
          "default": "sm"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<AuthLayout>\n  <h2 class=\"text-2xl font-bold mb-4\">Sign In</h2>\n  <form>\n    <Input label=\"Email\" type=\"email\" />\n    <Input label=\"Password\" type=\"password\" class=\"mt-4\" />\n    <Button class=\"mt-6 w-full\">Sign In</Button>\n  </form>\n</AuthLayout>"
        },
        {
          "title": "Gradient Background",
          "code": "<AuthLayout showBackground backgroundVariant=\"gradient\">\n  <h2>Create Account</h2>\n  <!-- Form content -->\n</AuthLayout>"
        },
        {
          "title": "Pattern Background",
          "code": "<AuthLayout showBackground backgroundVariant=\"pattern\">\n  <h2>Reset Password</h2>\n  <!-- Form content -->\n</AuthLayout>"
        },
        {
          "title": "Login Page",
          "code": "<script>\n  let email = $state('');\n  let password = $state('');\n  let loading = $state(false);\n\n  async function handleSubmit() {\n    loading = true;\n    // Handle login\n  }\n</script>\n\n<AuthLayout\n  logoSubtitle=\"Sign in to continue\"\n  footerLinks={[\n    { label: 'Privacy', href: '/privacy' },\n    { label: 'Terms', href: '/terms' },\n  ]}\n>\n  <form onsubmit={handleSubmit} class=\"space-y-4\">\n    <Input label=\"Email\" type=\"email\" bind:value={email} required />\n    <Input label=\"Password\" type=\"password\" bind:value={password} required />\n    <div class=\"flex items-center justify-between\">\n      <Checkbox label=\"Remember me\" />\n      <a href=\"/forgot-password\" class=\"text-sm text-primary\"> Forgot password? </a>\n    </div>\n    <Button type=\"submit\" class=\"w-full\" disabled={loading}>\n      {loading ? 'Signing in...' : 'Sign In'}\n    </Button>\n  </form>\n\n  <p class=\"mt-6 text-center text-sm text-muted-foreground\">\n    Don't have an account? <a href=\"/signup\" class=\"text-primary\">Sign up</a>\n  </p>\n</AuthLayout>"
        },
        {
          "title": "Registration Page",
          "code": "<AuthLayout maxWidth=\"md\" showBackground backgroundVariant=\"gradient\">\n  <h2 class=\"text-2xl font-bold mb-6\">Create your account</h2>\n\n  <form class=\"space-y-4\">\n    <div class=\"grid grid-cols-2 gap-4\">\n      <Input label=\"First Name\" required />\n      <Input label=\"Last Name\" required />\n    </div>\n    <Input label=\"Email\" type=\"email\" required />\n    <Input label=\"Password\" type=\"password\" required />\n    <Input label=\"Confirm Password\" type=\"password\" required />\n\n    <Checkbox>\n      I agree to the <a href=\"/terms\">Terms of Service</a>\n    </Checkbox>\n\n    <Button type=\"submit\" class=\"w-full\">Create Account</Button>\n  </form>\n</AuthLayout>"
        },
        {
          "title": "Two-Factor Authentication",
          "code": "<AuthLayout logoSubtitle=\"Verify your identity\">\n  <div class=\"text-center mb-6\">\n    <p class=\"text-muted-foreground\">Enter the 6-digit code from your authenticator app</p>\n  </div>\n\n  <OTPInput bind:value={code} autoFocus onComplete={verifyCode} />\n\n  <p class=\"mt-6 text-center text-sm text-muted-foreground\">\n    Can't access your authenticator? <a href=\"/recovery\" class=\"text-primary\">Use recovery code</a>\n  </p>\n</AuthLayout>"
        }
      ],
      "relatedComponents": [
        "Input",
        "Button",
        "OTPInput",
        "ErrorLayout"
      ]
    },
    {
      "name": "Avatar",
      "description": "User profile image with fallback support",
      "category": "core",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "size",
          "type": "'xs' | 'sm' | 'md' | 'lg' | 'xl'",
          "default": "'md'",
          "required": false,
          "description": "Size of the avatar"
        },
        {
          "name": "delayMs",
          "type": "number",
          "required": false,
          "description": "Delay before showing fallback"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "size",
          "values": [
            "xs",
            "sm",
            "md",
            "lg",
            "xl"
          ],
          "default": "md"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Avatar>\n  <AvatarImage src=\"/avatar.jpg\" alt=\"John Doe\" />\n  <AvatarFallback>JD</AvatarFallback>\n</Avatar>"
        },
        {
          "title": "Fallback with Delay",
          "description": "Prevent flash of fallback for slow-loading images:",
          "code": "<Avatar delayMs={500}>\n  <AvatarImage src=\"/slow-image.jpg\" alt=\"User\" />\n  <AvatarFallback>AB</AvatarFallback>\n</Avatar>"
        },
        {
          "title": "User Profile",
          "code": "<div class=\"flex items-center gap-3\">\n  <Avatar size=\"lg\">\n    <AvatarImage src={user.avatar} alt={user.name} />\n    <AvatarFallback>{getInitials(user.name)}</AvatarFallback>\n  </Avatar>\n  <div>\n    <p class=\"font-medium\">{user.name}</p>\n    <p class=\"text-sm text-muted-foreground\">{user.email}</p>\n  </div>\n</div>"
        },
        {
          "title": "Avatar Stack",
          "code": "<div class=\"flex -space-x-2\">\n  {#each users as user}\n    <Avatar size=\"sm\" class=\"border-2 border-background\">\n      <AvatarImage src={user.avatar} alt={user.name} />\n      <AvatarFallback>{getInitials(user.name)}</AvatarFallback>\n    </Avatar>\n  {/each}\n  {#if moreCount > 0}\n    <Avatar size=\"sm\" class=\"border-2 border-background\">\n      <AvatarFallback>+{moreCount}</AvatarFallback>\n    </Avatar>\n  {/if}\n</div>"
        },
        {
          "title": "Comment Avatar",
          "code": "<div class=\"flex gap-3\">\n  <Avatar size=\"sm\">\n    <AvatarImage src={comment.author.avatar} alt={comment.author.name} />\n    <AvatarFallback>{getInitials(comment.author.name)}</AvatarFallback>\n  </Avatar>\n  <div class=\"flex-1\">\n    <p class=\"font-medium text-sm\">{comment.author.name}</p>\n    <p class=\"text-sm\">{comment.text}</p>\n  </div>\n</div>"
        },
        {
          "title": "Navigation Avatar",
          "code": "<button class=\"flex items-center gap-2\">\n  <Avatar size=\"sm\">\n    <AvatarImage src={currentUser.avatar} alt={currentUser.name} />\n    <AvatarFallback>{getInitials(currentUser.name)}</AvatarFallback>\n  </Avatar>\n  <span class=\"text-sm\">{currentUser.name}</span>\n</button>"
        },
        {
          "title": "Colored Fallbacks",
          "code": "<script>\n  function getColorFromName(name: string): string {\n    const colors = ['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500'];\n    const index = name.charCodeAt(0) % colors.length;\n    return colors[index];\n  }\n</script>\n\n<Avatar>\n  <AvatarImage src={user.avatar} alt={user.name} />\n  <AvatarFallback class={getColorFromName(user.name)}>\n    {getInitials(user.name)}\n  </AvatarFallback>\n</Avatar>"
        }
      ],
      "relatedComponents": [
        "Badge",
        "Card"
      ]
    },
    {
      "name": "AvatarFallback",
      "description": "Fallback content displayed when Avatar image is unavailable",
      "category": "core",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Avatar>\n  <AvatarImage src=\"/user.jpg\" alt=\"John Doe\" />\n  <AvatarFallback>JD</AvatarFallback>\n</Avatar>"
        },
        {
          "title": "Initials",
          "code": "<Avatar>\n  <AvatarFallback>AB</AvatarFallback>\n</Avatar>"
        },
        {
          "title": "Single Letter",
          "code": "<Avatar>\n  <AvatarFallback>J</AvatarFallback>\n</Avatar>"
        },
        {
          "title": "With Icon",
          "code": "<Avatar>\n  <AvatarFallback>\n    <Icon name=\"user\" class=\"h-4 w-4\" />\n  </AvatarFallback>\n</Avatar>"
        },
        {
          "title": "Custom Styling",
          "code": "<Avatar>\n  <AvatarFallback class=\"bg-primary text-primary-foreground\">AD</AvatarFallback>\n</Avatar>"
        },
        {
          "title": "As Placeholder",
          "description": "When no image is available:",
          "code": "<Avatar>\n  <AvatarFallback>?</AvatarFallback>\n</Avatar>"
        }
      ],
      "relatedComponents": [
        "Avatar",
        "AvatarImage"
      ]
    },
    {
      "name": "AvatarImage",
      "description": "Image component for displaying user profile pictures in Avatars",
      "category": "core",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "src",
          "type": "string",
          "required": false,
          "description": "Image source URL (required)"
        },
        {
          "name": "alt",
          "type": "string",
          "required": false,
          "description": "Alt text for accessibility"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Avatar>\n  <AvatarImage src=\"/user-photo.jpg\" alt=\"John Doe\" />\n  <AvatarFallback>JD</AvatarFallback>\n</Avatar>"
        },
        {
          "title": "With Fallback",
          "code": "<Avatar>\n  <AvatarImage src=\"/avatar.jpg\" alt=\"Jane Smith\" />\n  <AvatarFallback>JS</AvatarFallback>\n</Avatar>"
        },
        {
          "title": "Different Sizes",
          "code": "<Avatar size=\"sm\">\n  <AvatarImage src=\"/small.jpg\" alt=\"Small avatar\" />\n</Avatar>\n\n<Avatar size=\"lg\">\n  <AvatarImage src=\"/large.jpg\" alt=\"Large avatar\" />\n</Avatar>"
        },
        {
          "title": "External Image",
          "code": "<Avatar>\n  <AvatarImage src=\"https://avatars.githubusercontent.com/u/123\" alt=\"GitHub user\" />\n  <AvatarFallback>GH</AvatarFallback>\n</Avatar>"
        }
      ],
      "relatedComponents": [
        "Avatar",
        "AvatarFallback"
      ]
    },
    {
      "name": "Badge",
      "description": "Small label component for status indicators and categorization",
      "category": "core",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "variant",
          "type": "'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info'",
          "default": "'default'",
          "required": false,
          "description": "Visual style variant"
        },
        {
          "name": "size",
          "type": "'default' | 'sm' | 'dot'",
          "default": "'default'",
          "required": false,
          "description": "Size of the badge"
        },
        {
          "name": "clickable",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Enables hover effects for clickable badges"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "variant",
          "values": [
            "default",
            "secondary",
            "destructive",
            "outline",
            "success",
            "warning",
            "info"
          ],
          "default": "default"
        },
        {
          "name": "size",
          "values": [
            "default",
            "sm",
            "dot"
          ],
          "default": "default"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Badge>Default</Badge>"
        },
        {
          "title": "Default",
          "code": "<Badge variant=\"default\">Default</Badge>"
        },
        {
          "title": "Secondary",
          "code": "<Badge variant=\"secondary\">Secondary</Badge>"
        },
        {
          "title": "Destructive",
          "code": "<Badge variant=\"destructive\">Destructive</Badge>"
        },
        {
          "title": "Outline",
          "code": "<Badge variant=\"outline\">Outline</Badge>"
        },
        {
          "title": "Success",
          "code": "<Badge variant=\"success\">Success</Badge>"
        },
        {
          "title": "Warning",
          "code": "<Badge variant=\"warning\">Warning</Badge>"
        },
        {
          "title": "Info",
          "code": "<Badge variant=\"info\">Info</Badge>"
        },
        {
          "title": "Status Indicators",
          "code": "<Badge variant=\"success\">Active</Badge>\n<Badge variant=\"warning\">Pending</Badge>\n<Badge variant=\"destructive\">Expired</Badge>"
        },
        {
          "title": "Notification Counts",
          "code": "<Badge size=\"dot\" variant=\"destructive\">3</Badge>"
        },
        {
          "title": "Category Tags",
          "code": "<div class=\"flex gap-2\">\n  <Badge variant=\"secondary\">React</Badge>\n  <Badge variant=\"secondary\">Svelte</Badge>\n  <Badge variant=\"secondary\">TypeScript</Badge>\n</div>"
        },
        {
          "title": "With Icons",
          "code": "<Badge>\n  <svg class=\"h-3 w-3\" fill=\"currentColor\" viewBox=\"0 0 20 20\">\n    <circle cx=\"10\" cy=\"10\" r=\"5\" />\n  </svg>\n  Online\n</Badge>"
        },
        {
          "title": "Table Status Column",
          "code": "<td>\n  {#if user.status === 'active'}\n    <Badge variant=\"success\">Active</Badge>\n  {:else if user.status === 'pending'}\n    <Badge variant=\"warning\">Pending</Badge>\n  {:else}\n    <Badge variant=\"destructive\">Inactive</Badge>\n  {/if}\n</td>"
        },
        {
          "title": "Article Tags",
          "code": "<article>\n  <h2>Article Title</h2>\n  <div class=\"flex gap-1 mt-2\">\n    {#each article.tags as tag}\n      <Badge variant=\"outline\" size=\"sm\">{tag}</Badge>\n    {/each}\n  </div>\n</article>"
        },
        {
          "title": "Version Badge",
          "code": "<Badge variant=\"info\">v2.0.0</Badge>"
        },
        {
          "title": "New Feature Badge",
          "code": "<h3>\n  Feature Name\n  <Badge variant=\"default\" size=\"sm\" class=\"ml-2\">New</Badge>\n</h3>"
        }
      ],
      "relatedComponents": [
        "Alert",
        "Button"
      ]
    },
    {
      "name": "BarChart",
      "description": "Compare categories with vertical or horizontal bars",
      "category": "chart",
      "importPath": "@classic-homes/charts-svelte",
      "props": [
        {
          "name": "data",
          "type": "BarChartData",
          "required": true,
          "description": "Chart data with categories and series"
        },
        {
          "name": "orientation",
          "type": "'vertical' | 'horizontal'",
          "default": "'vertical'",
          "required": false,
          "description": "Bar orientation"
        },
        {
          "name": "stacked",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Stack bars to show composition"
        },
        {
          "name": "showValues",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Show value labels on bars"
        },
        {
          "name": "onClick",
          "type": "(params: DataPointEventParams) => void",
          "required": false,
          "description": "Callback when a bar is clicked"
        },
        {
          "name": "title",
          "type": "string",
          "required": true,
          "description": "Chart title for accessibility"
        },
        {
          "name": "description",
          "type": "string",
          "required": false,
          "description": "Description for screen readers"
        },
        {
          "name": "height",
          "type": "number | string",
          "default": "400",
          "required": false,
          "description": "Chart height"
        },
        {
          "name": "theme",
          "type": "'light' | 'dark' | 'auto'",
          "default": "'auto'",
          "required": false,
          "description": "Theme preference"
        },
        {
          "name": "showLegend",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show chart legend"
        },
        {
          "name": "showTooltip",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show tooltip on hover"
        },
        {
          "name": "showGrid",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show grid lines"
        }
      ],
      "variants": [
        {
          "name": "orientation",
          "values": [
            "vertical",
            "horizontal"
          ],
          "default": "vertical"
        },
        {
          "name": "theme",
          "values": [
            "light",
            "dark",
            "auto"
          ],
          "default": "auto"
        }
      ],
      "slots": [],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { BarChart } from '@classic-homes/charts-svelte';\n\n  const data = {\n    categories: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],\n    series: [\n      { name: 'Sales', data: [320, 332, 301, 334, 390] },\n      { name: 'Returns', data: [120, 132, 101, 134, 90] },\n    ],\n  };\n</script>\n\n<BarChart title=\"Weekly Sales\" {data} height={400} />"
        },
        {
          "title": "Horizontal Bar Chart",
          "code": "<BarChart title=\"Sales by Region\" {data} orientation=\"horizontal\" height={400} />"
        },
        {
          "title": "Stacked Bar Chart",
          "code": "<BarChart title=\"Revenue Breakdown\" {data} stacked height={400} />"
        },
        {
          "title": "With Value Labels",
          "code": "<BarChart title=\"Monthly Performance\" {data} showValues height={400} />"
        }
      ],
      "relatedComponents": [
        "LineChart",
        "FunnelChart"
      ]
    },
    {
      "name": "Button",
      "description": "Clickable button component with multiple variants, sizes, and loading states",
      "category": "form",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "variant",
          "type": "'default' | 'secondary' | 'destructive' | 'outline' | 'ghost' | 'link'",
          "default": "'default'",
          "required": false,
          "description": "Visual style variant"
        },
        {
          "name": "size",
          "type": "'default' | 'sm' | 'md' | 'lg' | 'icon'",
          "default": "'default'",
          "required": false,
          "description": "Size of the button"
        },
        {
          "name": "type",
          "type": "'button' | 'submit' | 'reset'",
          "default": "'button'",
          "required": false,
          "description": "HTML button type"
        },
        {
          "name": "href",
          "type": "string",
          "required": false,
          "description": "If provided, renders as an anchor link"
        },
        {
          "name": "disabled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disables the button"
        },
        {
          "name": "loading",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Shows loading spinner"
        },
        {
          "name": "loadingText",
          "type": "string",
          "required": false,
          "description": "Text to show during loading (replaces children)"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        },
        {
          "name": "onclick",
          "type": "(e: MouseEvent) => void",
          "required": false,
          "description": "Click handler"
        }
      ],
      "variants": [
        {
          "name": "variant",
          "values": [
            "default",
            "secondary",
            "destructive",
            "outline",
            "ghost",
            "link"
          ],
          "default": "default"
        },
        {
          "name": "size",
          "values": [
            "default",
            "sm",
            "md",
            "lg",
            "icon"
          ],
          "default": "default"
        },
        {
          "name": "type",
          "values": [
            "button",
            "submit",
            "reset"
          ],
          "default": "button"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Button>Click Me</Button>"
        },
        {
          "title": "Default",
          "description": "The primary action button with solid background.",
          "code": "<Button variant=\"default\">Default Button</Button>"
        },
        {
          "title": "Secondary",
          "description": "For secondary actions that need less visual emphasis.",
          "code": "<Button variant=\"secondary\">Secondary Button</Button>"
        },
        {
          "title": "Destructive",
          "description": "For dangerous or irreversible actions like delete.",
          "code": "<Button variant=\"destructive\">Delete</Button>"
        },
        {
          "title": "Outline",
          "description": "Bordered button with transparent background.",
          "code": "<Button variant=\"outline\">Outline Button</Button>"
        },
        {
          "title": "Ghost",
          "description": "Minimal button with no background until hovered.",
          "code": "<Button variant=\"ghost\">Ghost Button</Button>"
        },
        {
          "title": "Link",
          "description": "Styled as an inline text link.",
          "code": "<Button variant=\"link\">Link Button</Button>"
        },
        {
          "title": "Disabled",
          "code": "<Button disabled>Disabled</Button>"
        },
        {
          "title": "Loading",
          "description": "Shows a spinner and optionally replaces the text:",
          "code": "<Button loading>Submit</Button>\n<Button loading loadingText=\"Saving...\">Save</Button>"
        },
        {
          "title": "Button Group",
          "code": "<div class=\"flex gap-2\">\n  <Button variant=\"outline\">Cancel</Button>\n  <Button>Save Changes</Button>\n</div>"
        },
        {
          "title": "Full Width Button",
          "code": "<Button class=\"w-full\">Full Width Button</Button>"
        },
        {
          "title": "Async Action",
          "code": "<script>\n  let loading = $state(false);\n\n  async function handleSave() {\n    loading = true;\n    try {\n      await saveData();\n    } finally {\n      loading = false;\n    }\n  }\n</script>\n\n<Button {loading} loadingText=\"Saving...\" onclick={handleSave}>Save</Button>"
        }
      ],
      "relatedComponents": [
        "Input",
        "FormField",
        "Dialog"
      ]
    },
    {
      "name": "CandlestickChart",
      "description": "Financial OHLC data with volume bars",
      "category": "chart",
      "importPath": "@classic-homes/charts-svelte",
      "props": [
        {
          "name": "data",
          "type": "CandlestickData",
          "required": true,
          "description": "OHLC data with optional volume"
        },
        {
          "name": "showVolume",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Display volume bars below chart"
        },
        {
          "name": "onClick",
          "type": "(params: CandlestickEventParams) => void",
          "required": false,
          "description": "Callback when clicked"
        },
        {
          "name": "title",
          "type": "string",
          "required": true,
          "description": "Chart title for accessibility"
        },
        {
          "name": "height",
          "type": "number | string",
          "default": "400",
          "required": false,
          "description": "Chart height"
        },
        {
          "name": "theme",
          "type": "'light' | 'dark' | 'auto'",
          "default": "'auto'",
          "required": false,
          "description": "Theme preference"
        },
        {
          "name": "showTooltip",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show tooltip on hover"
        },
        {
          "name": "showGrid",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show grid lines"
        }
      ],
      "variants": [
        {
          "name": "theme",
          "values": [
            "light",
            "dark",
            "auto"
          ],
          "default": "auto"
        }
      ],
      "slots": [],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { CandlestickChart } from '@classic-homes/charts-svelte';\n\n  const data = {\n    dates: ['2024-01', '2024-02', '2024-03', '2024-04'],\n    data: [\n      { open: 20, close: 34, low: 10, high: 38 },\n      { open: 40, close: 35, low: 30, high: 50 },\n      { open: 31, close: 38, low: 28, high: 48 },\n      { open: 38, close: 30, low: 25, high: 44 },\n    ],\n    volume: [100, 120, 90, 140],\n  };\n</script>\n\n<CandlestickChart title=\"Stock Price\" {data} showVolume height={400} />"
        },
        {
          "title": "With Volume",
          "code": "<CandlestickChart title=\"AAPL Stock\" {data} showVolume height={500} />"
        },
        {
          "title": "Without Volume",
          "code": "<CandlestickChart\n  title=\"Price History\"\n  data={{\n    dates: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],\n    data: [\n      { open: 100, close: 110, low: 95, high: 115 },\n      { open: 110, close: 105, low: 100, high: 120 },\n      { open: 105, close: 115, low: 102, high: 118 },\n      { open: 115, close: 108, low: 105, high: 120 },\n      { open: 108, close: 125, low: 105, high: 128 },\n    ],\n  }}\n/>"
        }
      ],
      "relatedComponents": [
        "LineChart",
        "BarChart"
      ]
    },
    {
      "name": "Card",
      "description": "Container component for grouping related content",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Card>\n  <CardHeader>\n    <CardTitle>Card Title</CardTitle>\n    <CardDescription>Card description text</CardDescription>\n  </CardHeader>\n  <CardContent>\n    <p>Card content goes here.</p>\n  </CardContent>\n  <CardFooter>\n    <Button>Action</Button>\n  </CardFooter>\n</Card>"
        },
        {
          "title": "Default",
          "description": "Standard card with 1px border:",
          "code": "<Card variant=\"default\">\n  <CardContent>Default card</CardContent>\n</Card>"
        },
        {
          "title": "Featured",
          "description": "Emphasized card with 2px border:",
          "code": "<Card variant=\"featured\">\n  <CardContent>Featured card with thicker border</CardContent>\n</Card>"
        },
        {
          "title": "Highlight",
          "description": "Positive/info callout with teal border:",
          "code": "<Card variant=\"highlight\">\n  <CardContent>Highlight card for important info</CardContent>\n</Card>"
        },
        {
          "title": "Warning",
          "description": "Alert card with red border:",
          "code": "<Card variant=\"warning\">\n  <CardContent>Warning card for alerts</CardContent>\n</Card>"
        },
        {
          "title": "Profile Card",
          "code": "<Card class=\"max-w-sm\">\n  <CardHeader>\n    <div class=\"flex items-center gap-4\">\n      <Avatar>\n        <AvatarImage src=\"/avatar.jpg\" alt=\"User\" />\n        <AvatarFallback>JD</AvatarFallback>\n      </Avatar>\n      <div>\n        <CardTitle>Jane Doe</CardTitle>\n        <CardDescription>Product Designer</CardDescription>\n      </div>\n    </div>\n  </CardHeader>\n  <CardContent>\n    <p class=\"text-sm\">Building beautiful interfaces at Acme Corp.</p>\n  </CardContent>\n  <CardFooter class=\"gap-2\">\n    <Button variant=\"outline\" size=\"sm\">Message</Button>\n    <Button size=\"sm\">Follow</Button>\n  </CardFooter>\n</Card>"
        },
        {
          "title": "Pricing Card",
          "code": "<Card variant=\"featured\" class=\"max-w-sm text-center\">\n  <CardHeader>\n    <CardTitle>Pro Plan</CardTitle>\n    <CardDescription>For growing teams</CardDescription>\n  </CardHeader>\n  <CardContent>\n    <div class=\"text-4xl font-bold\">$29</div>\n    <p class=\"text-muted-foreground\">/month</p>\n    <ul class=\"mt-4 space-y-2 text-left\">\n      <li>Unlimited projects</li>\n      <li>Priority support</li>\n      <li>Advanced analytics</li>\n    </ul>\n  </CardContent>\n  <CardFooter>\n    <Button class=\"w-full\">Get Started</Button>\n  </CardFooter>\n</Card>"
        },
        {
          "title": "Stats Card",
          "code": "<Card>\n  <CardHeader class=\"flex-row items-center justify-between pb-2\">\n    <CardTitle class=\"text-sm font-medium\">Total Revenue</CardTitle>\n    <svg class=\"h-4 w-4 text-muted-foreground\"><!-- icon --></svg>\n  </CardHeader>\n  <CardContent>\n    <div class=\"text-2xl font-bold\">$45,231.89</div>\n    <p class=\"text-xs text-muted-foreground\">+20.1% from last month</p>\n  </CardContent>\n</Card>"
        },
        {
          "title": "Card Grid",
          "code": "<div class=\"grid gap-4 md:grid-cols-2 lg:grid-cols-3\">\n  {#each items as item}\n    <Card interactive>\n      <CardHeader>\n        <CardTitle>{item.title}</CardTitle>\n      </CardHeader>\n      <CardContent>\n        <p>{item.description}</p>\n      </CardContent>\n    </Card>\n  {/each}\n</div>"
        },
        {
          "title": "Simple Content Card",
          "code": "<Card>\n  <CardContent class=\"pt-6\">\n    <p>Simple card with just content, no header or footer.</p>\n  </CardContent>\n</Card>"
        }
      ],
      "relatedComponents": [
        "Alert",
        "Dialog"
      ]
    },
    {
      "name": "CardContent",
      "description": "Main content area container for Card components",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Card>\n  <CardContent>\n    <p>This is the main content of the card.</p>\n  </CardContent>\n</Card>"
        },
        {
          "title": "With Header",
          "code": "<Card>\n  <CardHeader>\n    <CardTitle>Settings</CardTitle>\n  </CardHeader>\n  <CardContent>\n    <p>Adjust your preferences below.</p>\n  </CardContent>\n</Card>"
        },
        {
          "title": "Standalone Content",
          "description": "When using without a header, add top padding:",
          "code": "<Card>\n  <CardContent class=\"pt-6\">\n    <p>Simple card with just content.</p>\n  </CardContent>\n</Card>"
        },
        {
          "title": "With Form Elements",
          "code": "<CardContent>\n  <div class=\"space-y-4\">\n    <FormField label=\"Email\" name=\"email\" />\n    <FormField label=\"Password\" name=\"password\" type=\"password\" />\n  </div>\n</CardContent>"
        },
        {
          "title": "Rich Content",
          "code": "<CardContent>\n  <div class=\"flex items-center gap-4\">\n    <Avatar>\n      <AvatarImage src=\"/user.jpg\" alt=\"User\" />\n      <AvatarFallback>JD</AvatarFallback>\n    </Avatar>\n    <div>\n      <p class=\"font-medium\">John Doe</p>\n      <p class=\"text-sm text-muted-foreground\">Developer</p>\n    </div>\n  </div>\n</CardContent>"
        }
      ],
      "relatedComponents": [
        "Card",
        "CardHeader",
        "CardFooter"
      ]
    },
    {
      "name": "CardDescription",
      "description": "Secondary text component for Card headers",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Card>\n  <CardHeader>\n    <CardTitle>Card Title</CardTitle>\n    <CardDescription>This is a description of the card content.</CardDescription>\n  </CardHeader>\n</Card>"
        },
        {
          "title": "Descriptive Text",
          "code": "<CardDescription>\n  Configure your notification preferences and choose how you want to be contacted.\n</CardDescription>"
        },
        {
          "title": "With Custom Styling",
          "code": "<CardDescription class=\"text-xs\">Last updated 2 hours ago</CardDescription>"
        },
        {
          "title": "Multiple Lines",
          "code": "<CardDescription>\n  Your subscription includes unlimited access to all premium features. Upgrade anytime to unlock\n  more.\n</CardDescription>"
        }
      ],
      "relatedComponents": [
        "Card",
        "CardHeader",
        "CardTitle"
      ]
    },
    {
      "name": "CardFooter",
      "description": "Footer section container for Card action buttons",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Card>\n  <CardContent>\n    <p>Card content here.</p>\n  </CardContent>\n  <CardFooter>\n    <Button>Save Changes</Button>\n  </CardFooter>\n</Card>"
        },
        {
          "title": "Multiple Actions",
          "code": "<CardFooter class=\"gap-2\">\n  <Button variant=\"outline\">Cancel</Button>\n  <Button>Save</Button>\n</CardFooter>"
        },
        {
          "title": "Right-Aligned Actions",
          "code": "<CardFooter class=\"justify-end gap-2\">\n  <Button variant=\"ghost\">Skip</Button>\n  <Button>Continue</Button>\n</CardFooter>"
        },
        {
          "title": "Space Between",
          "code": "<CardFooter class=\"justify-between\">\n  <Button variant=\"ghost\">Back</Button>\n  <Button>Next</Button>\n</CardFooter>"
        },
        {
          "title": "Full-Width Button",
          "code": "<CardFooter>\n  <Button class=\"w-full\">Get Started</Button>\n</CardFooter>"
        },
        {
          "title": "With Summary Text",
          "code": "<CardFooter class=\"justify-between\">\n  <span class=\"text-sm text-muted-foreground\">2 items selected</span>\n  <Button size=\"sm\">Delete Selected</Button>\n</CardFooter>"
        }
      ],
      "relatedComponents": [
        "Card",
        "CardContent",
        "Button"
      ]
    },
    {
      "name": "CardHeader",
      "description": "Header section container for Card components with title and description",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "variant",
          "type": "'default' | 'compact' | 'bordered' | 'shaded'",
          "default": "'default'",
          "required": false,
          "description": "Header style variant"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "variant",
          "values": [
            "default",
            "compact",
            "bordered",
            "shaded"
          ],
          "default": "default"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Card>\n  <CardHeader>\n    <CardTitle>Card Title</CardTitle>\n    <CardDescription>Card description text</CardDescription>\n  </CardHeader>\n</Card>"
        },
        {
          "title": "Default",
          "description": "Standard padding (p-6):",
          "code": "<CardHeader variant=\"default\">\n  <CardTitle>Default Header</CardTitle>\n</CardHeader>"
        },
        {
          "title": "Compact",
          "description": "Reduced padding (p-4):",
          "code": "<CardHeader variant=\"compact\">\n  <CardTitle>Compact Header</CardTitle>\n</CardHeader>"
        },
        {
          "title": "Bordered",
          "description": "Header with bottom border:",
          "code": "<CardHeader variant=\"bordered\">\n  <CardTitle>Bordered Header</CardTitle>\n</CardHeader>"
        },
        {
          "title": "Shaded",
          "description": "Header with muted background:",
          "code": "<CardHeader variant=\"shaded\">\n  <CardTitle>Shaded Header</CardTitle>\n</CardHeader>"
        }
      ],
      "relatedComponents": [
        "Card",
        "CardTitle",
        "CardDescription"
      ]
    },
    {
      "name": "CardTitle",
      "description": "Title heading component for Card headers",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Card>\n  <CardHeader>\n    <CardTitle>Card Title</CardTitle>\n  </CardHeader>\n</Card>"
        },
        {
          "title": "With Description",
          "code": "<CardHeader>\n  <CardTitle>Account Settings</CardTitle>\n  <CardDescription>Manage your account preferences</CardDescription>\n</CardHeader>"
        },
        {
          "title": "Custom Styling",
          "code": "<CardTitle class=\"text-primary\">Featured Title</CardTitle>"
        },
        {
          "title": "In Stats Card",
          "code": "<CardHeader class=\"flex-row items-center justify-between pb-2\">\n  <CardTitle class=\"text-sm font-medium\">Total Revenue</CardTitle>\n  <Icon name=\"dollar\" class=\"h-4 w-4 text-muted-foreground\" />\n</CardHeader>"
        }
      ],
      "relatedComponents": [
        "Card",
        "CardHeader",
        "CardDescription"
      ]
    },
    {
      "name": "ChartContainer",
      "description": "Base wrapper component for charts with loading, error, and empty states",
      "category": "chart",
      "importPath": "@classic-homes/charts-svelte",
      "props": [
        {
          "name": "title",
          "type": "string",
          "required": true,
          "description": "Chart title for accessibility (aria-label)"
        },
        {
          "name": "description",
          "type": "string",
          "required": false,
          "description": "Chart description for screen readers"
        },
        {
          "name": "height",
          "type": "number | string",
          "default": "400",
          "required": false,
          "description": "Chart container height"
        },
        {
          "name": "loading",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Show loading skeleton"
        },
        {
          "name": "error",
          "type": "string | null",
          "required": false,
          "description": "Error message to display"
        },
        {
          "name": "empty",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Show empty state"
        },
        {
          "name": "emptyMessage",
          "type": "string",
          "default": "'No data available'",
          "required": false,
          "description": "Custom empty state message"
        },
        {
          "name": "onRetry",
          "type": "() => void",
          "required": false,
          "description": "Callback for retry button in error state"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes for chart area"
        },
        {
          "name": "wrapperClass",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes for outer wrapper"
        }
      ],
      "variants": [],
      "slots": [],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { ChartContainer } from '@classic-homes/charts-svelte';\n</script>\n\n<ChartContainer title=\"Sales Overview\" height={400}>\n  <!-- Your chart content here -->\n</ChartContainer>"
        },
        {
          "title": "With Loading State",
          "code": "<script>\n  import { ChartContainer, LineChart } from '@classic-homes/charts-svelte';\n\n  let loading = $state(true);\n  let data = $state(null);\n</script>\n\n<ChartContainer title=\"Revenue\" {loading}>\n  {#if data}\n    <LineChart {data} title=\"Revenue\" />\n  {/if}\n</ChartContainer>"
        },
        {
          "title": "With Error State",
          "code": "<script>\n  import { ChartContainer, BarChart } from '@classic-homes/charts-svelte';\n\n  let error = $state(null);\n\n  async function fetchData() {\n    try {\n      // fetch data\n    } catch (e) {\n      error = e.message;\n    }\n  }\n</script>\n\n<ChartContainer title=\"Analytics\" {error} onRetry={fetchData}>\n  <BarChart {data} title=\"Analytics\" />\n</ChartContainer>"
        },
        {
          "title": "With Empty State",
          "code": "<script>\n  import { ChartContainer, AreaChart } from '@classic-homes/charts-svelte';\n\n  let data = $state([]);\n</script>\n\n<ChartContainer\n  title=\"User Activity\"\n  empty={data.length === 0}\n  emptyMessage=\"No activity recorded yet\"\n>\n  <AreaChart {data} title=\"User Activity\" />\n</ChartContainer>"
        },
        {
          "title": "Custom Styling",
          "code": "<ChartContainer title=\"Dashboard Chart\" wrapperClass=\"shadow-lg\" class=\"p-4\">\n  <PieChart {data} title=\"Distribution\" />\n</ChartContainer>"
        }
      ],
      "relatedComponents": [
        "ChartSkeleton",
        "ChartError",
        "ChartEmpty"
      ]
    },
    {
      "name": "ChartEmpty",
      "description": "Empty state component displayed when chart has no data",
      "category": "chart",
      "importPath": "@classic-homes/charts-svelte",
      "props": [
        {
          "name": "message",
          "type": "string",
          "default": "'No data available'",
          "required": false,
          "description": "Empty state message"
        },
        {
          "name": "height",
          "type": "number | string",
          "default": "400",
          "required": false,
          "description": "Component height"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { ChartEmpty } from '@classic-homes/charts-svelte';\n</script>\n\n<ChartEmpty />"
        },
        {
          "title": "Custom Message",
          "code": "<ChartEmpty message=\"No sales data for this period\" />"
        },
        {
          "title": "Custom Height",
          "code": "<ChartEmpty height={300} message=\"Start adding data to see your chart\" />"
        },
        {
          "title": "Conditional Rendering",
          "code": "<script>\n  import { ChartEmpty, LineChart } from '@classic-homes/charts-svelte';\n\n  let data = $state([]);\n</script>\n\n{#if data.length === 0}\n  <ChartEmpty message=\"No activity recorded\" />\n{:else}\n  <LineChart {data} title=\"Activity\" />\n{/if}"
        },
        {
          "title": "With Custom Styling",
          "code": "<ChartEmpty message=\"No results found\" class=\"rounded-xl bg-muted/30\" height={250} />"
        }
      ],
      "relatedComponents": [
        "ChartContainer",
        "ChartError",
        "ChartSkeleton"
      ]
    },
    {
      "name": "ChartError",
      "description": "Error state component for charts with optional retry functionality",
      "category": "chart",
      "importPath": "@classic-homes/charts-svelte",
      "props": [
        {
          "name": "message",
          "type": "string",
          "required": true,
          "description": "Error message to display"
        },
        {
          "name": "height",
          "type": "number | string",
          "default": "400",
          "required": false,
          "description": "Component height"
        },
        {
          "name": "onRetry",
          "type": "() => void",
          "required": false,
          "description": "Callback when retry button is clicked"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { ChartError } from '@classic-homes/charts-svelte';\n</script>\n\n<ChartError message=\"Failed to load chart data\" />"
        },
        {
          "title": "With Retry Button",
          "code": "<script>\n  import { ChartError } from '@classic-homes/charts-svelte';\n\n  async function fetchData() {\n    // Retry logic\n  }\n</script>\n\n<ChartError message=\"Network error occurred\" onRetry={fetchData} />"
        },
        {
          "title": "Custom Height",
          "code": "<ChartError message=\"Unable to process data\" height={300} onRetry={handleRetry} />"
        },
        {
          "title": "Conditional Error Handling",
          "code": "<script>\n  import { ChartError, BarChart } from '@classic-homes/charts-svelte';\n\n  let error = $state(null);\n  let data = $state(null);\n\n  async function loadData() {\n    try {\n      error = null;\n      data = await fetchChartData();\n    } catch (e) {\n      error = e.message;\n    }\n  }\n</script>\n\n{#if error}\n  <ChartError message={error} onRetry={loadData} />\n{:else if data}\n  <BarChart {data} title=\"Sales\" />\n{/if}"
        },
        {
          "title": "With Custom Styling",
          "code": "<ChartError\n  message=\"Service temporarily unavailable\"\n  class=\"rounded-xl\"\n  onRetry={() => location.reload()}\n/>"
        }
      ],
      "relatedComponents": [
        "ChartContainer",
        "ChartEmpty",
        "ChartSkeleton"
      ]
    },
    {
      "name": "ChartSkeleton",
      "description": "Loading skeleton placeholder for charts with animated bars",
      "category": "chart",
      "importPath": "@classic-homes/charts-svelte",
      "props": [
        {
          "name": "height",
          "type": "number | string",
          "default": "400",
          "required": false,
          "description": "Skeleton height"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { ChartSkeleton } from '@classic-homes/charts-svelte';\n</script>\n\n<ChartSkeleton />"
        },
        {
          "title": "Custom Height",
          "code": "<ChartSkeleton height={300} />"
        },
        {
          "title": "With Custom Class",
          "code": "<ChartSkeleton class=\"rounded-xl shadow-md\" />"
        },
        {
          "title": "Conditional Loading",
          "code": "<script>\n  import { ChartSkeleton, LineChart } from '@classic-homes/charts-svelte';\n\n  let loading = $state(true);\n  let data = $state(null);\n\n  async function loadData() {\n    loading = true;\n    data = await fetchChartData();\n    loading = false;\n  }\n</script>\n\n{#if loading}\n  <ChartSkeleton height={400} />\n{:else}\n  <LineChart {data} title=\"Revenue\" />\n{/if}"
        },
        {
          "title": "In Card",
          "code": "<script>\n  import { ChartSkeleton, AreaChart } from '@classic-homes/charts-svelte';\n  import { Card, CardHeader, CardTitle, CardContent } from '@classic-homes/theme-svelte';\n</script>\n\n<Card>\n  <CardHeader>\n    <CardTitle>Analytics</CardTitle>\n  </CardHeader>\n  <CardContent>\n    {#if loading}\n      <ChartSkeleton height={300} />\n    {:else}\n      <AreaChart {data} title=\"Monthly Users\" />\n    {/if}\n  </CardContent>\n</Card>"
        },
        {
          "title": "Multiple Skeletons",
          "code": "<div class=\"grid grid-cols-2 gap-4\">\n  <ChartSkeleton height={250} />\n  <ChartSkeleton height={250} />\n</div>"
        }
      ],
      "relatedComponents": [
        "ChartContainer",
        "ChartError",
        "ChartEmpty",
        "Skeleton"
      ]
    },
    {
      "name": "Checkbox",
      "description": "Checkbox input for boolean selections and multiple choice options",
      "category": "form",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "checked",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Whether the checkbox is checked (bindable)"
        },
        {
          "name": "disabled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disables the checkbox"
        },
        {
          "name": "required",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Marks as required"
        },
        {
          "name": "name",
          "type": "string",
          "required": false,
          "description": "Name attribute for forms"
        },
        {
          "name": "value",
          "type": "string",
          "required": false,
          "description": "Value attribute for forms"
        },
        {
          "name": "id",
          "type": "string",
          "required": false,
          "description": "Element ID for label association"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        },
        {
          "name": "onCheckedChange",
          "type": "(checked: boolean) => void",
          "required": false,
          "description": "Callback when checked state changes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let checked = $state(false);\n</script>\n\n<Checkbox bind:checked />"
        },
        {
          "title": "Checked",
          "code": "<Checkbox checked />"
        },
        {
          "title": "Disabled",
          "code": "<Checkbox disabled />\n<Checkbox disabled checked />"
        },
        {
          "title": "Required",
          "code": "<Checkbox required id=\"required-checkbox\" />"
        },
        {
          "title": "Terms Agreement",
          "code": "<script>\n  let agreed = $state(false);\n</script>\n\n<div class=\"flex items-start gap-2\">\n  <Checkbox id=\"agree\" bind:checked={agreed} class=\"mt-1\" />\n  <Label for=\"agree\" class=\"leading-normal\">\n    I agree to the <a href=\"/terms\" class=\"text-primary underline\">Terms of Service</a>\n    and <a href=\"/privacy\" class=\"text-primary underline\">Privacy Policy</a>\n  </Label>\n</div>\n\n<Button disabled={!agreed}>Continue</Button>"
        },
        {
          "title": "Feature Selection",
          "code": "<script>\n  let features = $state(['basic']);\n\n  function toggleFeature(feature: string) {\n    if (features.includes(feature)) {\n      features = features.filter((f) => f !== feature);\n    } else {\n      features = [...features, feature];\n    }\n  }\n</script>\n\n<div class=\"space-y-2\">\n  <div class=\"flex items-center gap-2\">\n    <Checkbox\n      id=\"basic\"\n      checked={features.includes('basic')}\n      onCheckedChange={() => toggleFeature('basic')}\n    />\n    <Label for=\"basic\">Basic features</Label>\n  </div>\n  <div class=\"flex items-center gap-2\">\n    <Checkbox\n      id=\"advanced\"\n      checked={features.includes('advanced')}\n      onCheckedChange={() => toggleFeature('advanced')}\n    />\n    <Label for=\"advanced\">Advanced features</Label>\n  </div>\n  <div class=\"flex items-center gap-2\">\n    <Checkbox\n      id=\"premium\"\n      checked={features.includes('premium')}\n      onCheckedChange={() => toggleFeature('premium')}\n    />\n    <Label for=\"premium\">Premium features</Label>\n  </div>\n</div>"
        },
        {
          "title": "Form Submission",
          "code": "<form onsubmit={handleSubmit}>\n  <Checkbox name=\"newsletter\" value=\"subscribed\" />\n  <button type=\"submit\">Submit</button>\n</form>"
        }
      ],
      "relatedComponents": [
        "Switch",
        "RadioGroup",
        "Label"
      ]
    },
    {
      "name": "Combobox",
      "description": "Searchable select with async loading support",
      "category": "form",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "value",
          "type": "string",
          "default": "''",
          "required": false,
          "description": "Current selected value (bindable)"
        },
        {
          "name": "options",
          "type": "ComboboxOption[]",
          "default": "Required",
          "required": false,
          "description": "Array of options"
        },
        {
          "name": "placeholder",
          "type": "string",
          "default": "'Select an option...'",
          "required": false,
          "description": "Placeholder text"
        },
        {
          "name": "emptyMessage",
          "type": "string",
          "default": "'No results found.'",
          "required": false,
          "description": "Message when no options match"
        },
        {
          "name": "disabled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disables the combobox"
        },
        {
          "name": "required",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Marks as required"
        },
        {
          "name": "name",
          "type": "string",
          "required": false,
          "description": "Name attribute for forms"
        },
        {
          "name": "id",
          "type": "string",
          "required": false,
          "description": "Element ID (auto-generated if empty)"
        },
        {
          "name": "label",
          "type": "string",
          "required": false,
          "description": "Label text"
        },
        {
          "name": "hint",
          "type": "string",
          "required": false,
          "description": "Hint text below the input"
        },
        {
          "name": "error",
          "type": "string",
          "required": false,
          "description": "Error message"
        },
        {
          "name": "loading",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Shows loading state"
        },
        {
          "name": "debounceMs",
          "type": "number",
          "default": "300",
          "required": false,
          "description": "Debounce time for search"
        },
        {
          "name": "onValueChange",
          "type": "(value: string) => void",
          "required": false,
          "description": "Callback when value changes"
        },
        {
          "name": "onSearch",
          "type": "(query: string) => void",
          "required": false,
          "description": "Callback for search queries"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let value = $state('');\n\n  const options = [\n    { value: 'apple', label: 'Apple' },\n    { value: 'banana', label: 'Banana' },\n    { value: 'orange', label: 'Orange' },\n  ];\n</script>\n\n<Combobox {options} bind:value placeholder=\"Select a fruit...\" />"
        },
        {
          "title": "User Search",
          "code": "<script>\n  let selectedUser = $state('');\n  let users = $state([]);\n  let loading = $state(false);\n\n  async function searchUsers(query: string) {\n    if (!query) {\n      users = [];\n      return;\n    }\n    loading = true;\n    const response = await fetch(`/api/users?search=${query}`);\n    users = await response.json();\n    loading = false;\n  }\n</script>\n\n<Combobox\n  label=\"User\"\n  options={users}\n  bind:value={selectedUser}\n  onSearch={searchUsers}\n  {loading}\n  placeholder=\"Search users...\"\n  emptyMessage=\"No users found\"\n/>"
        },
        {
          "title": "Location Autocomplete",
          "code": "<Combobox\n  label=\"Location\"\n  hint=\"Enter a city name or zip code\"\n  options={locations}\n  bind:value\n  onSearch={searchLocations}\n  placeholder=\"Enter city or zip code...\"\n  emptyMessage=\"No locations found\"\n/>"
        }
      ],
      "relatedComponents": [
        "Select",
        "MultiSelect"
      ]
    },
    {
      "name": "DashboardLayout",
      "description": "Complete dashboard layout with responsive sidebar and header",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "navigation",
          "type": "NavSection[]",
          "required": false,
          "description": "Navigation sections for sidebar"
        },
        {
          "name": "user",
          "type": "User",
          "required": false,
          "description": "Current user data"
        },
        {
          "name": "appName",
          "type": "string",
          "default": "'Dashboard'",
          "required": false,
          "description": "Application name"
        },
        {
          "name": "pageTitle",
          "type": "string",
          "required": false,
          "description": "Page title in header"
        },
        {
          "name": "backLink",
          "type": "BackLink",
          "required": false,
          "description": "Back link at top of navigation"
        },
        {
          "name": "sidebarVariant",
          "type": "'light' | 'dark'",
          "default": "'light'",
          "required": false,
          "description": "Sidebar visual variant"
        },
        {
          "name": "sidebarCollapsed",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Initial collapsed state"
        },
        {
          "name": "showHeader",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Whether to show header"
        },
        {
          "name": "logo",
          "type": "Snippet",
          "required": false,
          "description": "Custom logo"
        },
        {
          "name": "icon",
          "type": "Snippet<[NavItem]>",
          "required": false,
          "description": "Custom icon renderer"
        },
        {
          "name": "quickLinks",
          "type": "QuickLink[]",
          "required": false,
          "description": "Quick links at bottom"
        },
        {
          "name": "quickLinksDisplay",
          "type": "'list' | 'icons'",
          "default": "'list'",
          "required": false,
          "description": "Quick links display mode"
        },
        {
          "name": "onNavigate",
          "type": "(item: NavItem) => void",
          "required": false,
          "description": "Navigation callback"
        },
        {
          "name": "headerStart",
          "type": "Snippet",
          "required": false,
          "description": "Content after toggle button"
        },
        {
          "name": "headerEnd",
          "type": "Snippet",
          "required": false,
          "description": "Content at end of header"
        },
        {
          "name": "userMenu",
          "type": "Snippet<[User]>",
          "required": false,
          "description": "Custom user menu"
        },
        {
          "name": "sidebarFooter",
          "type": "Snippet",
          "required": false,
          "description": "Sidebar footer content"
        },
        {
          "name": "expandedWidth",
          "type": "number",
          "default": "256",
          "required": false,
          "description": "Expanded sidebar width (px)"
        },
        {
          "name": "collapsedWidth",
          "type": "number",
          "default": "64",
          "required": false,
          "description": "Collapsed sidebar width (px)"
        },
        {
          "name": "searchable",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Enable navigation search"
        },
        {
          "name": "searchPlaceholder",
          "type": "string",
          "default": "'Search...'",
          "required": false,
          "description": "Search input placeholder"
        },
        {
          "name": "headerSearch",
          "type": "HeaderSearchConfig",
          "required": false,
          "description": "Header search configuration"
        },
        {
          "name": "mobileBreakpoint",
          "type": "'sm' | 'md' | 'lg'",
          "default": "'lg'",
          "required": false,
          "description": "Mobile/desktop breakpoint"
        },
        {
          "name": "children",
          "type": "Snippet",
          "required": false,
          "description": "Main content"
        }
      ],
      "variants": [
        {
          "name": "sidebarVariant",
          "values": [
            "light",
            "dark"
          ],
          "default": "light"
        },
        {
          "name": "quickLinksDisplay",
          "values": [
            "list",
            "icons"
          ],
          "default": "list"
        },
        {
          "name": "mobileBreakpoint",
          "values": [
            "sm",
            "md",
            "lg"
          ],
          "default": "lg"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  const navigation = [\n    {\n      id: 'main',\n      title: 'Main',\n      items: [\n        { id: 'dashboard', name: 'Dashboard', href: '/dashboard', icon: 'home' },\n        { id: 'projects', name: 'Projects', href: '/projects', icon: 'folder' },\n      ],\n    },\n  ];\n</script>\n\n<DashboardLayout {navigation}>\n  <h1>Dashboard Content</h1>\n</DashboardLayout>"
        }
      ],
      "relatedComponents": [
        "Sidebar",
        "Header",
        "AppShell"
      ]
    },
    {
      "name": "DataPanel",
      "description": "A flexible, draggable panel component supporting pinned, detached, and card modes",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "open",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Panel visibility"
        },
        {
          "name": "onOpenChange",
          "type": "(open: boolean) => void",
          "required": false,
          "description": "Callback when visibility changes"
        },
        {
          "name": "mode",
          "type": "'pinned' | 'detached'",
          "default": "'pinned'",
          "required": false,
          "description": "Panel mode"
        },
        {
          "name": "onModeChange",
          "type": "(mode: PanelMode) => void",
          "required": false,
          "description": "Callback when mode changes"
        },
        {
          "name": "variant",
          "type": "'full' | 'card'",
          "default": "'full'",
          "required": false,
          "description": "Panel variant"
        },
        {
          "name": "onVariantChange",
          "type": "(variant: PanelVariant) => void",
          "required": false,
          "description": "Callback when variant changes"
        },
        {
          "name": "edge",
          "type": "'left' | 'right' | 'top' | 'bottom'",
          "default": "'right'",
          "required": false,
          "description": "Edge to pin to (pinned mode)"
        },
        {
          "name": "onEdgeChange",
          "type": "(edge: PanelEdge) => void",
          "required": false,
          "description": "Callback when edge changes"
        },
        {
          "name": "expanded",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Whether panel is expanded"
        },
        {
          "name": "onExpandedChange",
          "type": "(expanded: boolean) => void",
          "required": false,
          "description": "Callback when expanded state changes"
        },
        {
          "name": "title",
          "type": "string",
          "required": false,
          "description": "Panel title"
        },
        {
          "name": "subtitle",
          "type": "string",
          "required": false,
          "description": "Panel subtitle"
        },
        {
          "name": "actions",
          "type": "DataPanelAction[]",
          "default": "[]",
          "required": false,
          "description": "Footer action buttons"
        },
        {
          "name": "constraints",
          "type": "PanelConstraints",
          "default": "defaults",
          "required": false,
          "description": "Size constraints"
        },
        {
          "name": "cardConfig",
          "type": "CardConfig",
          "default": "defaults",
          "required": false,
          "description": "Card mode configuration"
        },
        {
          "name": "disableClose",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disable close button"
        },
        {
          "name": "disableResize",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disable resize functionality"
        },
        {
          "name": "disableDrag",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disable drag functionality"
        },
        {
          "name": "disableModeSwitch",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disable mode switching menu"
        },
        {
          "name": "persistKey",
          "type": "string",
          "required": false,
          "description": "localStorage key for state persistence"
        },
        {
          "name": "snapThreshold",
          "type": "number",
          "default": "20",
          "required": false,
          "description": "Distance for edge snap detection (px)"
        },
        {
          "name": "detachThreshold",
          "type": "number",
          "default": "40",
          "required": false,
          "description": "Distance to drag before detaching (px)"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "mode",
          "values": [
            "pinned",
            "detached"
          ],
          "default": "pinned"
        },
        {
          "name": "variant",
          "values": [
            "full",
            "card"
          ],
          "default": "full"
        },
        {
          "name": "edge",
          "values": [
            "left",
            "right",
            "top",
            "bottom"
          ],
          "default": "right"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let open = $state(true);\n</script>\n\n<DataPanel bind:open title=\"Details\" subtitle=\"View information\">\n  <p>Panel content goes here.</p>\n</DataPanel>"
        },
        {
          "title": "Pinned Mode (Default)",
          "description": "The panel attaches to a viewport edge and can be resized along the perpendicular axis.",
          "code": "<DataPanel mode=\"pinned\" edge=\"right\" title=\"Right Sidebar\">\n  <p>Pinned to the right edge.</p>\n</DataPanel>"
        },
        {
          "title": "Detached Mode",
          "description": "A floating window that can be freely dragged and resized. Snaps to edges when dragged near them.",
          "code": "<DataPanel mode=\"detached\" title=\"Floating Panel\">\n  <p>Drag me anywhere!</p>\n</DataPanel>"
        },
        {
          "title": "Full Variant (Default)",
          "description": "Traditional sidebar/panel that takes the full width or height of its pinned edge.",
          "code": "<DataPanel variant=\"full\" edge=\"right\">\n  <p>Full-height sidebar.</p>\n</DataPanel>"
        },
        {
          "title": "Card Variant",
          "description": "Mobile-friendly bottom sheet with snap points. Swipe up to expand, down to collapse.",
          "code": "<DataPanel variant=\"card\" title=\"Details\" subtitle=\"Swipe to expand\">\n  <p>Card content with snap points.</p>\n</DataPanel>"
        },
        {
          "title": "Pinned Sidebar with Actions",
          "code": "<script>\n  let panelOpen = $state(true);\n\n  const actions = [\n    { label: 'Cancel', variant: 'ghost', onClick: () => (panelOpen = false) },\n    { label: 'Save', variant: 'primary', onClick: handleSave },\n  ];\n</script>\n\n<DataPanel\n  bind:open={panelOpen}\n  title=\"Edit Item\"\n  subtitle=\"Make changes to your item\"\n  edge=\"right\"\n  {actions}\n>\n  <form>\n    <FormField label=\"Name\" name=\"name\" />\n    <FormField label=\"Description\" name=\"description\" type=\"textarea\" />\n  </form>\n</DataPanel>"
        },
        {
          "title": "Detached Floating Panel",
          "code": "<script>\n  let inspectorOpen = $state(false);\n</script>\n\n<Button onclick={() => (inspectorOpen = true)}>Open Inspector</Button>\n\n<DataPanel\n  bind:open={inspectorOpen}\n  mode=\"detached\"\n  title=\"Inspector\"\n  constraints={{ minWidth: 300, maxWidth: 500 }}\n>\n  <div class=\"space-y-4\">\n    <p>Inspect element properties here.</p>\n  </div>\n</DataPanel>"
        },
        {
          "title": "Mobile Bottom Sheet (Card Mode)",
          "code": "<script>\n  let detailsOpen = $state(true);\n  let expanded = $state(false);\n</script>\n\n<DataPanel\n  bind:open={detailsOpen}\n  bind:expanded\n  variant=\"card\"\n  title=\"Location Details\"\n  subtitle=\"123 Main Street\"\n  cardConfig={{\n    maxWidth: 480,\n    showDragHandle: true,\n  }}\n>\n  <div class=\"space-y-4\">\n    <img src=\"/location.jpg\" alt=\"Location\" class=\"w-full rounded\" />\n    <p>Additional details about this location.</p>\n  </div>\n</DataPanel>"
        },
        {
          "title": "With State Persistence",
          "code": "<DataPanel title=\"Preferences\" persistKey=\"user-preferences-panel\">\n  <p>Panel state (position, size, mode) is saved to localStorage.</p>\n</DataPanel>"
        },
        {
          "title": "Custom Header and Footer",
          "code": "<DataPanel title=\"Custom Panel\">\n  {#snippet header()}\n    <div class=\"flex items-center gap-2\">\n      <Avatar size=\"sm\">JD</Avatar>\n      <div>\n        <p class=\"font-medium\">John Doe</p>\n        <p class=\"text-xs text-muted-foreground\">Online</p>\n      </div>\n    </div>\n  {/snippet}\n\n  <p>Panel content here.</p>\n\n  {#snippet footer()}\n    <div class=\"flex justify-between w-full\">\n      <Button variant=\"ghost\" size=\"sm\">Help</Button>\n      <Button size=\"sm\">Continue</Button>\n    </div>\n  {/snippet}\n</DataPanel>"
        },
        {
          "title": "Controlled Mode",
          "code": "<script>\n  let mode = $state<'pinned' | 'detached'>('pinned');\n  let edge = $state<'left' | 'right' | 'top' | 'bottom'>('right');\n</script>\n\n<div class=\"flex gap-2 mb-4\">\n  <Button onclick={() => (mode = 'pinned')}>Pin</Button>\n  <Button onclick={() => (mode = 'detached')}>Detach</Button>\n  <Select bind:value={edge} options={['left', 'right', 'top', 'bottom']} />\n</div>\n\n<DataPanel\n  {mode}\n  {edge}\n  onModeChange={(m) => (mode = m)}\n  onEdgeChange={(e) => (edge = e)}\n  title=\"Controlled Panel\"\n>\n  <p>Mode: {mode}, Edge: {edge}</p>\n</DataPanel>"
        }
      ],
      "relatedComponents": [
        "Sidebar",
        "Dialog",
        "Card",
        "DashboardLayout"
      ]
    },
    {
      "name": "DataPanelContent",
      "description": "Scrollable content area for DataPanel",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "children",
          "type": "Snippet / ReactNode",
          "required": false,
          "description": "Content to render (required)"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<DataPanelContent>\n  <p>Your content goes here.</p>\n  <p>Overflow content will scroll.</p>\n</DataPanelContent>"
        }
      ],
      "relatedComponents": [
        "DataPanel",
        "DataPanelHeader",
        "DataPanelFooter"
      ]
    },
    {
      "name": "DataPanelFooter",
      "description": "Footer component for DataPanel with action buttons",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "actions",
          "type": "DataPanelAction[]",
          "default": "[]",
          "required": false,
          "description": "Array of action button configurations"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  const actions = [\n    { label: 'Cancel', variant: 'ghost', onClick: handleCancel },\n    { label: 'Save', variant: 'primary', onClick: handleSave },\n  ];\n</script>\n\n<DataPanelFooter {actions} />"
        }
      ],
      "relatedComponents": [
        "DataPanel",
        "DataPanelHeader",
        "DataPanelContent",
        "Button"
      ]
    },
    {
      "name": "DataPanelHeader",
      "description": "Header component for DataPanel with title, actions, and mode controls",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "title",
          "type": "string",
          "required": false,
          "description": "Panel title text"
        },
        {
          "name": "subtitle",
          "type": "string",
          "required": false,
          "description": "Panel subtitle text"
        },
        {
          "name": "mode",
          "type": "'pinned' | 'detached'",
          "required": false,
          "description": "Current panel mode (required)"
        },
        {
          "name": "edge",
          "type": "'left' | 'right' | 'top' | 'bottom'",
          "required": false,
          "description": "Current panel edge (required)"
        },
        {
          "name": "isExpanded",
          "type": "boolean",
          "required": false,
          "description": "Whether panel is expanded (required)"
        },
        {
          "name": "onModeChange",
          "type": "(mode: PanelMode) => void",
          "required": false,
          "description": "Callback when mode changes"
        },
        {
          "name": "onEdgeChange",
          "type": "(edge: PanelEdge) => void",
          "required": false,
          "description": "Callback when edge changes"
        },
        {
          "name": "onExpandedChange",
          "type": "(expanded: boolean) => void",
          "required": false,
          "description": "Callback when expand state changes"
        },
        {
          "name": "onClose",
          "type": "() => void",
          "required": false,
          "description": "Callback when close is clicked"
        },
        {
          "name": "disableClose",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Hide close button"
        },
        {
          "name": "disableModeSwitch",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Hide mode switching menu"
        },
        {
          "name": "showModeMenuOnly",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Only render the mode dropdown"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "mode",
          "values": [
            "pinned",
            "detached"
          ]
        },
        {
          "name": "edge",
          "values": [
            "left",
            "right",
            "top",
            "bottom"
          ]
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<DataPanelHeader\n  title=\"Details\"\n  subtitle=\"View more information\"\n  mode=\"pinned\"\n  edge=\"right\"\n  isExpanded={true}\n  onModeChange={(m) => console.log('Mode:', m)}\n  onEdgeChange={(e) => console.log('Edge:', e)}\n  onClose={() => console.log('Close')}\n/>"
        }
      ],
      "relatedComponents": [
        "DataPanel",
        "DataPanelContent",
        "DataPanelFooter"
      ]
    },
    {
      "name": "DataPanelTab",
      "description": "Collapsed tab indicator for pinned DataPanel",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "title",
          "type": "string",
          "default": "'Panel'",
          "required": false,
          "description": "Tab label text"
        },
        {
          "name": "edge",
          "type": "'left' | 'right' | 'top' | 'bottom'",
          "required": false,
          "description": "Edge position (required)"
        },
        {
          "name": "onclick",
          "type": "() => void",
          "required": false,
          "description": "Click handler to expand panel"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "edge",
          "values": [
            "left",
            "right",
            "top",
            "bottom"
          ]
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<DataPanelTab title=\"Details\" edge=\"right\" onclick={() => console.log('Expand panel')} />"
        }
      ],
      "relatedComponents": [
        "DataPanel",
        "DataPanelHeader"
      ]
    },
    {
      "name": "DataTable",
      "description": "Powerful data table with native Svelte 5 runes - sorting, filtering, pagination, virtualization, and inline editing",
      "category": "data",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "data",
          "type": "T[]",
          "required": false,
          "description": "Array of data rows"
        },
        {
          "name": "columns",
          "type": "DataTableColumn<T>[]",
          "required": false,
          "description": "Column definitions"
        },
        {
          "name": "loading",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Show loading state"
        },
        {
          "name": "loadingRows",
          "type": "number",
          "default": "5",
          "required": false,
          "description": "Number of skeleton rows"
        },
        {
          "name": "emptyMessage",
          "type": "string",
          "default": "'No data available'",
          "required": false,
          "description": "Empty state message"
        },
        {
          "name": "sortColumn",
          "type": "string",
          "required": false,
          "description": "Current sort column ID (bindable)"
        },
        {
          "name": "sortDirection",
          "type": "'asc' | 'desc'",
          "default": "'asc'",
          "required": false,
          "description": "Sort direction (bindable)"
        },
        {
          "name": "onSort",
          "type": "(column: string, direction: 'asc' | 'desc') => void",
          "required": false,
          "description": "Sort callback (enables server-side sorting)"
        },
        {
          "name": "onRowClick",
          "type": "(row: T) => void",
          "required": false,
          "description": "Row click callback"
        },
        {
          "name": "caption",
          "type": "string",
          "required": false,
          "description": "Accessible caption"
        },
        {
          "name": "row",
          "type": "Snippet<[T, number]>",
          "required": false,
          "description": "Custom row renderer"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional classes"
        },
        {
          "name": "enablePagination",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Enable built-in pagination"
        },
        {
          "name": "pageSize",
          "type": "number",
          "default": "10",
          "required": false,
          "description": "Rows per page"
        },
        {
          "name": "getRowId",
          "type": "(row: T, index: number) => string",
          "required": false,
          "description": "Function to get unique row ID"
        }
      ],
      "variants": [
        {
          "name": "sortDirection",
          "values": [
            "asc",
            "desc"
          ],
          "default": "asc"
        },
        {
          "name": "onSort",
          "values": [
            "asc",
            "desc"
          ]
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Creating a Table Instance",
          "code": "<script lang=\"ts\">\n  import {\n    createDataTable,\n    DataTableToolbar,\n    DataTablePagination,\n    type DataTableColumn,\n  } from '@classic-homes/theme-svelte';\n\n  interface User {\n    id: number;\n    name: string;\n    email: string;\n    role: string;\n  }\n\n  // Column definitions\n  const columns: DataTableColumn<User>[] = [\n    { id: 'id', header: 'ID', accessor: 'id', sortable: true },\n    { id: 'name', header: 'Name', accessor: 'name', sortable: true },\n    { id: 'email', header: 'Email', accessor: 'email', sortable: true },\n    {\n      id: 'role',\n      header: 'Role',\n      accessor: 'role',\n      filterOptions: [\n        { value: 'Admin', label: 'Admin' },\n        { value: 'Editor', label: 'Editor' },\n        { value: 'Viewer', label: 'Viewer' },\n      ],\n    },\n  ];\n\n  // Create table instance\n  const table = createDataTable<User>(() => ({\n    data: users,\n    columns,\n    getRowId: (row) => String(row.id),\n  }));\n</script>\n\n<!-- Toolbar with search and filters -->\n<DataTableToolbar {table} />\n\n<!-- Table rendering -->\n<table>\n  <thead>\n    <tr>\n      {#each table.columns as column}\n        <th onclick={() => column.sortable && table.toggleSort(column.id)}>\n          {column.header}\n        </th>\n      {/each}\n    </tr>\n  </thead>\n  <tbody>\n    {#each table.rows as row}\n      <tr>\n        {#each table.columns as column}\n          <td>{table.getFormattedCellValue(row.original, column)}</td>\n        {/each}\n      </tr>\n    {/each}\n  </tbody>\n</table>\n\n<!-- Pagination -->\n<DataTablePagination {table} />"
        },
        {
          "title": "Row Selection",
          "description": "Enable checkbox selection for rows:",
          "code": "<script>\n  let rowSelection = $state<Set<string>>(new Set());\n\n  const table = createDataTable<User>(() => ({\n    data: users,\n    columns,\n    getRowId: (row) => String(row.id),\n    onRowSelectionChange: (selection) => {\n      rowSelection = selection;\n    },\n  }));\n</script>\n\n<!-- Render checkbox in header -->\n<th>\n  <Checkbox\n    checked={table.isAllSelected}\n    indeterminate={table.isSomeSelected}\n    onCheckedChange={() => table.toggleAllRows()}\n  />\n</th>\n\n<!-- Render checkbox in each row -->\n{#each table.rows as row}\n  <td>\n    <Checkbox checked={row.isSelected} onCheckedChange={() => table.toggleRowSelection(row.id)} />\n  </td>\n{/each}"
        },
        {
          "title": "Column Visibility",
          "description": "Allow users to toggle column visibility:",
          "code": "<script>\n  let columnVisibility = $state<Set<string>>(new Set());\n\n  const table = createDataTable<User>(() => ({\n    data: users,\n    columns,\n    getRowId: (row) => String(row.id),\n    onColumnVisibilityChange: (hidden) => {\n      columnVisibility = hidden;\n    },\n  }));\n</script>\n\n<!-- DataTableToolbar includes column visibility dropdown -->\n<DataTableToolbar {table} />"
        },
        {
          "title": "Global Search",
          "description": "Filter across all columns:",
          "code": "<script>\n  let globalFilter = $state('');\n\n  const table = createDataTable<User>(() => ({\n    data: users,\n    columns,\n    getRowId: (row) => String(row.id),\n    onGlobalFilterChange: (filter) => {\n      globalFilter = filter;\n    },\n  }));\n</script>\n\n<!-- DataTableToolbar includes a search input -->\n<DataTableToolbar {table} />"
        },
        {
          "title": "Column Filters",
          "description": "Filter individual columns with faceted filtering:",
          "code": "<script>\n  const columns: DataTableColumn<User>[] = [\n    {\n      id: 'role',\n      header: 'Role',\n      accessor: 'role',\n      filterOptions: [\n        { value: 'Admin', label: 'Admin' },\n        { value: 'Editor', label: 'Editor' },\n        { value: 'Viewer', label: 'Viewer' },\n      ],\n    },\n    {\n      id: 'status',\n      header: 'Status',\n      accessor: 'status',\n      filterOptions: [\n        { value: 'active', label: 'Active' },\n        { value: 'inactive', label: 'Inactive' },\n      ],\n    },\n  ];\n\n  const table = createDataTable<User>(() => ({\n    data: users,\n    columns,\n    getRowId: (row) => String(row.id),\n  }));\n</script>\n\n<!-- DataTableToolbar renders filter dropdowns for columns with filterOptions -->\n<DataTableToolbar {table} />"
        },
        {
          "title": "Virtualization",
          "description": "Render thousands of rows efficiently:",
          "code": "<script>\n  const table = createDataTable<User>(() => ({\n    data: largeDataset, // 10,000+ rows\n    columns,\n    getRowId: (row) => String(row.id),\n    manualPagination: true, // Disable pagination for virtualization\n  }));\n</script>\n\n<DataTableVirtual\n  {table}\n  rowHeight={48}\n  overscan={10}\n  height=\"500px\"\n  onRowClick={(row) => console.log(row)}\n/>"
        },
        {
          "title": "Inline Editing",
          "description": "Enable double-click cell editing:",
          "code": "<script>\n  function handleCellEdit(rowId: string, columnId: string, value: unknown, row: User) {\n    // Update your data source\n    const index = users.findIndex((u) => String(u.id) === rowId);\n    if (index !== -1) {\n      users[index] = { ...users[index], [columnId]: value };\n    }\n  }\n\n  const columns: DataTableColumn<User>[] = [\n    { id: 'name', header: 'Name', accessor: 'name', enableEditing: true },\n    { id: 'email', header: 'Email', accessor: 'email', enableEditing: true },\n  ];\n</script>\n\n{#each table.rows as row}\n  <tr>\n    {#each table.columns as column}\n      <td>\n        <DataTableEditableCell {row} {column} onSave={handleCellEdit} />\n      </td>\n    {/each}\n  </tr>\n{/each}"
        }
      ],
      "relatedComponents": [
        "Skeleton",
        "Badge",
        "Button",
        "Checkbox"
      ]
    },
    {
      "name": "DataTableColumnFilter",
      "description": "Per-column text filter input for filtering individual columns in data tables",
      "category": "data",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "table",
          "type": "DataTableInstance<T>",
          "default": "**required**",
          "required": false,
          "description": "The table instance from createDataTable"
        },
        {
          "name": "column",
          "type": "DataTableColumn<T>",
          "default": "**required**",
          "required": false,
          "description": "The column to filter"
        },
        {
          "name": "placeholder",
          "type": "string",
          "default": "'Filter {columnName}...'",
          "required": false,
          "description": "Placeholder text for the input"
        },
        {
          "name": "debounceMs",
          "type": "number",
          "default": "300",
          "required": false,
          "description": "Debounce delay in milliseconds"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { createDataTable, DataTableColumnFilter } from '@classic-homes/theme-svelte';\n\n  const table = createDataTable(() => ({\n    data: users,\n    columns,\n    getRowId: (row) => String(row.id),\n  }));\n\n  // Get a specific column for filtering\n  const nameColumn = $derived(table.columns.find((c) => c.id === 'name'));\n</script>\n\n<!-- Filter for the name column -->\n{#if nameColumn}\n  <DataTableColumnFilter {table} column={nameColumn} />\n{/if}"
        },
        {
          "title": "Custom Placeholder",
          "code": "<DataTableColumnFilter {table} {column} placeholder=\"Search names...\" />"
        },
        {
          "title": "Faster Debounce",
          "code": "<DataTableColumnFilter {table} {column} debounceMs={150} />"
        },
        {
          "title": "In Table Header",
          "code": "<thead>\n  <tr>\n    {#each table.columns as column}\n      <th>\n        {column.header}\n        <DataTableColumnFilter {table} {column} class=\"mt-1\" />\n      </th>\n    {/each}\n  </tr>\n</thead>"
        },
        {
          "title": "Multiple Column Filters",
          "code": "<div class=\"flex gap-2\">\n  {#each table.columns as column}\n    <DataTableColumnFilter {table} {column} class=\"w-40\" />\n  {/each}\n</div>"
        }
      ],
      "relatedComponents": [
        "DataTable",
        "DataTableSearch",
        "DataTableFacetedFilter"
      ]
    },
    {
      "name": "DataTableColumnVisibility",
      "description": "Dropdown component for toggling column visibility in data tables",
      "category": "data",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "table",
          "type": "DataTableInstance<T>",
          "default": "**required**",
          "required": false,
          "description": "The table instance from createDataTable"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { createDataTable, DataTableColumnVisibility } from '@classic-homes/theme-svelte';\n\n  let columnVisibility = $state<Set<string>>(new Set());\n\n  const table = createDataTable(() => ({\n    data: users,\n    columns,\n    getRowId: (row) => String(row.id),\n    onColumnVisibilityChange: (hidden) => {\n      columnVisibility = hidden;\n    },\n  }));\n</script>\n\n<DataTableColumnVisibility {table} />"
        },
        {
          "title": "With Initial Hidden Columns",
          "code": "<script>\n  const table = createDataTable(() => ({\n    data: users,\n    columns,\n    getRowId: (row) => String(row.id),\n    initialHiddenColumns: ['email', 'phone'], // Hide these by default\n  }));\n</script>\n\n<DataTableColumnVisibility {table} />"
        },
        {
          "title": "In Toolbar",
          "code": "<div class=\"flex items-center gap-2\">\n  <DataTableSearch {table} />\n  <DataTableColumnVisibility {table} />\n</div>"
        }
      ],
      "relatedComponents": [
        "DataTable",
        "DataTableToolbar",
        "DropdownMenu"
      ]
    },
    {
      "name": "DataTableEditableCell",
      "description": "Inline editable cell component for data tables with double-click editing",
      "category": "data",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "row",
          "type": "DataTableRow<T>",
          "default": "**required**",
          "required": false,
          "description": "The row object from table.rows"
        },
        {
          "name": "column",
          "type": "DataTableColumn<T>",
          "default": "**required**",
          "required": false,
          "description": "The column definition"
        },
        {
          "name": "onSave",
          "type": "(rowId: string, columnId: string, value: unknown, row: T) => void",
          "default": "**required**",
          "required": false,
          "description": "Callback when cell value is saved"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { createDataTable, DataTableEditableCell } from '@classic-homes/theme-svelte';\n\n  function handleCellEdit(rowId, columnId, value, row) {\n    // Update your data source\n    const index = users.findIndex((u) => String(u.id) === rowId);\n    if (index !== -1) {\n      users[index] = { ...users[index], [columnId]: value };\n    }\n  }\n\n  const columns = [\n    { id: 'name', header: 'Name', accessor: 'name', enableEditing: true },\n    { id: 'email', header: 'Email', accessor: 'email', enableEditing: true },\n    { id: 'role', header: 'Role', accessor: 'role' }, // Not editable\n  ];\n\n  const table = createDataTable(() => ({\n    data: users,\n    columns,\n    getRowId: (row) => String(row.id),\n  }));\n</script>\n\n{#each table.rows as row}\n  <tr>\n    {#each table.columns as column}\n      <td>\n        <DataTableEditableCell {row} {column} onSave={handleCellEdit} />\n      </td>\n    {/each}\n  </tr>\n{/each}"
        },
        {
          "title": "With Custom Formatting",
          "description": "Cells can have custom display formatting while editing the raw value:",
          "code": "<script>\n  const columns = [\n    {\n      id: 'price',\n      header: 'Price',\n      accessor: 'price',\n      enableEditing: true,\n      format: (value) => `$${Number(value).toFixed(2)}`,\n    },\n  ];\n</script>"
        },
        {
          "title": "Async Save Handler",
          "code": "<script>\n  async function handleCellEdit(rowId, columnId, value, row) {\n    try {\n      await api.updateUser(row.id, { [columnId]: value });\n      // Update local state after successful save\n      users = users.map((u) => (String(u.id) === rowId ? { ...u, [columnId]: value } : u));\n    } catch (error) {\n      console.error('Failed to save:', error);\n      // Optionally show error toast\n    }\n  }\n</script>"
        },
        {
          "title": "With Type Conversion",
          "code": "<script>\n  function handleCellEdit(rowId, columnId, value, row) {\n    let parsedValue = value;\n\n    // Convert to number for numeric columns\n    if (columnId === 'salary' || columnId === 'quantity') {\n      parsedValue = parseInt(String(value), 10) || 0;\n    }\n\n    const index = data.findIndex((item) => String(item.id) === rowId);\n    if (index !== -1) {\n      data[index] = { ...data[index], [columnId]: parsedValue };\n    }\n  }\n</script>"
        }
      ],
      "relatedComponents": [
        "DataTable",
        "DataTableVirtual"
      ]
    },
    {
      "name": "DataTableFacetedFilter",
      "description": "Multi-select dropdown filter for categorical columns in data tables",
      "category": "data",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "table",
          "type": "DataTableInstance<T>",
          "default": "**required**",
          "required": false,
          "description": "The table instance from createDataTable"
        },
        {
          "name": "columnId",
          "type": "string",
          "default": "**required**",
          "required": false,
          "description": "The column ID to filter"
        },
        {
          "name": "title",
          "type": "string",
          "default": "**required**",
          "required": false,
          "description": "Title displayed on the filter button"
        },
        {
          "name": "options",
          "type": "FilterOption[]",
          "default": "**required**",
          "required": false,
          "description": "Available filter options"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { createDataTable, DataTableFacetedFilter } from '@classic-homes/theme-svelte';\n\n  const table = createDataTable(() => ({\n    data: users,\n    columns,\n    getRowId: (row) => String(row.id),\n  }));\n</script>\n\n<DataTableFacetedFilter\n  {table}\n  columnId=\"status\"\n  title=\"Status\"\n  options={[\n    { value: 'active', label: 'Active' },\n    { value: 'inactive', label: 'Inactive' },\n    { value: 'pending', label: 'Pending' },\n  ]}\n/>"
        },
        {
          "title": "With Counts",
          "description": "Display the count of items for each option:",
          "code": "<script>\n  // Calculate counts from your data\n  const statusCounts = $derived.by(() => {\n    const counts = { active: 0, inactive: 0, pending: 0 };\n    users.forEach((u) => counts[u.status]++);\n    return counts;\n  });\n\n  const statusOptions = $derived([\n    { value: 'active', label: 'Active', count: statusCounts.active },\n    { value: 'inactive', label: 'Inactive', count: statusCounts.inactive },\n    { value: 'pending', label: 'Pending', count: statusCounts.pending },\n  ]);\n</script>\n\n<DataTableFacetedFilter {table} columnId=\"status\" title=\"Status\" options={statusOptions} />"
        },
        {
          "title": "Multiple Filters",
          "code": "<div class=\"flex gap-2\">\n  <DataTableFacetedFilter {table} columnId=\"status\" title=\"Status\" options={statusOptions} />\n  <DataTableFacetedFilter {table} columnId=\"role\" title=\"Role\" options={roleOptions} />\n  <DataTableFacetedFilter\n    {table}\n    columnId=\"department\"\n    title=\"Department\"\n    options={departmentOptions}\n  />\n</div>"
        },
        {
          "title": "In DataTableToolbar",
          "code": "<DataTableToolbar {table}>\n  {#snippet filters()}\n    <DataTableFacetedFilter {table} columnId=\"status\" title=\"Status\" options={statusOptions} />\n    <DataTableFacetedFilter\n      {table}\n      columnId=\"priority\"\n      title=\"Priority\"\n      options={priorityOptions}\n    />\n  {/snippet}\n</DataTableToolbar>"
        },
        {
          "title": "Dynamic Options from Data",
          "code": "<script>\n  // Extract unique values from data\n  const uniqueStatuses = $derived(\n    [...new Set(users.map((u) => u.status))].map((status) => ({\n      value: status,\n      label: status.charAt(0).toUpperCase() + status.slice(1),\n      count: users.filter((u) => u.status === status).length,\n    }))\n  );\n</script>\n\n<DataTableFacetedFilter {table} columnId=\"status\" title=\"Status\" options={uniqueStatuses} />"
        }
      ],
      "relatedComponents": [
        "DataTable",
        "DataTableToolbar",
        "DataTableColumnFilter"
      ]
    },
    {
      "name": "DataTablePagination",
      "description": "Pagination controls for DataTable with page navigation and rows per page selector",
      "category": "data",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "table",
          "type": "DataTableInstance<T>",
          "default": "**required**",
          "required": false,
          "description": "The table instance from createDataTable"
        },
        {
          "name": "pageSizeOptions",
          "type": "number[]",
          "default": "[10, 25, 50, 100]",
          "required": false,
          "description": "Available page size options"
        },
        {
          "name": "showSelectedCount",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show selected row count when rows are selected"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { createDataTable, DataTablePagination } from '@classic-homes/theme-svelte';\n\n  const table = createDataTable(() => ({\n    data: users,\n    columns,\n    getRowId: (row) => String(row.id),\n  }));\n</script>\n\n<table>...</table>\n<DataTablePagination {table} />"
        },
        {
          "title": "Custom Page Sizes",
          "code": "<DataTablePagination {table} pageSizeOptions={[5, 10, 20, 50]} />"
        },
        {
          "title": "Hide Selected Count",
          "code": "<DataTablePagination {table} showSelectedCount={false} />"
        },
        {
          "title": "With Custom Styling",
          "code": "<DataTablePagination {table} class=\"border-t pt-4\" />"
        }
      ],
      "relatedComponents": [
        "DataTable",
        "DataTableToolbar"
      ]
    },
    {
      "name": "DataTableSearch",
      "description": "Global search input component for filtering all columns in a data table",
      "category": "data",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "table",
          "type": "DataTableInstance<T>",
          "default": "**required**",
          "required": false,
          "description": "The table instance from createDataTable"
        },
        {
          "name": "placeholder",
          "type": "string",
          "default": "'Search...'",
          "required": false,
          "description": "Placeholder text for the input"
        },
        {
          "name": "debounceMs",
          "type": "number",
          "default": "300",
          "required": false,
          "description": "Debounce delay in milliseconds"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { createDataTable, DataTableSearch } from '@classic-homes/theme-svelte';\n\n  let globalFilter = $state('');\n\n  const table = createDataTable(() => ({\n    data: users,\n    columns,\n    getRowId: (row) => String(row.id),\n    onGlobalFilterChange: (filter) => {\n      globalFilter = filter;\n    },\n  }));\n</script>\n\n<DataTableSearch {table} /><table>...</table>"
        },
        {
          "title": "Custom Placeholder",
          "code": "<DataTableSearch {table} placeholder=\"Search users by name or email...\" />"
        },
        {
          "title": "Faster Debounce",
          "description": "For more responsive filtering:",
          "code": "<DataTableSearch {table} debounceMs={150} />"
        },
        {
          "title": "No Debounce",
          "description": "For immediate filtering (smaller datasets):",
          "code": "<DataTableSearch {table} debounceMs={0} />"
        },
        {
          "title": "Custom Width",
          "code": "<DataTableSearch {table} class=\"w-[300px]\" />"
        },
        {
          "title": "In Custom Layout",
          "code": "<div class=\"flex items-center gap-4\">\n  <DataTableSearch {table} class=\"flex-1\" />\n  <Button>Add User</Button>\n</div>"
        }
      ],
      "relatedComponents": [
        "DataTable",
        "DataTableToolbar",
        "DataTableColumnFilter"
      ]
    },
    {
      "name": "DataTableToolbar",
      "description": "Combined toolbar component with search, filters, and column visibility controls",
      "category": "data",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "table",
          "type": "DataTableInstance<T>",
          "default": "**required**",
          "required": false,
          "description": "The table instance from createDataTable"
        },
        {
          "name": "showSearch",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show global search input"
        },
        {
          "name": "showColumnVisibility",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show column visibility dropdown"
        },
        {
          "name": "searchPlaceholder",
          "type": "string",
          "default": "'Search...'",
          "required": false,
          "description": "Placeholder text for search input"
        },
        {
          "name": "filters",
          "type": "Snippet",
          "required": false,
          "description": "Custom filters slot"
        },
        {
          "name": "actions",
          "type": "Snippet",
          "required": false,
          "description": "Custom actions slot"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { createDataTable, DataTableToolbar } from '@classic-homes/theme-svelte';\n\n  const table = createDataTable(() => ({\n    data: users,\n    columns,\n    getRowId: (row) => String(row.id),\n  }));\n</script>\n\n<DataTableToolbar {table} /><table>...</table>"
        },
        {
          "title": "Search Only",
          "code": "<DataTableToolbar {table} showColumnVisibility={false} />"
        },
        {
          "title": "Custom Placeholder",
          "code": "<DataTableToolbar {table} searchPlaceholder=\"Search users...\" />"
        },
        {
          "title": "With Custom Filters",
          "code": "<DataTableToolbar {table}>\n  {#snippet filters()}\n    <DataTableFacetedFilter\n      {table}\n      columnId=\"status\"\n      title=\"Status\"\n      options={[\n        { value: 'active', label: 'Active' },\n        { value: 'inactive', label: 'Inactive' },\n      ]}\n    />\n  {/snippet}\n</DataTableToolbar>"
        },
        {
          "title": "With Custom Actions",
          "code": "<DataTableToolbar {table}>\n  {#snippet actions()}\n    <Button variant=\"outline\" size=\"sm\">Export</Button>\n  {/snippet}\n</DataTableToolbar>"
        },
        {
          "title": "Full Customization",
          "code": "<DataTableToolbar {table} showSearch={true} showColumnVisibility={true}>\n  {#snippet filters()}\n    <DataTableFacetedFilter {table} columnId=\"role\" title=\"Role\" options={roleOptions} />\n    <DataTableFacetedFilter {table} columnId=\"status\" title=\"Status\" options={statusOptions} />\n  {/snippet}\n  {#snippet actions()}\n    <Button onclick={handleExport}>Export CSV</Button>\n    <Button onclick={handleDelete} variant=\"destructive\">Delete Selected</Button>\n  {/snippet}\n</DataTableToolbar>"
        }
      ],
      "relatedComponents": [
        "DataTable",
        "DataTableSearch",
        "DataTableColumnVisibility",
        "DataTableFacetedFilter"
      ]
    },
    {
      "name": "DataTableVirtual",
      "description": "Virtualized table component for efficiently rendering large datasets with 10,000+ rows",
      "category": "data",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "table",
          "type": "DataTableInstance<T>",
          "default": "**required**",
          "required": false,
          "description": "The table instance from createDataTable"
        },
        {
          "name": "rowHeight",
          "type": "number",
          "default": "48",
          "required": false,
          "description": "Height of each row in pixels (must be fixed)"
        },
        {
          "name": "overscan",
          "type": "number",
          "default": "10",
          "required": false,
          "description": "Number of rows to render outside visible area"
        },
        {
          "name": "height",
          "type": "string",
          "default": "'500px'",
          "required": false,
          "description": "Height of the container (CSS value)"
        },
        {
          "name": "onRowClick",
          "type": "(row: T) => void",
          "required": false,
          "description": "Callback when row is clicked"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { createDataTable, DataTableVirtual } from '@classic-homes/theme-svelte';\n\n  const table = createDataTable(() => ({\n    data: largeDataset, // 10,000+ rows\n    columns,\n    getRowId: (row) => String(row.id),\n    manualPagination: true, // Disable pagination for virtualization\n  }));\n</script>\n\n<DataTableVirtual {table} height=\"600px\" />"
        },
        {
          "title": "Custom Row Height",
          "code": "<DataTableVirtual {table} rowHeight={64} height=\"600px\" />"
        },
        {
          "title": "With More Overscan",
          "description": "For smoother scrolling, increase the overscan:",
          "code": "<DataTableVirtual {table} overscan={15} height=\"600px\" />"
        },
        {
          "title": "With Row Click Handler",
          "code": "<DataTableVirtual {table} height=\"600px\" onRowClick={(row) => goto(`/users/${row.id}`)} />"
        },
        {
          "title": "With Row Selection",
          "code": "<script>\n  let rowSelection = $state<Set<string>>(new Set());\n\n  const table = createDataTable(() => ({\n    data: largeDataset,\n    columns,\n    getRowId: (row) => String(row.id),\n    manualPagination: true,\n    onRowSelectionChange: (selection) => {\n      rowSelection = selection;\n    },\n  }));\n</script>\n\n<DataTableVirtual {table} height=\"600px\" />"
        },
        {
          "title": "Full Height Container",
          "code": "<div class=\"flex-1 min-h-0\">\n  <DataTableVirtual {table} height=\"100%\" />\n</div>"
        }
      ],
      "relatedComponents": [
        "DataTable",
        "DataTablePagination"
      ]
    },
    {
      "name": "DateTimePicker",
      "description": "Calendar-based date and time selection",
      "category": "form",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "value",
          "type": "Date | null",
          "default": "null",
          "required": false,
          "description": "Selected date (bindable)"
        },
        {
          "name": "stringValue",
          "type": "string | null",
          "default": "null",
          "required": false,
          "description": "ISO string value (alternative for form binding)"
        },
        {
          "name": "onValueChange",
          "type": "(date: Date | null) => void",
          "required": false,
          "description": "Callback when value changes"
        },
        {
          "name": "onStringValueChange",
          "type": "(value: string | null) => void",
          "required": false,
          "description": "Callback when stringValue changes"
        },
        {
          "name": "placeholder",
          "type": "string",
          "required": false,
          "description": "Placeholder text"
        },
        {
          "name": "disabled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disables the picker"
        },
        {
          "name": "required",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Marks as required"
        },
        {
          "name": "min",
          "type": "Date",
          "required": false,
          "description": "Minimum selectable date"
        },
        {
          "name": "max",
          "type": "Date",
          "required": false,
          "description": "Maximum selectable date"
        },
        {
          "name": "timeFormat",
          "type": "'12h' | '24h'",
          "default": "'12h'",
          "required": false,
          "description": "Time format (12-hour or 24-hour)"
        },
        {
          "name": "minuteStep",
          "type": "number",
          "default": "15",
          "required": false,
          "description": "Minute step interval"
        },
        {
          "name": "name",
          "type": "string",
          "required": false,
          "description": "Name attribute for form submission"
        },
        {
          "name": "id",
          "type": "string",
          "required": false,
          "description": "Element ID"
        },
        {
          "name": "dateOnly",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Show only date picker without time selection"
        },
        {
          "name": "label",
          "type": "string",
          "required": false,
          "description": "Label text for the picker"
        },
        {
          "name": "hint",
          "type": "string",
          "required": false,
          "description": "Hint text displayed below the picker"
        },
        {
          "name": "error",
          "type": "string",
          "required": false,
          "description": "Error message to display"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "timeFormat",
          "values": [
            "12h",
            "24h"
          ],
          "default": "12h"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let date = $state(null);\n</script>\n\n<DateTimePicker bind:value={date} />"
        },
        {
          "title": "Event Scheduler",
          "code": "<DateTimePicker label=\"Event date and time\" bind:value={eventDate} min={new Date()} />"
        },
        {
          "title": "Birth Date",
          "code": "<DateTimePicker\n  label=\"Date of birth\"\n  bind:value={birthDate}\n  max={new Date()}\n  dateOnly\n  placeholder=\"Select your birth date\"\n/>"
        },
        {
          "title": "Booking Window",
          "code": "<script>\n  const today = new Date();\n  const maxDate = new Date();\n  maxDate.setMonth(maxDate.getMonth() + 3);\n</script>\n\n<DateTimePicker label=\"Reservation date\" bind:value={reservationDate} min={today} max={maxDate} />"
        }
      ],
      "relatedComponents": [
        "Input",
        "FormField"
      ]
    },
    {
      "name": "Dialog",
      "description": "Modal window for focused interactions and confirmations",
      "category": "overlay",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "open",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Whether the dialog is open (bindable)"
        },
        {
          "name": "title",
          "type": "string",
          "required": false,
          "description": "Dialog title displayed in header"
        },
        {
          "name": "description",
          "type": "string",
          "required": false,
          "description": "Description text below title"
        },
        {
          "name": "showClose",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Whether to show the close button"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes for content"
        },
        {
          "name": "onOpenChange",
          "type": "(open: boolean) => void",
          "required": false,
          "description": "Callback when open state changes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let open = $state(false);\n</script>\n\n<Button onclick={() => (open = true)}>Open Dialog</Button>\n\n<Dialog bind:open title=\"Dialog Title\" description=\"Dialog description text\">\n  <p>Dialog content goes here.</p>\n</Dialog>"
        },
        {
          "title": "Confirmation Dialog",
          "code": "<script>\n  let deleteDialogOpen = $state(false);\n\n  async function handleDelete() {\n    await deleteItem();\n    deleteDialogOpen = false;\n  }\n</script>\n\n<Button variant=\"destructive\" onclick={() => (deleteDialogOpen = true)}>Delete</Button>\n\n<Dialog\n  bind:open={deleteDialogOpen}\n  title=\"Delete Item\"\n  description=\"This action cannot be undone.\"\n>\n  <p>Are you sure you want to delete this item permanently?</p>\n\n  {#snippet footer()}\n    <Button variant=\"outline\" onclick={() => (deleteDialogOpen = false)}>Cancel</Button>\n    <Button variant=\"destructive\" onclick={handleDelete}>Delete</Button>\n  {/snippet}\n</Dialog>"
        },
        {
          "title": "Form Dialog",
          "code": "<Dialog bind:open title=\"Edit Profile\">\n  <form onsubmit|preventDefault={handleSubmit} class=\"space-y-4\">\n    <FormField label=\"Name\" id=\"name\" bind:value={name} required />\n    <FormField label=\"Email\" id=\"email\" type=\"email\" bind:value={email} required />\n  </form>\n\n  {#snippet footer()}\n    <Button variant=\"outline\" onclick={() => (open = false)}>Cancel</Button>\n    <Button onclick={handleSubmit}>Save Changes</Button>\n  {/snippet}\n</Dialog>"
        },
        {
          "title": "Info Dialog",
          "code": "<Dialog title=\"Keyboard Shortcuts\">\n  <dl class=\"space-y-2\">\n    <div class=\"flex justify-between\">\n      <dt class=\"font-medium\">Save</dt>\n      <dd><kbd class=\"px-2 py-1 bg-muted rounded text-sm\">Ctrl+S</kbd></dd>\n    </div>\n    <div class=\"flex justify-between\">\n      <dt class=\"font-medium\">Search</dt>\n      <dd><kbd class=\"px-2 py-1 bg-muted rounded text-sm\">Ctrl+K</kbd></dd>\n    </div>\n  </dl>\n\n  {#snippet trigger()}\n    <Button variant=\"ghost\" size=\"icon\">\n      <HelpIcon class=\"h-4 w-4\" />\n    </Button>\n  {/snippet}\n</Dialog>"
        },
        {
          "title": "Wide Dialog",
          "code": "<Dialog bind:open title=\"Preview\" class=\"max-w-4xl\">\n  <img src={previewUrl} alt=\"Preview\" class=\"w-full\" />\n</Dialog>"
        }
      ],
      "relatedComponents": [
        "AlertDialog",
        "Tooltip",
        "DropdownMenu"
      ]
    },
    {
      "name": "DonutChart",
      "description": "Pie chart variant with center labels for key metrics",
      "category": "chart",
      "importPath": "@classic-homes/charts-svelte",
      "props": [
        {
          "name": "data",
          "type": "PieSliceData[]",
          "required": true,
          "description": "Array of slice data"
        },
        {
          "name": "showLabels",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Show labels on slices"
        },
        {
          "name": "labelPosition",
          "type": "'inside' | 'outside'",
          "default": "'outside'",
          "required": false,
          "description": "Position of labels"
        },
        {
          "name": "innerRadius",
          "type": "number | string",
          "default": "'50%'",
          "required": false,
          "description": "Inner radius for donut hole"
        },
        {
          "name": "centerLabel",
          "type": "string",
          "required": false,
          "description": "Text label in the center"
        },
        {
          "name": "centerValue",
          "type": "string | number",
          "required": false,
          "description": "Value displayed in the center"
        },
        {
          "name": "onClick",
          "type": "(params: SliceEventParams) => void",
          "required": false,
          "description": "Callback when clicked"
        },
        {
          "name": "title",
          "type": "string",
          "required": true,
          "description": "Chart title for accessibility"
        },
        {
          "name": "height",
          "type": "number | string",
          "default": "400",
          "required": false,
          "description": "Chart height"
        },
        {
          "name": "theme",
          "type": "'light' | 'dark' | 'auto'",
          "default": "'auto'",
          "required": false,
          "description": "Theme preference"
        },
        {
          "name": "showLegend",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show chart legend"
        },
        {
          "name": "showTooltip",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show tooltip on hover"
        }
      ],
      "variants": [
        {
          "name": "labelPosition",
          "values": [
            "inside",
            "outside"
          ],
          "default": "outside"
        },
        {
          "name": "theme",
          "values": [
            "light",
            "dark",
            "auto"
          ],
          "default": "auto"
        }
      ],
      "slots": [],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { DonutChart } from '@classic-homes/charts-svelte';\n\n  const data = [\n    { name: 'Completed', value: 65 },\n    { name: 'In Progress', value: 25 },\n    { name: 'Pending', value: 10 },\n  ];\n</script>\n\n<DonutChart title=\"Task Status\" {data} centerLabel=\"Total\" centerValue=\"100\" height={400} />"
        },
        {
          "title": "Dashboard KPI",
          "code": "<DonutChart title=\"Budget Usage\" {data} centerLabel=\"Spent\" centerValue=\"$45,000\" height={300} />"
        },
        {
          "title": "With Labels",
          "code": "<DonutChart title=\"Category Distribution\" {data} showLabels centerLabel=\"Items\" centerValue=\"156\" />"
        },
        {
          "title": "Custom Inner Radius",
          "code": "<DonutChart title=\"Thin Donut\" {data} innerRadius=\"70%\" />"
        }
      ],
      "relatedComponents": [
        "PieChart",
        "GaugeChart"
      ]
    },
    {
      "name": "DropdownMenu",
      "description": "Contextual menu with items, groups, and keyboard navigation",
      "category": "overlay",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "items",
          "type": "(DropdownMenuItem | DropdownMenuGroup)[]",
          "required": false,
          "description": "Menu items"
        },
        {
          "name": "trigger",
          "type": "Snippet",
          "required": false,
          "description": "Trigger element"
        },
        {
          "name": "side",
          "type": "'top' | 'right' | 'bottom' | 'left'",
          "default": "'bottom'",
          "required": false,
          "description": "Menu position"
        },
        {
          "name": "align",
          "type": "'start' | 'center' | 'end'",
          "default": "'start'",
          "required": false,
          "description": "Menu alignment"
        },
        {
          "name": "open",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Controlled open state (bindable)"
        },
        {
          "name": "onOpenChange",
          "type": "(open: boolean) => void",
          "required": false,
          "description": "Open change callback"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional classes"
        }
      ],
      "variants": [
        {
          "name": "side",
          "values": [
            "top",
            "right",
            "bottom",
            "left"
          ],
          "default": "bottom"
        },
        {
          "name": "align",
          "values": [
            "start",
            "center",
            "end"
          ],
          "default": "start"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  const items = [\n    { label: 'Profile', onSelect: () => goto('/profile') },\n    { label: 'Settings', onSelect: () => goto('/settings') },\n    { type: 'separator' },\n    { label: 'Log out', onSelect: logout, destructive: true },\n  ];\n</script>\n\n<DropdownMenu {items}>\n  {#snippet trigger()}\n    <Button variant=\"outline\">Menu</Button>\n  {/snippet}\n</DropdownMenu>"
        },
        {
          "title": "Regular Items",
          "code": "<script>\n  const items = [\n    { label: 'Edit', onSelect: handleEdit },\n    { label: 'Duplicate', onSelect: handleDuplicate },\n    { label: 'Delete', onSelect: handleDelete, destructive: true },\n  ];\n</script>"
        },
        {
          "title": "With Separators",
          "code": "<script>\n  const items = [\n    { label: 'Cut', shortcut: '⌘X' },\n    { label: 'Copy', shortcut: '⌘C' },\n    { label: 'Paste', shortcut: '⌘V' },\n    { type: 'separator' },\n    { label: 'Select All', shortcut: '⌘A' },\n  ];\n</script>"
        },
        {
          "title": "Labels",
          "code": "<script>\n  const items = [{ type: 'label', label: 'Actions' }, { label: 'Edit' }, { label: 'Delete' }];\n</script>"
        },
        {
          "title": "Disabled Items",
          "code": "<script>\n  const items = [\n    { label: 'Save', disabled: !hasChanges },\n    { label: 'Publish', disabled: !canPublish },\n  ];\n</script>"
        },
        {
          "title": "Side",
          "code": "<!-- Below trigger (default) -->\n<DropdownMenu {items} side=\"bottom\">...</DropdownMenu>\n\n<!-- Above trigger -->\n<DropdownMenu {items} side=\"top\">...</DropdownMenu>\n\n<!-- To the right -->\n<DropdownMenu {items} side=\"right\">...</DropdownMenu>\n\n<!-- To the left -->\n<DropdownMenu {items} side=\"left\">...</DropdownMenu>"
        },
        {
          "title": "Alignment",
          "code": "<!-- Align to start (default) -->\n<DropdownMenu {items} align=\"start\">...</DropdownMenu>\n\n<!-- Center aligned -->\n<DropdownMenu {items} align=\"center\">...</DropdownMenu>\n\n<!-- Align to end -->\n<DropdownMenu {items} align=\"end\">...</DropdownMenu>"
        },
        {
          "title": "User Menu",
          "code": "<script>\n  const items = [\n    { type: 'label', label: user.email },\n    { type: 'separator' },\n    { label: 'Profile', onSelect: () => goto('/profile') },\n    { label: 'Settings', onSelect: () => goto('/settings') },\n    { label: 'Billing', onSelect: () => goto('/billing') },\n    { type: 'separator' },\n    { label: 'Help', onSelect: openHelp },\n    { type: 'separator' },\n    { label: 'Log out', onSelect: logout, destructive: true },\n  ];\n</script>\n\n<DropdownMenu {items} align=\"end\">\n  {#snippet trigger()}\n    <Avatar>{user.initials}</Avatar>\n  {/snippet}\n</DropdownMenu>"
        },
        {
          "title": "Context Actions",
          "code": "<script>\n  const items = [\n    { label: 'Open', onSelect: openItem },\n    { label: 'Open in new tab', onSelect: openInNewTab },\n    { type: 'separator' },\n    { label: 'Rename', shortcut: 'F2', onSelect: rename },\n    { label: 'Duplicate', shortcut: '⌘D', onSelect: duplicate },\n    { label: 'Move to...', onSelect: moveTo },\n    { type: 'separator' },\n    { label: 'Delete', destructive: true, onSelect: deleteItem },\n  ];\n</script>\n\n<DropdownMenu {items}>\n  {#snippet trigger()}\n    <Button variant=\"ghost\" size=\"icon\">\n      <MoreHorizontalIcon />\n    </Button>\n  {/snippet}\n</DropdownMenu>"
        },
        {
          "title": "Table Row Actions",
          "code": "{#each rows as row}\n  <tr>\n    <td>{row.name}</td>\n    <td>\n      <DropdownMenu items={getRowActions(row)}>\n        {#snippet trigger()}\n          <Button variant=\"ghost\" size=\"sm\">Actions</Button>\n        {/snippet}\n      </DropdownMenu>\n    </td>\n  </tr>\n{/each}"
        }
      ],
      "relatedComponents": [
        "Button",
        "AlertDialog",
        "Tooltip"
      ]
    },
    {
      "name": "ErrorLayout",
      "description": "Layout for error pages with status codes and messaging",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "statusCode",
          "type": "number",
          "required": false,
          "description": "HTTP status code (404, 500, etc.)"
        },
        {
          "name": "title",
          "type": "string",
          "required": false,
          "description": "Custom title (overrides default)"
        },
        {
          "name": "description",
          "type": "string",
          "required": false,
          "description": "Error description"
        },
        {
          "name": "showLogo",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show logo at top"
        },
        {
          "name": "logo",
          "type": "Snippet",
          "required": false,
          "description": "Custom logo"
        },
        {
          "name": "showHomeButton",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show \"Go Home\" button"
        },
        {
          "name": "homeUrl",
          "type": "string",
          "default": "'/'",
          "required": false,
          "description": "Home button URL"
        },
        {
          "name": "homeText",
          "type": "string",
          "default": "'Go Home'",
          "required": false,
          "description": "Home button text"
        },
        {
          "name": "showBackButton",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show \"Go Back\" button"
        },
        {
          "name": "backText",
          "type": "string",
          "default": "'Go Back'",
          "required": false,
          "description": "Back button text"
        },
        {
          "name": "illustration",
          "type": "Snippet",
          "required": false,
          "description": "Custom illustration"
        },
        {
          "name": "actions",
          "type": "Snippet",
          "required": false,
          "description": "Additional actions"
        },
        {
          "name": "footer",
          "type": "Snippet",
          "required": false,
          "description": "Footer content"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional classes"
        },
        {
          "name": "children",
          "type": "Snippet",
          "required": false,
          "description": "Custom content"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<ErrorLayout statusCode={404} />"
        },
        {
          "title": "SvelteKit Error Page",
          "code": "<!-- src/routes/+error.svelte -->\n<script>\n  import { page } from '$app/stores';\n  import { ErrorLayout } from '@classic-homes/theme-svelte';\n</script>\n\n<ErrorLayout statusCode={$page.status} description={$page.error?.message} />"
        },
        {
          "title": "Access Denied",
          "code": "<ErrorLayout\n  statusCode={403}\n  title=\"Access Denied\"\n  description=\"You need additional permissions to view this page.\"\n  showBackButton={false}\n>\n  {#snippet actions()}\n    <Button href=\"/request-access\">Request Access</Button>\n    <Button variant=\"outline\" href=\"/dashboard\">Back to Dashboard</Button>\n  {/snippet}\n</ErrorLayout>"
        },
        {
          "title": "Session Expired",
          "code": "<ErrorLayout\n  statusCode={401}\n  title=\"Session Expired\"\n  description=\"Your session has expired. Please sign in again to continue.\"\n  showBackButton={false}\n  showHomeButton={false}\n>\n  {#snippet actions()}\n    <Button href=\"/login\">Sign In Again</Button>\n  {/snippet}\n</ErrorLayout>"
        },
        {
          "title": "Maintenance Mode",
          "code": "<ErrorLayout\n  title=\"Scheduled Maintenance\"\n  description=\"We're currently performing system upgrades. Expected completion: 2:00 PM EST\"\n  showBackButton={false}\n>\n  {#snippet illustration()}\n    <svg class=\"w-32 h-32 text-muted-foreground\">\n      <!-- Maintenance icon -->\n    </svg>\n  {/snippet}\n</ErrorLayout>"
        }
      ],
      "relatedComponents": [
        "AuthLayout",
        "Button",
        "AppShell"
      ]
    },
    {
      "name": "FileUpload",
      "description": "File upload component with drag-and-drop support",
      "category": "form",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "files",
          "type": "FileMetadata[]",
          "default": "[]",
          "required": false,
          "description": "Uploaded files (bindable)"
        },
        {
          "name": "accept",
          "type": "string",
          "required": false,
          "description": "Accepted file types"
        },
        {
          "name": "multiple",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Allow multiple files"
        },
        {
          "name": "maxSize",
          "type": "number",
          "required": false,
          "description": "Max file size in bytes"
        },
        {
          "name": "maxFiles",
          "type": "number",
          "required": false,
          "description": "Max number of files"
        },
        {
          "name": "disabled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disables upload"
        },
        {
          "name": "error",
          "type": "string",
          "required": false,
          "description": "Error message"
        },
        {
          "name": "onUpload",
          "type": "(file: File) => Promise<FileMetadata>",
          "required": false,
          "description": "Upload handler"
        },
        {
          "name": "onRemove",
          "type": "(file: FileMetadata) => void",
          "required": false,
          "description": "Remove handler"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let files = $state([]);\n</script>\n\n<FileUpload bind:files />"
        },
        {
          "title": "Profile Picture",
          "code": "<FileUpload\n  accept=\"image/*\"\n  maxSize={2 * 1024 * 1024}\n  bind:files={avatar}\n  onUpload={uploadAvatar}\n/>"
        },
        {
          "title": "Document Upload",
          "code": "<FileUpload\n  accept=\".pdf,.doc,.docx\"\n  multiple\n  maxFiles={10}\n  maxSize={10 * 1024 * 1024}\n  bind:files={documents}\n  onUpload={uploadDocument}\n/>"
        },
        {
          "title": "With Progress",
          "description": "Files show upload progress:",
          "code": "<FileUpload\n  bind:files\n  onUpload={async (file) => {\n    // Upload with progress tracking\n    return await uploadWithProgress(file, (progress) => {\n      // Progress updates automatically\n    });\n  }}\n/>"
        }
      ],
      "relatedComponents": [
        "Input",
        "Button"
      ]
    },
    {
      "name": "Footer",
      "description": "Site footer with link sections and branding",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "links",
          "type": "NavSection[]",
          "default": "[]",
          "required": false,
          "description": "Link sections"
        },
        {
          "name": "copyright",
          "type": "string",
          "default": "Auto-generated",
          "required": false,
          "description": "Copyright text"
        },
        {
          "name": "showLogo",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show logo"
        },
        {
          "name": "strongBorder",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "10px accent border"
        },
        {
          "name": "variant",
          "type": "'default' | 'dark'",
          "default": "'default'",
          "required": false,
          "description": "Visual variant"
        },
        {
          "name": "children",
          "type": "Snippet",
          "required": false,
          "description": "Custom content"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional classes"
        }
      ],
      "variants": [
        {
          "name": "variant",
          "values": [
            "default",
            "dark"
          ],
          "default": "default"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Footer />"
        },
        {
          "title": "Marketing Site Footer",
          "code": "<script>\n  const links = [\n    {\n      id: 'product',\n      title: 'Product',\n      items: [\n        { id: 'features', name: 'Features', href: '/features' },\n        { id: 'pricing', name: 'Pricing', href: '/pricing' },\n        { id: 'integrations', name: 'Integrations', href: '/integrations' },\n        { id: 'changelog', name: 'Changelog', href: '/changelog' },\n      ],\n    },\n    {\n      id: 'company',\n      title: 'Company',\n      items: [\n        { id: 'about', name: 'About Us', href: '/about' },\n        { id: 'blog', name: 'Blog', href: '/blog' },\n        { id: 'careers', name: 'Careers', href: '/careers' },\n        { id: 'press', name: 'Press Kit', href: '/press' },\n      ],\n    },\n    {\n      id: 'support',\n      title: 'Support',\n      items: [\n        { id: 'help', name: 'Help Center', href: '/help' },\n        { id: 'contact', name: 'Contact Us', href: '/contact' },\n        { id: 'status', name: 'System Status', href: 'https://status.example.com', external: true },\n      ],\n    },\n    {\n      id: 'legal',\n      title: 'Legal',\n      items: [\n        { id: 'privacy', name: 'Privacy', href: '/privacy' },\n        { id: 'terms', name: 'Terms', href: '/terms' },\n        { id: 'security', name: 'Security', href: '/security' },\n      ],\n    },\n  ];\n</script>\n\n<Footer {links} copyright=\"2024 Example Inc. All rights reserved.\" />"
        },
        {
          "title": "Minimal Footer",
          "code": "<Footer copyright=\"2024 Company Name\" showLogo={false} />"
        },
        {
          "title": "Branded Dark Footer",
          "code": "<Footer\n  {links}\n  variant=\"dark\"\n  strongBorder\n  copyright=\"2024 Classic Homes. Building quality since 1985.\"\n>\n  <div class=\"grid grid-cols-3 gap-4 text-center\">\n    <div>\n      <p class=\"text-2xl font-bold\">500+</p>\n      <p class=\"text-sm text-gray-400\">Homes Built</p>\n    </div>\n    <div>\n      <p class=\"text-2xl font-bold\">25+</p>\n      <p class=\"text-sm text-gray-400\">Years Experience</p>\n    </div>\n    <div>\n      <p class=\"text-2xl font-bold\">98%</p>\n      <p class=\"text-sm text-gray-400\">Satisfaction</p>\n    </div>\n  </div>\n</Footer>"
        }
      ],
      "relatedComponents": [
        "Header",
        "PublicLayout",
        "Separator"
      ]
    },
    {
      "name": "FormField",
      "description": "Complete form field component with label, input, and validation",
      "category": "form",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "label",
          "type": "string",
          "default": "Required",
          "required": false,
          "description": "Label text"
        },
        {
          "name": "id",
          "type": "string",
          "default": "Required",
          "required": false,
          "description": "Input ID for accessibility"
        },
        {
          "name": "type",
          "type": "'text' | 'email' | 'password' | 'tel' | 'url' | 'number' | 'date' | 'textarea'",
          "default": "'text'",
          "required": false,
          "description": "Input type"
        },
        {
          "name": "value",
          "type": "string",
          "default": "''",
          "required": false,
          "description": "Field value (bindable)"
        },
        {
          "name": "error",
          "type": "string",
          "required": false,
          "description": "Error message to display"
        },
        {
          "name": "hint",
          "type": "string",
          "required": false,
          "description": "Help text shown below input"
        },
        {
          "name": "disabled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disables the field"
        },
        {
          "name": "required",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Marks as required"
        },
        {
          "name": "placeholder",
          "type": "string",
          "required": false,
          "description": "Placeholder text"
        },
        {
          "name": "autocomplete",
          "type": "AutoFill",
          "required": false,
          "description": "Browser autocomplete hint"
        },
        {
          "name": "name",
          "type": "string",
          "required": false,
          "description": "Name attribute for forms"
        },
        {
          "name": "rows",
          "type": "number",
          "default": "3",
          "required": false,
          "description": "Rows for textarea type"
        },
        {
          "name": "readonly",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Makes field read-only"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        },
        {
          "name": "onValueChange",
          "type": "(value: string) => void",
          "required": false,
          "description": "Callback when value changes"
        }
      ],
      "variants": [
        {
          "name": "type",
          "values": [
            "text",
            "email",
            "password",
            "tel",
            "url",
            "number",
            "date",
            "textarea"
          ],
          "default": "text"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let email = $state('');\n</script>\n\n<FormField label=\"Email\" id=\"email\" type=\"email\" bind:value={email} placeholder=\"your@email.com\" />"
        },
        {
          "title": "Text (Default)",
          "code": "<FormField label=\"Name\" id=\"name\" bind:value={name} />"
        },
        {
          "title": "Email",
          "code": "<FormField label=\"Email\" id=\"email\" type=\"email\" autocomplete=\"email\" bind:value={email} />"
        },
        {
          "title": "Password",
          "code": "<FormField\n  label=\"Password\"\n  id=\"password\"\n  type=\"password\"\n  autocomplete=\"new-password\"\n  bind:value={password}\n/>"
        },
        {
          "title": "Tel (Phone)",
          "code": "<FormField label=\"Phone\" id=\"phone\" type=\"tel\" autocomplete=\"tel\" bind:value={phone} />"
        },
        {
          "title": "Number",
          "code": "<FormField label=\"Age\" id=\"age\" type=\"number\" bind:value={age} />"
        },
        {
          "title": "Date",
          "code": "<FormField label=\"Birth Date\" id=\"birthdate\" type=\"date\" bind:value={birthdate} />"
        },
        {
          "title": "URL",
          "code": "<FormField label=\"Website\" id=\"website\" type=\"url\" placeholder=\"https://\" bind:value={website} />"
        },
        {
          "title": "Textarea",
          "code": "<FormField label=\"Description\" id=\"description\" type=\"textarea\" rows={5} bind:value={description} />"
        },
        {
          "title": "Error State",
          "description": "Display validation errors:",
          "code": "<FormField label=\"Email\" id=\"email\" type=\"email\" bind:value={email} error={emailError} />"
        },
        {
          "title": "With Hint",
          "description": "Provide helpful guidance:",
          "code": "<FormField\n  label=\"Password\"\n  id=\"password\"\n  type=\"password\"\n  bind:value={password}\n  hint=\"Must be at least 8 characters\"\n/>"
        },
        {
          "title": "Disabled",
          "code": "<FormField label=\"Username\" id=\"username\" disabled value=\"johndoe\" />"
        },
        {
          "title": "Read-only",
          "code": "<FormField label=\"ID\" id=\"user-id\" readonly value=\"USR-12345\" />"
        },
        {
          "title": "Login Form",
          "code": "<script>\n  let email = $state('');\n  let password = $state('');\n  let errors = $state({ email: '', password: '' });\n\n  function validate() {\n    errors = { email: '', password: '' };\n\n    if (!email) errors.email = 'Email is required';\n    else if (!email.includes('@')) errors.email = 'Invalid email address';\n\n    if (!password) errors.password = 'Password is required';\n    else if (password.length < 8) errors.password = 'Password must be at least 8 characters';\n\n    return !errors.email && !errors.password;\n  }\n</script>\n\n<form onsubmit|preventDefault={() => validate() && login()}>\n  <div class=\"space-y-4\">\n    <FormField\n      label=\"Email\"\n      id=\"email\"\n      type=\"email\"\n      required\n      autocomplete=\"email\"\n      bind:value={email}\n      error={errors.email}\n    />\n    <FormField\n      label=\"Password\"\n      id=\"password\"\n      type=\"password\"\n      required\n      autocomplete=\"current-password\"\n      bind:value={password}\n      error={errors.password}\n    />\n    <Button type=\"submit\" class=\"w-full\">Sign In</Button>\n  </div>\n</form>"
        },
        {
          "title": "Contact Form",
          "code": "<form class=\"space-y-4\">\n  <FormField label=\"Full Name\" id=\"name\" required autocomplete=\"name\" bind:value={name} />\n  <FormField\n    label=\"Email\"\n    id=\"email\"\n    type=\"email\"\n    required\n    autocomplete=\"email\"\n    bind:value={email}\n  />\n  <FormField\n    label=\"Phone\"\n    id=\"phone\"\n    type=\"tel\"\n    autocomplete=\"tel\"\n    bind:value={phone}\n    hint=\"Optional\"\n  />\n  <FormField\n    label=\"Message\"\n    id=\"message\"\n    type=\"textarea\"\n    rows={5}\n    required\n    bind:value={message}\n    hint=\"Please describe your inquiry in detail\"\n  />\n  <Button type=\"submit\">Send Message</Button>\n</form>"
        },
        {
          "title": "Controlled Field",
          "code": "<script>\n  let value = $state('');\n\n  function handleChange(newValue: string) {\n    // Normalize phone number\n    value = newValue.replace(/\\D/g, '').slice(0, 10);\n  }\n</script>\n\n<FormField\n  label=\"Phone\"\n  id=\"phone\"\n  type=\"tel\"\n  {value}\n  onValueChange={handleChange}\n  hint=\"10 digits only\"\n/>"
        }
      ],
      "relatedComponents": [
        "Input",
        "Label",
        "Textarea"
      ]
    },
    {
      "name": "FormPageLayout",
      "description": "Specialized layout for form pages with optional sidebar",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "title",
          "type": "string",
          "required": false,
          "description": "Page title (h1)"
        },
        {
          "name": "description",
          "type": "string",
          "required": false,
          "description": "Page description text"
        },
        {
          "name": "helpText",
          "type": "string",
          "required": false,
          "description": "Footer help text (supports HTML)"
        },
        {
          "name": "showNotices",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show notices section"
        },
        {
          "name": "sageBackground",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Use sage green background"
        },
        {
          "name": "children",
          "type": "Snippet",
          "required": false,
          "description": "Main form content"
        },
        {
          "name": "notices",
          "type": "Snippet",
          "required": false,
          "description": "Notices content"
        },
        {
          "name": "sidebar",
          "type": "Snippet",
          "required": false,
          "description": "Sidebar content"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<FormPageLayout\n  title=\"Contact Us\"\n  description=\"Fill out the form below and we'll get back to you within 24 hours.\"\n>\n  <form class=\"space-y-4\">\n    <Input label=\"Name\" required />\n    <Input label=\"Email\" type=\"email\" required />\n    <Textarea label=\"Message\" required />\n    <Button type=\"submit\">Send Message</Button>\n  </form>\n</FormPageLayout>"
        },
        {
          "title": "Contact Form",
          "code": "<FormPageLayout\n  title=\"Get in Touch\"\n  description=\"We'd love to hear from you. Send us a message and we'll respond as soon as possible.\"\n  helpText=\"By submitting this form, you agree to our <a href='/privacy'>Privacy Policy</a>.\"\n>\n  {#snippet sidebar()}\n    <div class=\"space-y-6\">\n      <Card>\n        <Card.Header>\n          <Card.Title>Contact Info</Card.Title>\n        </Card.Header>\n        <Card.Content class=\"space-y-3\">\n          <p class=\"text-sm\">\n            <strong>Email:</strong><br />\n            support@example.com\n          </p>\n          <p class=\"text-sm\">\n            <strong>Phone:</strong><br />\n            (555) 123-4567\n          </p>\n        </Card.Content>\n      </Card>\n    </div>\n  {/snippet}\n\n  <Card>\n    <Card.Content class=\"pt-6\">\n      <form class=\"space-y-4\">\n        <div class=\"grid grid-cols-2 gap-4\">\n          <Input label=\"First Name\" required />\n          <Input label=\"Last Name\" required />\n        </div>\n        <Input label=\"Email\" type=\"email\" required />\n        <Input label=\"Phone\" type=\"tel\" />\n        <Select label=\"Subject\" required>\n          <option value=\"\">Select a topic...</option>\n          <option value=\"sales\">Sales Inquiry</option>\n          <option value=\"support\">Technical Support</option>\n          <option value=\"other\">Other</option>\n        </Select>\n        <Textarea label=\"Message\" rows={5} required />\n        <Button type=\"submit\" class=\"w-full\">Send Message</Button>\n      </form>\n    </Card.Content>\n  </Card>\n</FormPageLayout>"
        },
        {
          "title": "Application Form",
          "code": "<FormPageLayout\n  title=\"Apply Now\"\n  description=\"Complete your application in just a few minutes.\"\n  sageBackground\n>\n  {#snippet notices()}\n    <Alert variant=\"info\" class=\"mb-0\">\n      <strong>Save your progress:</strong> Your application will be saved automatically.\n    </Alert>\n  {/snippet}\n\n  {#snippet sidebar()}\n    <div class=\"space-y-4\">\n      <Card>\n        <Card.Header>\n          <Card.Title>Progress</Card.Title>\n        </Card.Header>\n        <Card.Content>\n          <div class=\"space-y-2\">\n            <div class=\"flex justify-between text-sm\">\n              <span>Personal Info</span>\n              <Badge variant=\"success\">Complete</Badge>\n            </div>\n            <div class=\"flex justify-between text-sm\">\n              <span>Employment</span>\n              <Badge variant=\"warning\">In Progress</Badge>\n            </div>\n            <div class=\"flex justify-between text-sm\">\n              <span>Documents</span>\n              <Badge variant=\"outline\">Not Started</Badge>\n            </div>\n          </div>\n        </Card.Content>\n      </Card>\n\n      <Card>\n        <Card.Header>\n          <Card.Title>Need Help?</Card.Title>\n        </Card.Header>\n        <Card.Content>\n          <p class=\"text-sm text-muted-foreground mb-4\">\n            Our team is here to assist you with your application.\n          </p>\n          <Button variant=\"outline\" class=\"w-full\">Chat with Us</Button>\n        </Card.Content>\n      </Card>\n    </div>\n  {/snippet}\n\n  <Card>\n    <Card.Content class=\"pt-6\">\n      <!-- Multi-step form content -->\n    </Card.Content>\n  </Card>\n</FormPageLayout>"
        }
      ],
      "relatedComponents": [
        "Card",
        "Alert",
        "Input",
        "PageHeader"
      ]
    },
    {
      "name": "FunnelChart",
      "description": "Visualize conversion rates and process flows",
      "category": "chart",
      "importPath": "@classic-homes/charts-svelte",
      "props": [
        {
          "name": "data",
          "type": "FunnelSliceData[]",
          "required": true,
          "description": "Array of funnel stages"
        },
        {
          "name": "sort",
          "type": "'ascending' | 'descending' | 'none'",
          "default": "'descending'",
          "required": false,
          "description": "Sort order"
        },
        {
          "name": "orientation",
          "type": "'vertical' | 'horizontal'",
          "default": "'vertical'",
          "required": false,
          "description": "Funnel orientation"
        },
        {
          "name": "showLabels",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show value labels"
        },
        {
          "name": "onClick",
          "type": "(params: SliceEventParams) => void",
          "required": false,
          "description": "Callback when clicked"
        },
        {
          "name": "title",
          "type": "string",
          "required": true,
          "description": "Chart title for accessibility"
        },
        {
          "name": "height",
          "type": "number | string",
          "default": "400",
          "required": false,
          "description": "Chart height"
        },
        {
          "name": "theme",
          "type": "'light' | 'dark' | 'auto'",
          "default": "'auto'",
          "required": false,
          "description": "Theme preference"
        },
        {
          "name": "showLegend",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show chart legend"
        },
        {
          "name": "showTooltip",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show tooltip on hover"
        }
      ],
      "variants": [
        {
          "name": "sort",
          "values": [
            "ascending",
            "descending",
            "none"
          ],
          "default": "descending"
        },
        {
          "name": "orientation",
          "values": [
            "vertical",
            "horizontal"
          ],
          "default": "vertical"
        },
        {
          "name": "theme",
          "values": [
            "light",
            "dark",
            "auto"
          ],
          "default": "auto"
        }
      ],
      "slots": [],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { FunnelChart } from '@classic-homes/charts-svelte';\n\n  const data = [\n    { name: 'Visited', value: 100 },\n    { name: 'Inquired', value: 80 },\n    { name: 'Ordered', value: 60 },\n    { name: 'Paid', value: 40 },\n    { name: 'Completed', value: 20 },\n  ];\n</script>\n\n<FunnelChart title=\"Conversion Funnel\" {data} sort=\"descending\" showLabels height={400} />"
        },
        {
          "title": "Sales Pipeline",
          "code": "<FunnelChart\n  title=\"Sales Pipeline\"\n  data={[\n    { name: 'Leads', value: 500 },\n    { name: 'Qualified', value: 350 },\n    { name: 'Proposal', value: 200 },\n    { name: 'Negotiation', value: 100 },\n    { name: 'Closed', value: 50 },\n  ]}\n  sort=\"descending\"\n/>"
        },
        {
          "title": "Ascending Funnel",
          "code": "<FunnelChart title=\"Growth Funnel\" {data} sort=\"ascending\" />"
        }
      ],
      "relatedComponents": [
        "BarChart",
        "SankeyChart"
      ]
    },
    {
      "name": "GaugeChart",
      "description": "Display single value metrics with progress indicator",
      "category": "chart",
      "importPath": "@classic-homes/charts-svelte",
      "props": [
        {
          "name": "value",
          "type": "number",
          "required": true,
          "description": "Current value to display"
        },
        {
          "name": "min",
          "type": "number",
          "default": "0",
          "required": false,
          "description": "Minimum value of the gauge"
        },
        {
          "name": "max",
          "type": "number",
          "default": "100",
          "required": false,
          "description": "Maximum value of the gauge"
        },
        {
          "name": "splitNumber",
          "type": "number",
          "default": "10",
          "required": false,
          "description": "Number of tick divisions"
        },
        {
          "name": "showProgress",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Show colored progress arc"
        },
        {
          "name": "formatValue",
          "type": "(value: number) => string",
          "required": false,
          "description": "Custom value formatter"
        },
        {
          "name": "title",
          "type": "string",
          "required": true,
          "description": "Chart title for accessibility"
        },
        {
          "name": "height",
          "type": "number | string",
          "default": "400",
          "required": false,
          "description": "Chart height"
        },
        {
          "name": "theme",
          "type": "'light' | 'dark' | 'auto'",
          "default": "'auto'",
          "required": false,
          "description": "Theme preference"
        },
        {
          "name": "animation",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Enable/disable animations"
        }
      ],
      "variants": [
        {
          "name": "theme",
          "values": [
            "light",
            "dark",
            "auto"
          ],
          "default": "auto"
        }
      ],
      "slots": [],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { GaugeChart } from '@classic-homes/charts-svelte';\n</script>\n\n<GaugeChart title=\"Performance Score\" value={72} min={0} max={100} showProgress height={400} />"
        },
        {
          "title": "Performance Score",
          "code": "<GaugeChart title=\"Performance Score\" value={85} min={0} max={100} showProgress />"
        },
        {
          "title": "With Custom Formatter",
          "code": "<GaugeChart title=\"Completion Rate\" value={72} formatValue={(v) => `${v}%`} showProgress />"
        },
        {
          "title": "Temperature Gauge",
          "code": "<GaugeChart\n  title=\"CPU Temperature\"\n  value={65}\n  min={0}\n  max={100}\n  formatValue={(v) => `${v}°C`}\n  showProgress\n/>"
        },
        {
          "title": "Revenue KPI",
          "code": "<GaugeChart\n  title=\"Monthly Target\"\n  value={85000}\n  min={0}\n  max={100000}\n  formatValue={(v) => `$${(v / 1000).toFixed(0)}K`}\n  showProgress\n/>"
        }
      ],
      "relatedComponents": [
        "DonutChart",
        "RadarChart"
      ]
    },
    {
      "name": "Header",
      "description": "Flexible application header with navigation support",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "showMenuButton",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Show hamburger menu button"
        },
        {
          "name": "menuOpen",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Mobile menu state"
        },
        {
          "name": "onMenuClick",
          "type": "() => void",
          "required": false,
          "description": "Menu button callback"
        },
        {
          "name": "showCollapseButton",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Show sidebar collapse button"
        },
        {
          "name": "sidebarCollapsed",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Sidebar collapsed state"
        },
        {
          "name": "onCollapseClick",
          "type": "() => void",
          "required": false,
          "description": "Collapse button callback"
        },
        {
          "name": "title",
          "type": "string",
          "required": false,
          "description": "Page title"
        },
        {
          "name": "start",
          "type": "Snippet",
          "required": false,
          "description": "Content after buttons"
        },
        {
          "name": "nav",
          "type": "Snippet",
          "required": false,
          "description": "Navigation content"
        },
        {
          "name": "end",
          "type": "Snippet",
          "required": false,
          "description": "Right side content"
        },
        {
          "name": "mobileNav",
          "type": "Snippet",
          "required": false,
          "description": "Mobile navigation drawer"
        },
        {
          "name": "mobileBreakpoint",
          "type": "'sm' | 'md' | 'lg'",
          "default": "'lg'",
          "required": false,
          "description": "Responsive breakpoint"
        },
        {
          "name": "strongBorder",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Thicker bottom border"
        },
        {
          "name": "backdropBlur",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Blur effect"
        },
        {
          "name": "elevated",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Higher z-index"
        },
        {
          "name": "maxWidth",
          "type": "'none' | 'sm' | ... | '7xl'",
          "default": "'none'",
          "required": false,
          "description": "Content max-width"
        },
        {
          "name": "search",
          "type": "HeaderSearchConfig",
          "required": false,
          "description": "Search configuration"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional classes"
        }
      ],
      "variants": [
        {
          "name": "mobileBreakpoint",
          "values": [
            "sm",
            "md",
            "lg"
          ],
          "default": "lg"
        },
        {
          "name": "maxWidth",
          "values": [
            "none",
            "sm",
            "7xl"
          ],
          "default": "none"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Header title=\"Dashboard\" />"
        },
        {
          "title": "E-commerce Header",
          "code": "<Header backdropBlur elevated maxWidth=\"7xl\">\n  {#snippet start()}\n    <a href=\"/\" class=\"font-bold text-xl\">Store</a>\n  {/snippet}\n\n  {#snippet nav()}\n    <a href=\"/products\">Products</a>\n    <a href=\"/categories\">Categories</a>\n    <a href=\"/deals\">Deals</a>\n  {/snippet}\n\n  {#snippet end()}\n    <Button variant=\"ghost\" size=\"icon\">\n      <SearchIcon />\n    </Button>\n    <Button variant=\"ghost\" size=\"icon\">\n      <CartIcon />\n      <Badge class=\"absolute -top-1 -right-1\">3</Badge>\n    </Button>\n    <Button variant=\"ghost\" size=\"icon\">\n      <UserIcon />\n    </Button>\n  {/snippet}\n</Header>"
        },
        {
          "title": "Admin Header",
          "code": "<Header\n  showCollapseButton\n  sidebarCollapsed={!sidebarOpen}\n  onCollapseClick={() => (sidebarOpen = !sidebarOpen)}\n  title=\"Admin Panel\"\n>\n  {#snippet end()}\n    <div class=\"flex items-center gap-4\">\n      <Badge variant=\"outline\">v2.1.0</Badge>\n      <Button variant=\"ghost\" size=\"icon\">\n        <SettingsIcon />\n      </Button>\n      <DropdownMenu>\n        <DropdownMenu.Trigger>\n          <Avatar>AD</Avatar>\n        </DropdownMenu.Trigger>\n        <DropdownMenu.Content>\n          <DropdownMenu.Item>Profile</DropdownMenu.Item>\n          <DropdownMenu.Item>Settings</DropdownMenu.Item>\n          <DropdownMenu.Separator />\n          <DropdownMenu.Item>Log out</DropdownMenu.Item>\n        </DropdownMenu.Content>\n      </DropdownMenu>\n    </div>\n  {/snippet}\n</Header>"
        }
      ],
      "relatedComponents": [
        "Sidebar",
        "DashboardLayout",
        "PublicLayout"
      ]
    },
    {
      "name": "HeaderSearch",
      "description": "Docusaurus-style search component with modal dialog and keyboard navigation",
      "category": "navigation",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "enabled",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Whether search is visible"
        },
        {
          "name": "placeholder",
          "type": "string",
          "default": "'Search...'",
          "required": false,
          "description": "Placeholder text"
        },
        {
          "name": "onSearch",
          "type": "(query: string) => void",
          "required": false,
          "description": "Search callback (debounced 200ms)"
        },
        {
          "name": "onSelect",
          "type": "(item: SearchResultItem) => void",
          "required": false,
          "description": "Selection callback"
        },
        {
          "name": "onOpenChange",
          "type": "(open: boolean) => void",
          "required": false,
          "description": "Dialog open/close callback"
        },
        {
          "name": "results",
          "type": "SearchResultItem[] | SearchResultGroup[]",
          "default": "[]",
          "required": false,
          "description": "Search results to display"
        },
        {
          "name": "loading",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Loading state"
        },
        {
          "name": "emptyMessage",
          "type": "string",
          "default": "'No results found.'",
          "required": false,
          "description": "Empty state message"
        },
        {
          "name": "enableShortcut",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Enable Cmd/Ctrl+K shortcut"
        },
        {
          "name": "shortcutKey",
          "type": "string",
          "default": "'k'",
          "required": false,
          "description": "Custom shortcut key"
        },
        {
          "name": "variant",
          "type": "'default' | 'icon'",
          "default": "'default'",
          "required": false,
          "description": "Trigger button style"
        },
        {
          "name": "size",
          "type": "'sm' | 'md'",
          "default": "'md'",
          "required": false,
          "description": "Trigger button size"
        },
        {
          "name": "renderResult",
          "type": "Snippet<[item, isSelected]>",
          "required": false,
          "description": "Custom result renderer"
        },
        {
          "name": "renderEmpty",
          "type": "Snippet",
          "required": false,
          "description": "Custom empty state renderer"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional classes for trigger"
        }
      ],
      "variants": [
        {
          "name": "variant",
          "values": [
            "default",
            "icon"
          ],
          "default": "default"
        },
        {
          "name": "size",
          "values": [
            "sm",
            "md"
          ],
          "default": "md"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let results: SearchResultItem[] = $state([]);\n  let loading = $state(false);\n\n  async function handleSearch(query: string) {\n    loading = true;\n    results = await fetchSearchResults(query);\n    loading = false;\n  }\n\n  function handleSelect(item: SearchResultItem) {\n    goto(item.href);\n  }\n</script>\n\n<HeaderSearch\n  enabled={true}\n  placeholder=\"Search...\"\n  onSearch={handleSearch}\n  onSelect={handleSelect}\n  {results}\n  {loading}\n/>"
        },
        {
          "title": "Default",
          "description": "Full search box with placeholder and keyboard shortcut hint:",
          "code": "<HeaderSearch variant=\"default\" />"
        },
        {
          "title": "Icon",
          "description": "Compact icon-only button:",
          "code": "<HeaderSearch variant=\"icon\" />"
        },
        {
          "title": "In Header",
          "code": "<Header>\n  <div slot=\"actions\">\n    <HeaderSearch onSearch={handleSearch} onSelect={handleSelect} {results} />\n  </div>\n</Header>"
        },
        {
          "title": "Grouped Results",
          "code": "<script>\n  const groupedResults = [\n    {\n      title: 'Components',\n      items: [\n        { id: '1', title: 'Button', href: '/docs/button' },\n        { id: '2', title: 'Card', href: '/docs/card' },\n      ],\n    },\n    {\n      title: 'Guides',\n      items: [{ id: '3', title: 'Getting Started', href: '/guides/start' }],\n    },\n  ];\n</script>\n\n<HeaderSearch results={groupedResults} />"
        },
        {
          "title": "Custom Result Rendering",
          "code": "<HeaderSearch {results}>\n  {#snippet renderResult(item, isSelected)}\n    <div class=\"flex items-center gap-2\">\n      <Icon name={item.icon} />\n      <span>{item.title}</span>\n      {#if item.badge}\n        <Badge>{item.badge}</Badge>\n      {/if}\n    </div>\n  {/snippet}\n</HeaderSearch>"
        },
        {
          "title": "Custom Empty State",
          "code": "<HeaderSearch {results}>\n  {#snippet renderEmpty()}\n    <div class=\"text-center py-8\">\n      <Icon name=\"search\" class=\"h-8 w-8 mx-auto mb-2 text-muted-foreground\" />\n      <p>No results found</p>\n      <a href=\"/help\" class=\"text-primary\">Need help?</a>\n    </div>\n  {/snippet}\n</HeaderSearch>"
        }
      ],
      "relatedComponents": [
        "Header",
        "Dialog"
      ]
    },
    {
      "name": "HeatmapChart",
      "description": "Matrix data visualization with color intensity",
      "category": "chart",
      "importPath": "@classic-homes/charts-svelte",
      "props": [
        {
          "name": "data",
          "type": "HeatmapData",
          "required": true,
          "description": "Matrix data with coordinates and values"
        },
        {
          "name": "xAxis",
          "type": "string[]",
          "required": true,
          "description": "X-axis labels"
        },
        {
          "name": "yAxis",
          "type": "string[]",
          "required": true,
          "description": "Y-axis labels"
        },
        {
          "name": "showValues",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Display values in cells"
        },
        {
          "name": "onClick",
          "type": "(params: HeatmapEventParams) => void",
          "required": false,
          "description": "Callback when clicked"
        },
        {
          "name": "title",
          "type": "string",
          "required": true,
          "description": "Chart title for accessibility"
        },
        {
          "name": "height",
          "type": "number | string",
          "default": "400",
          "required": false,
          "description": "Chart height"
        },
        {
          "name": "theme",
          "type": "'light' | 'dark' | 'auto'",
          "default": "'auto'",
          "required": false,
          "description": "Theme preference"
        },
        {
          "name": "showTooltip",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show tooltip on hover"
        }
      ],
      "variants": [
        {
          "name": "theme",
          "values": [
            "light",
            "dark",
            "auto"
          ],
          "default": "auto"
        }
      ],
      "slots": [],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { HeatmapChart } from '@classic-homes/charts-svelte';\n\n  const xAxis = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];\n  const yAxis = ['12a', '6a', '12p', '6p'];\n\n  const data = {\n    data: [\n      [0, 0, 5],\n      [0, 1, 1],\n      [0, 2, 0],\n      [0, 3, 2],\n      [1, 0, 1],\n      [1, 1, 0],\n      [1, 2, 3],\n      [1, 3, 4],\n      // ... more data points\n    ],\n    min: 0,\n    max: 5,\n  };\n</script>\n\n<HeatmapChart title=\"Activity Heatmap\" {data} {xAxis} {yAxis} showValues height={400} />"
        },
        {
          "title": "GitHub-style Activity",
          "code": "<HeatmapChart\n  title=\"Commit Activity\"\n  {data}\n  xAxis={['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']}\n  yAxis={['12a', '4a', '8a', '12p', '4p', '8p']}\n/>"
        },
        {
          "title": "Correlation Matrix",
          "code": "<HeatmapChart\n  title=\"Feature Correlation\"\n  {data}\n  xAxis={['Price', 'Size', 'Age', 'Rooms']}\n  yAxis={['Price', 'Size', 'Age', 'Rooms']}\n  showValues\n/>"
        }
      ],
      "relatedComponents": [
        "ScatterChart",
        "TreemapChart"
      ]
    },
    {
      "name": "Icon",
      "description": "A unified icon component with 50+ commonly used icons",
      "category": "core",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "name",
          "type": "IconName",
          "required": false,
          "description": "Name of the icon to display (required)"
        },
        {
          "name": "size",
          "type": "number",
          "default": "24",
          "required": false,
          "description": "Size of the icon in pixels"
        },
        {
          "name": "strokeWidth",
          "type": "number",
          "default": "2",
          "required": false,
          "description": "Stroke width for stroke-based icons"
        },
        {
          "name": "label",
          "type": "string",
          "required": false,
          "description": "Accessible label (renders as visually hidden text)"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Icon name=\"check\" />\n<Icon name=\"close\" />\n<Icon name=\"edit\" />"
        },
        {
          "title": "Navigation & Actions",
          "code": "<Icon name=\"close\" />\n<!-- X mark -->\n<Icon name=\"check\" />\n<!-- Checkmark -->\n<Icon name=\"plus\" />\n<!-- Plus sign -->\n<Icon name=\"minus\" />\n<!-- Minus sign -->\n<Icon name=\"menu\" />\n<!-- Hamburger menu -->\n<Icon name=\"search\" />\n<!-- Magnifying glass -->\n<Icon name=\"home\" />\n<!-- House -->\n<Icon name=\"settings\" />\n<!-- Gear -->\n<Icon name=\"refresh\" />\n<!-- Refresh arrows -->"
        },
        {
          "title": "Arrows & Chevrons",
          "code": "<Icon name=\"chevron-down\" />\n<Icon name=\"chevron-up\" />\n<Icon name=\"chevron-left\" />\n<Icon name=\"chevron-right\" />\n<Icon name=\"arrow-left\" />\n<Icon name=\"arrow-right\" />\n<Icon name=\"external-link\" />"
        },
        {
          "title": "Editing & Files",
          "code": "<Icon name=\"edit\" />\n<!-- Pencil -->\n<Icon name=\"trash\" />\n<!-- Trash can -->\n<Icon name=\"copy\" />\n<!-- Copy documents -->\n<Icon name=\"download\" />\n<!-- Download arrow -->\n<Icon name=\"upload\" />\n<!-- Upload arrow -->\n<Icon name=\"file\" />\n<!-- Document -->\n<Icon name=\"folder\" />\n<!-- Folder -->\n<Icon name=\"image\" />\n<!-- Image placeholder -->"
        },
        {
          "title": "Feedback & Status",
          "code": "<Icon name=\"info\" />\n<!-- Info circle -->\n<Icon name=\"alert-circle\" />\n<!-- Alert circle -->\n<Icon name=\"alert-triangle\" />\n<!-- Warning triangle -->\n<Icon name=\"loading\" />\n<!-- Animated spinner -->"
        },
        {
          "title": "Communication",
          "code": "<Icon name=\"message\" />\n<!-- Speech bubble -->\n<Icon name=\"chat\" />\n<!-- Simple chat bubble -->\n<Icon name=\"mail\" />\n<!-- Envelope -->\n<Icon name=\"phone\" />\n<!-- Phone -->\n<Icon name=\"send\" />\n<!-- Paper plane -->"
        },
        {
          "title": "User & Security",
          "code": "<Icon name=\"user\" />\n<!-- User silhouette -->\n<Icon name=\"lock\" />\n<!-- Locked padlock -->\n<Icon name=\"unlock\" />\n<!-- Unlocked padlock -->\n<Icon name=\"eye\" />\n<!-- Visible eye -->\n<Icon name=\"eye-off\" />\n<!-- Hidden eye -->"
        },
        {
          "title": "UI & Interaction",
          "code": "<Icon name=\"more\" />\n<!-- Vertical dots -->\n<Icon name=\"more-horizontal\" />\n<!-- Horizontal dots -->\n<Icon name=\"grip\" />\n<!-- Drag handle (6 dots) -->\n<Icon name=\"filter\" />\n<!-- Filter funnel -->\n<Icon name=\"sort\" />\n<!-- Sort arrows -->\n<Icon name=\"link\" />\n<!-- Chain link -->"
        },
        {
          "title": "Time & Date",
          "code": "<Icon name=\"calendar\" />\n<!-- Calendar -->\n<Icon name=\"clock\" />\n<!-- Clock face -->\n<Icon name=\"history\" />\n<!-- Clock with history -->"
        },
        {
          "title": "Social & Favorites",
          "code": "<Icon name=\"star\" />\n<!-- Star -->\n<Icon name=\"heart\" />\n<!-- Heart -->\n<Icon name=\"bookmark\" />\n<!-- Bookmark -->"
        },
        {
          "title": "Icon Buttons",
          "code": "<button class=\"p-2 rounded-md hover:bg-muted\">\n  <Icon name=\"close\" size={20} label=\"Close dialog\" />\n</button>\n\n<button class=\"p-2 rounded-md hover:bg-muted\">\n  <Icon name=\"settings\" size={20} label=\"Open settings\" />\n</button>"
        },
        {
          "title": "Button with Icon",
          "code": "<Button>\n  <Icon name=\"plus\" size={18} class=\"mr-2\" />\n  Add Item\n</Button>\n\n<Button variant=\"destructive\">\n  <Icon name=\"trash\" size={18} class=\"mr-2\" />\n  Delete\n</Button>"
        },
        {
          "title": "Status Indicators",
          "code": "<div class=\"flex items-center gap-2\">\n  <Icon name=\"check\" class=\"text-green-500\" size={20} />\n  <span>Completed</span>\n</div>\n\n<div class=\"flex items-center gap-2\">\n  <Icon name=\"alert-circle\" class=\"text-yellow-500\" size={20} />\n  <span>Pending review</span>\n</div>\n\n<div class=\"flex items-center gap-2\">\n  <Icon name=\"close\" class=\"text-red-500\" size={20} />\n  <span>Failed</span>\n</div>"
        },
        {
          "title": "Loading State",
          "code": "<Button disabled>\n  <Icon name=\"loading\" size={18} class=\"mr-2\" />\n  Saving...\n</Button>"
        },
        {
          "title": "Navigation Menu",
          "code": "<nav class=\"space-y-1\">\n  <a href=\"/\" class=\"flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted\">\n    <Icon name=\"home\" size={20} />\n    Home\n  </a>\n  <a href=\"/settings\" class=\"flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted\">\n    <Icon name=\"settings\" size={20} />\n    Settings\n  </a>\n  <a href=\"/profile\" class=\"flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted\">\n    <Icon name=\"user\" size={20} />\n    Profile\n  </a>\n</nav>"
        },
        {
          "title": "Input with Icon",
          "code": "<div class=\"relative\">\n  <Icon\n    name=\"search\"\n    size={18}\n    class=\"absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground\"\n  />\n  <Input class=\"pl-10\" placeholder=\"Search...\" />\n</div>"
        },
        {
          "title": "Password Toggle",
          "code": "<script>\n  let showPassword = $state(false);\n</script>\n\n<div class=\"relative\">\n  <Input type={showPassword ? 'text' : 'password'} />\n  <button\n    type=\"button\"\n    class=\"absolute right-3 top-1/2 -translate-y-1/2\"\n    onclick={() => (showPassword = !showPassword)}\n  >\n    <Icon\n      name={showPassword ? 'eye-off' : 'eye'}\n      size={18}\n      label={showPassword ? 'Hide password' : 'Show password'}\n    />\n  </button>\n</div>"
        }
      ],
      "relatedComponents": [
        "Button",
        "Spinner"
      ]
    },
    {
      "name": "Input",
      "description": "Text input field for single-line data entry with various input modes",
      "category": "form",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "type",
          "type": "string",
          "default": "'text'",
          "required": false,
          "description": "HTML input type"
        },
        {
          "name": "value",
          "type": "string | number",
          "default": "''",
          "required": false,
          "description": "Input value (bindable)"
        },
        {
          "name": "placeholder",
          "type": "string",
          "required": false,
          "description": "Placeholder text"
        },
        {
          "name": "disabled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disables the input"
        },
        {
          "name": "readonly",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Makes the input read-only"
        },
        {
          "name": "required",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Marks as required"
        },
        {
          "name": "name",
          "type": "string",
          "required": false,
          "description": "Input name for forms"
        },
        {
          "name": "id",
          "type": "string",
          "required": false,
          "description": "Input ID for label association"
        },
        {
          "name": "autocomplete",
          "type": "AutoFill",
          "required": false,
          "description": "Browser autocomplete hint"
        },
        {
          "name": "inputmode",
          "type": "'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search'",
          "required": false,
          "description": "Mobile keyboard hint"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        },
        {
          "name": "onValueChange",
          "type": "(value: string) => void",
          "required": false,
          "description": "Callback when value changes"
        }
      ],
      "variants": [
        {
          "name": "inputmode",
          "values": [
            "none",
            "text",
            "tel",
            "url",
            "email",
            "numeric",
            "decimal",
            "search"
          ]
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Input placeholder=\"Enter text...\" />"
        },
        {
          "title": "Text (Default)",
          "code": "<Input type=\"text\" placeholder=\"Enter name\" />"
        },
        {
          "title": "Email",
          "code": "<Input type=\"email\" placeholder=\"your@email.com\" autocomplete=\"email\" />"
        },
        {
          "title": "Password",
          "code": "<Input type=\"password\" placeholder=\"Enter password\" autocomplete=\"current-password\" />"
        },
        {
          "title": "Number",
          "code": "<Input type=\"number\" placeholder=\"0\" />"
        },
        {
          "title": "Search",
          "code": "<Input type=\"search\" placeholder=\"Search...\" />"
        },
        {
          "title": "Tel (Phone)",
          "code": "<Input type=\"tel\" placeholder=\"(555) 123-4567\" autocomplete=\"tel\" inputmode=\"tel\" />"
        },
        {
          "title": "URL",
          "code": "<Input type=\"url\" placeholder=\"https://example.com\" inputmode=\"url\" />"
        },
        {
          "title": "Disabled",
          "code": "<Input disabled value=\"Cannot edit\" />"
        },
        {
          "title": "Read-only",
          "code": "<Input readonly value=\"Read only value\" />"
        },
        {
          "title": "Required",
          "code": "<Input required placeholder=\"Required field\" />"
        },
        {
          "title": "Search Input",
          "code": "<div class=\"relative\">\n  <svg\n    class=\"absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground\"\n    fill=\"none\"\n    viewBox=\"0 0 24 24\"\n    stroke=\"currentColor\"\n  >\n    <path\n      stroke-linecap=\"round\"\n      stroke-linejoin=\"round\"\n      stroke-width=\"2\"\n      d=\"M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z\"\n    />\n  </svg>\n  <Input type=\"search\" placeholder=\"Search...\" class=\"pl-10\" />\n</div>"
        },
        {
          "title": "Input with Button",
          "code": "<div class=\"flex gap-2\">\n  <Input placeholder=\"Enter email\" class=\"flex-1\" />\n  <Button>Subscribe</Button>\n</div>"
        },
        {
          "title": "Custom Width",
          "code": "<Input class=\"w-64\" placeholder=\"Fixed width\" />\n<Input class=\"max-w-sm\" placeholder=\"Max width\" />"
        }
      ],
      "relatedComponents": [
        "Label",
        "FormField",
        "Textarea"
      ]
    },
    {
      "name": "Label",
      "description": "Accessible label component for form inputs",
      "category": "form",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "for",
          "type": "string",
          "required": false,
          "description": "ID of the associated input element"
        },
        {
          "name": "disabled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Applies disabled styling"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Label for=\"email\">Email Address</Label>"
        },
        {
          "title": "Disabled",
          "description": "When the associated input is disabled, apply matching styles to the label:",
          "code": "<div class=\"space-y-2\">\n  <Label for=\"disabled-input\" disabled>Disabled Field</Label>\n  <Input id=\"disabled-input\" disabled value=\"Cannot edit\" />\n</div>"
        },
        {
          "title": "With Peer Styling",
          "description": "Labels automatically style differently when their peer input is disabled:",
          "code": "<div class=\"grid gap-2\">\n  <Input id=\"peer-demo\" disabled class=\"peer\" />\n  <Label for=\"peer-demo\">This label fades when input is disabled</Label>\n</div>"
        },
        {
          "title": "Visually Hidden Label",
          "description": "For inputs where a visible label is not desired (like search), use screen-reader-only styling:",
          "code": "<Label for=\"search\" class=\"sr-only\">Search</Label>\n<Input id=\"search\" type=\"search\" placeholder=\"Search...\" />"
        },
        {
          "title": "Form Field Group",
          "code": "<div class=\"space-y-4\">\n  <div class=\"space-y-2\">\n    <Label for=\"first-name\">First Name</Label>\n    <Input id=\"first-name\" />\n  </div>\n  <div class=\"space-y-2\">\n    <Label for=\"last-name\">Last Name</Label>\n    <Input id=\"last-name\" />\n  </div>\n</div>"
        },
        {
          "title": "Horizontal Layout",
          "code": "<div class=\"flex items-center gap-4\">\n  <Label for=\"inline-input\" class=\"shrink-0 w-24\">Name</Label>\n  <Input id=\"inline-input\" class=\"flex-1\" />\n</div>"
        },
        {
          "title": "With Checkbox",
          "code": "<script>\n  import { Label, Checkbox } from '@classic-homes/theme-svelte';\n</script>\n\n<div class=\"flex items-center gap-2\">\n  <Checkbox id=\"terms\" />\n  <Label for=\"terms\">I agree to the terms and conditions</Label>\n</div>"
        }
      ],
      "relatedComponents": [
        "Input",
        "Checkbox",
        "FormField"
      ]
    },
    {
      "name": "LineChart",
      "description": "Display trends over time with smooth curves and area fill options",
      "category": "chart",
      "importPath": "@classic-homes/charts-svelte",
      "props": [
        {
          "name": "data",
          "type": "LineChartData",
          "required": true,
          "description": "Chart data with categories and series"
        },
        {
          "name": "smooth",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Use smooth curved lines instead of straight segments"
        },
        {
          "name": "areaFilled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Fill the area under the line"
        },
        {
          "name": "showDataPoints",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Show data point markers on the line"
        },
        {
          "name": "stacked",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Stack multiple series on top of each other"
        },
        {
          "name": "onClick",
          "type": "(params: DataPointEventParams) => void",
          "required": false,
          "description": "Callback when a data point is clicked"
        },
        {
          "name": "onHover",
          "type": "(params: DataPointEventParams) => void",
          "required": false,
          "description": "Callback when hovering over a data point"
        },
        {
          "name": "title",
          "type": "string",
          "required": true,
          "description": "Chart title for accessibility (aria-label)"
        },
        {
          "name": "description",
          "type": "string",
          "required": false,
          "description": "Optional description for screen readers"
        },
        {
          "name": "height",
          "type": "number | string",
          "default": "400",
          "required": false,
          "description": "Chart height in pixels or CSS value"
        },
        {
          "name": "loading",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Show loading skeleton"
        },
        {
          "name": "error",
          "type": "string | null",
          "required": false,
          "description": "Error message to display"
        },
        {
          "name": "theme",
          "type": "'light' | 'dark' | 'auto'",
          "default": "'auto'",
          "required": false,
          "description": "Theme preference"
        },
        {
          "name": "animation",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Enable/disable animations"
        },
        {
          "name": "showLegend",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show chart legend"
        },
        {
          "name": "showTooltip",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show tooltip on hover"
        },
        {
          "name": "showGrid",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show grid lines"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Custom CSS class"
        }
      ],
      "variants": [
        {
          "name": "theme",
          "values": [
            "light",
            "dark",
            "auto"
          ],
          "default": "auto"
        }
      ],
      "slots": [],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { LineChart } from '@classic-homes/charts-svelte';\n\n  const data = {\n    categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],\n    series: [\n      { name: '2024', data: [120, 132, 101, 134, 90, 230] },\n      { name: '2025', data: [220, 182, 191, 234, 290, 330] },\n    ],\n  };\n</script>\n\n<LineChart title=\"Monthly Revenue\" {data} smooth height={400} />"
        },
        {
          "title": "Smooth Line Chart",
          "code": "<LineChart title=\"Monthly Sales\" {data} smooth height={400} />"
        },
        {
          "title": "With Data Points",
          "code": "<LineChart title=\"Monthly Sales\" {data} smooth showDataPoints height={400} />"
        },
        {
          "title": "Stacked Lines",
          "code": "<LineChart title=\"Stacked Revenue\" {data} stacked smooth height={400} />"
        },
        {
          "title": "With Area Fill",
          "code": "<LineChart title=\"Revenue Trend\" {data} areaFilled smooth height={400} />"
        },
        {
          "title": "Handling Click Events",
          "code": "<script>\n  function handleClick(params) {\n    console.log('Clicked:', params.seriesName, params.name, params.value);\n  }\n</script>\n\n<LineChart title=\"Interactive Chart\" {data} onClick={handleClick} />"
        }
      ],
      "relatedComponents": [
        "AreaChart",
        "BarChart"
      ]
    },
    {
      "name": "LoadingLogo",
      "description": "Animated brand logo for loading states",
      "category": "branding",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "width",
          "type": "number",
          "default": "40",
          "required": false,
          "description": "Width in pixels"
        },
        {
          "name": "height",
          "type": "number",
          "default": "40",
          "required": false,
          "description": "Height in pixels"
        },
        {
          "name": "loading",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Enable loading animation"
        },
        {
          "name": "variant",
          "type": "'light' | 'dark'",
          "default": "'dark'",
          "required": false,
          "description": "Color variant"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional classes"
        }
      ],
      "variants": [
        {
          "name": "variant",
          "values": [
            "light",
            "dark"
          ],
          "default": "dark"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<LoadingLogo loading />"
        },
        {
          "title": "Dark (Default)",
          "description": "For light backgrounds:",
          "code": "<LoadingLogo variant=\"dark\" loading />"
        },
        {
          "title": "Light",
          "description": "For dark backgrounds:",
          "code": "<div class=\"bg-gray-900 p-8\">\n  <LoadingLogo variant=\"light\" loading />\n</div>"
        },
        {
          "title": "Splash Screen",
          "code": "{#if loading}\n  <div class=\"fixed inset-0 flex items-center justify-center bg-background\">\n    <div class=\"flex flex-col items-center gap-4\">\n      <LoadingLogo width={64} height={64} loading />\n      <p class=\"text-sm text-muted-foreground\">Loading...</p>\n    </div>\n  </div>\n{/if}"
        },
        {
          "title": "Page Loading",
          "code": "{#if pageLoading}\n  <div class=\"flex items-center justify-center min-h-[400px]\">\n    <LoadingLogo loading />\n  </div>\n{:else}\n  <main>{pageContent}</main>\n{/if}"
        },
        {
          "title": "Dark Background Splash",
          "code": "<div class=\"fixed inset-0 flex items-center justify-center bg-gray-900\">\n  <LoadingLogo variant=\"light\" width={80} height={80} loading />\n</div>"
        },
        {
          "title": "Button Loading State",
          "code": "<Button disabled={submitting}>\n  {#if submitting}\n    <LoadingLogo width={20} height={20} loading class=\"mr-2\" />\n    Saving...\n  {:else}\n    Save\n  {/if}\n</Button>"
        },
        {
          "title": "Initial App Load",
          "code": "<!-- +layout.svelte -->\n<script>\n  let appReady = $state(false);\n\n  onMount(() => {\n    // Initialize app\n    setTimeout(() => (appReady = true), 1500);\n  });\n</script>\n\n{#if !appReady}\n  <div class=\"fixed inset-0 z-50 flex items-center justify-center bg-background\">\n    <LoadingLogo width={64} height={64} loading />\n  </div>\n{/if}\n\n<div class={appReady ? '' : 'hidden'}>\n  <slot />\n</div>"
        }
      ],
      "relatedComponents": [
        "LogoMain",
        "LoadingPage",
        "LoadingOverlay",
        "Spinner",
        "Skeleton"
      ]
    },
    {
      "name": "LoadingOverlay",
      "description": "Flexible loading overlay for fullscreen, section, or inline use",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "loading",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Whether loading is active"
        },
        {
          "name": "variant",
          "type": "'spinner' | 'logo' | 'progress' | 'skeleton'",
          "default": "'spinner'",
          "required": false,
          "description": "Which indicator to show"
        },
        {
          "name": "mode",
          "type": "'fullscreen' | 'overlay' | 'inline'",
          "default": "'overlay'",
          "required": false,
          "description": "Positioning mode"
        },
        {
          "name": "size",
          "type": "'sm' | 'md' | 'lg' | 'xl'",
          "default": "'md'",
          "required": false,
          "description": "Indicator size"
        },
        {
          "name": "message",
          "type": "string",
          "required": false,
          "description": "Loading message text"
        },
        {
          "name": "progress",
          "type": "number",
          "required": false,
          "description": "Progress value (0–100) for progress variant"
        },
        {
          "name": "blur",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Backdrop blur on overlay/fullscreen modes"
        },
        {
          "name": "opaque",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Fully opaque background"
        },
        {
          "name": "delay",
          "type": "number",
          "default": "0",
          "required": false,
          "description": "Delay in ms before showing"
        },
        {
          "name": "children",
          "type": "Snippet (Svelte) / ReactNode (React)",
          "required": false,
          "description": "Content behind the overlay"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "variant",
          "values": [
            "spinner",
            "logo",
            "progress",
            "skeleton"
          ],
          "default": "spinner"
        },
        {
          "name": "mode",
          "values": [
            "fullscreen",
            "overlay",
            "inline"
          ],
          "default": "overlay"
        },
        {
          "name": "size",
          "values": [
            "sm",
            "md",
            "lg",
            "xl"
          ],
          "default": "md"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<!-- Overlay on a section (default mode) -->\n<div class=\"relative\" style=\"height: 200px;\">\n  <p>Content behind the overlay</p>\n  <LoadingOverlay loading />\n</div>"
        },
        {
          "title": "Fullscreen",
          "description": "Covers the entire viewport, typically for initial app loading.",
          "code": "<LoadingOverlay loading mode=\"fullscreen\" variant=\"logo\" message=\"Starting app...\" />"
        },
        {
          "title": "Inline",
          "description": "Renders as an inline element, useful for loading states within text or buttons.",
          "code": "<LoadingOverlay mode=\"inline\" variant=\"spinner\" size=\"sm\" message=\"Saving...\" />"
        },
        {
          "title": "Spinner",
          "description": "Default circular spinning indicator:",
          "code": "<LoadingOverlay variant=\"spinner\" />"
        },
        {
          "title": "Logo",
          "description": "Branded loading with the Classic Homes logo animation:",
          "code": "<LoadingOverlay variant=\"logo\" />"
        },
        {
          "title": "Skeleton",
          "description": "Content placeholder skeleton:",
          "code": "<LoadingOverlay variant=\"skeleton\" />"
        },
        {
          "title": "Section Loading",
          "code": "<div class=\"relative border rounded-lg\" style=\"min-height: 200px;\">\n  <div class=\"p-4\">\n    <h3>Dashboard Stats</h3>\n    <p>Revenue: $45,231</p>\n  </div>\n  <LoadingOverlay loading={isLoading} message=\"Refreshing...\" />\n</div>"
        },
        {
          "title": "Inline Save Indicator",
          "code": "<div class=\"flex items-center gap-4\">\n  <Button onclick={save}>Save</Button>\n  {#if saving}\n    <LoadingOverlay mode=\"inline\" size=\"sm\" message=\"Saving changes...\" />\n  {/if}\n</div>"
        },
        {
          "title": "Opaque Overlay",
          "code": "<!-- Fully opaque, hides content completely -->\n<div class=\"relative\" style=\"height: 200px;\">\n  <p>Sensitive content</p>\n  <LoadingOverlay loading opaque />\n</div>"
        },
        {
          "title": "Without Blur",
          "code": "<div class=\"relative\" style=\"height: 200px;\">\n  <p>Content visible without blur</p>\n  <LoadingOverlay loading blur={false} />\n</div>"
        }
      ],
      "relatedComponents": [
        "LoadingPage",
        "LoadingSection",
        "Spinner",
        "ProgressBar",
        "Skeleton"
      ]
    },
    {
      "name": "LoadingPage",
      "description": "Full-page branded loading screen with logo and progress",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "loading",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Whether loading is active"
        },
        {
          "name": "message",
          "type": "string",
          "required": false,
          "description": "Loading message (e.g. \"Loading...\")"
        },
        {
          "name": "progress",
          "type": "number",
          "required": false,
          "description": "Determinate progress value (0–100)"
        },
        {
          "name": "showLogo",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show branded LoadingLogo"
        },
        {
          "name": "logoVariant",
          "type": "'light' | 'dark'",
          "default": "'dark'",
          "required": false,
          "description": "Logo color variant"
        },
        {
          "name": "delay",
          "type": "number",
          "default": "200",
          "required": false,
          "description": "Delay in ms before showing"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "logoVariant",
          "values": [
            "light",
            "dark"
          ],
          "default": "dark"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "{#if appLoading}\n  <LoadingPage loading message=\"Loading your experience...\" />\n{/if}"
        },
        {
          "title": "App Initialization",
          "code": "<script>\n  import { LoadingPage } from '@classic-homes/theme-svelte';\n\n  let appReady = $state(false);\n\n  onMount(async () => {\n    await initializeApp();\n    appReady = true;\n  });\n</script>\n\n{#if !appReady}\n  <LoadingPage loading message=\"Initializing...\" />\n{/if}\n\n{#if appReady}\n  <App />\n{/if}"
        },
        {
          "title": "With Progress",
          "description": "Show determinate progress for operations with known duration:",
          "code": "<script>\n  let progress = $state(0);\n  let message = $state('Downloading resources...');\n\n  async function loadApp() {\n    for (let i = 0; i <= 100; i += 10) {\n      progress = i;\n      if (i >= 50) message = 'Processing data...';\n      if (i >= 80) message = 'Almost done...';\n      await new Promise((r) => setTimeout(r, 200));\n    }\n  }\n</script>\n\n<LoadingPage loading {progress} {message} />"
        },
        {
          "title": "Without Logo",
          "description": "Use a plain spinner instead of the branded logo:",
          "code": "<LoadingPage loading showLogo={false} message=\"Loading...\" />"
        },
        {
          "title": "Light Variant (Dark Background)",
          "description": "If your app has a dark initial background:",
          "code": "<LoadingPage loading logoVariant=\"light\" class=\"bg-gray-900\" />"
        },
        {
          "title": "Route Transition",
          "code": "<!-- SvelteKit layout -->\n<script>\n  import { navigating } from '$app/stores';\n  import { LoadingPage } from '@classic-homes/theme-svelte';\n</script>\n\n{#if $navigating}\n  <LoadingPage loading message=\"Loading page...\" delay={300} />\n{/if}\n\n<slot />"
        }
      ],
      "relatedComponents": [
        "LoadingOverlay",
        "LoadingSection",
        "LoadingLogo",
        "ProgressBar",
        "Spinner"
      ]
    },
    {
      "name": "LoadingSection",
      "description": "Section-level content wrapper with loading overlay",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "loading",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Whether loading is active"
        },
        {
          "name": "variant",
          "type": "'spinner' | 'skeleton' | 'logo' | 'progress'",
          "default": "'spinner'",
          "required": false,
          "description": "Indicator type"
        },
        {
          "name": "message",
          "type": "string",
          "required": false,
          "description": "Loading message"
        },
        {
          "name": "progress",
          "type": "number",
          "required": false,
          "description": "Progress value (0–100)"
        },
        {
          "name": "minHeight",
          "type": "string",
          "default": "'12rem'",
          "required": false,
          "description": "Minimum height to preserve layout"
        },
        {
          "name": "blur",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Backdrop blur effect"
        },
        {
          "name": "delay",
          "type": "number",
          "default": "0",
          "required": false,
          "description": "Delay in ms before showing"
        },
        {
          "name": "children",
          "type": "Snippet (Svelte) / ReactNode (React)",
          "required": false,
          "description": "Content being loaded"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "variant",
          "values": [
            "spinner",
            "skeleton",
            "logo",
            "progress"
          ],
          "default": "spinner"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<LoadingSection loading={isLoading}>\n  <div class=\"p-4\">\n    <h3>Dashboard Stats</h3>\n    <p>Revenue: $45,231</p>\n  </div>\n</LoadingSection>"
        },
        {
          "title": "Spinner (default)",
          "code": "<LoadingSection loading variant=\"spinner\" message=\"Loading...\">\n  <MyContent />\n</LoadingSection>"
        },
        {
          "title": "Skeleton",
          "code": "<LoadingSection loading variant=\"skeleton\">\n  <MyContent />\n</LoadingSection>"
        },
        {
          "title": "Logo",
          "code": "<LoadingSection loading variant=\"logo\">\n  <MyContent />\n</LoadingSection>"
        },
        {
          "title": "Progress",
          "code": "<LoadingSection loading variant=\"progress\" progress={45}>\n  <MyContent />\n</LoadingSection>"
        },
        {
          "title": "Dashboard Panels",
          "description": "Multiple sections loading independently:",
          "code": "<div class=\"grid grid-cols-2 gap-4\">\n  <LoadingSection loading={statsLoading} message=\"Loading stats...\">\n    <Card>\n      <CardHeader><CardTitle>Revenue</CardTitle></CardHeader>\n      <CardContent><p class=\"text-2xl font-bold\">$45,231</p></CardContent>\n    </Card>\n  </LoadingSection>\n\n  <LoadingSection loading={chartLoading}>\n    <Card>\n      <CardHeader><CardTitle>Chart</CardTitle></CardHeader>\n      <CardContent><MyChart /></CardContent>\n    </Card>\n  </LoadingSection>\n</div>"
        },
        {
          "title": "Data Table Loading",
          "code": "<LoadingSection loading={tableLoading} variant=\"skeleton\" minHeight=\"20rem\">\n  <DataTable {columns} data={rows} />\n</LoadingSection>"
        },
        {
          "title": "Form Submission",
          "code": "<LoadingSection loading={submitting} message=\"Saving changes...\" minHeight=\"auto\">\n  <form onsubmit={handleSubmit}>\n    <Input name=\"email\" />\n    <Button type=\"submit\">Save</Button>\n  </form>\n</LoadingSection>"
        },
        {
          "title": "Without Blur",
          "code": "<LoadingSection loading blur={false}>\n  <p>Content visible without blur behind overlay</p>\n</LoadingSection>"
        },
        {
          "title": "With Delay",
          "description": "Prevent flash for typically-fast operations:",
          "code": "<LoadingSection loading={saving} delay={300} message=\"Saving...\">\n  <MyForm />\n</LoadingSection>"
        },
        {
          "title": "Fetch Pattern",
          "code": "<script>\n  import { LoadingSection } from '@classic-homes/theme-svelte';\n\n  let data = $state(null);\n  let loading = $state(true);\n  let error = $state(null);\n\n  onMount(async () => {\n    try {\n      const res = await fetch('/api/data');\n      data = await res.json();\n    } catch (e) {\n      error = e;\n    } finally {\n      loading = false;\n    }\n  });\n</script>\n\n<LoadingSection {loading}>\n  {#if error}\n    <Alert variant=\"destructive\">{error.message}</Alert>\n  {:else if data}\n    <DataDisplay {data} />\n  {/if}\n</LoadingSection>"
        }
      ],
      "relatedComponents": [
        "LoadingOverlay",
        "LoadingPage",
        "Skeleton",
        "Spinner"
      ]
    },
    {
      "name": "LogoMain",
      "description": "Brand logo component with multiple layouts and variants",
      "category": "branding",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "variant",
          "type": "'horizontal' | 'stacked' | 'icon'",
          "default": "'horizontal'",
          "required": false,
          "description": "Layout variant"
        },
        {
          "name": "color",
          "type": "'dark' | 'light' | 'mono-dark' | 'mono-light'",
          "default": "'dark'",
          "required": false,
          "description": "Color scheme"
        },
        {
          "name": "subtitle",
          "type": "string",
          "required": false,
          "description": "Optional subtitle text"
        },
        {
          "name": "environment",
          "type": "'local' | 'dev' | 'demo'",
          "required": false,
          "description": "Environment indicator"
        },
        {
          "name": "size",
          "type": "'sm' | 'md' | 'lg'",
          "default": "'md'",
          "required": false,
          "description": "Size preset"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional classes"
        },
        {
          "name": "ariaLabel",
          "type": "string",
          "default": "'Classic Homes'",
          "required": false,
          "description": "Accessible label"
        }
      ],
      "variants": [
        {
          "name": "variant",
          "values": [
            "horizontal",
            "stacked",
            "icon"
          ],
          "default": "horizontal"
        },
        {
          "name": "color",
          "values": [
            "dark",
            "light",
            "mono-dark",
            "mono-light"
          ],
          "default": "dark"
        },
        {
          "name": "environment",
          "values": [
            "local",
            "dev",
            "demo"
          ]
        },
        {
          "name": "size",
          "values": [
            "sm",
            "md",
            "lg"
          ],
          "default": "md"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<LogoMain />"
        },
        {
          "title": "Horizontal (Default)",
          "description": "Logo icon and text side by side:",
          "code": "<LogoMain variant=\"horizontal\" />"
        },
        {
          "title": "Stacked",
          "description": "Logo icon above text:",
          "code": "<LogoMain variant=\"stacked\" />"
        },
        {
          "title": "Icon Only",
          "description": "Just the logo icon:",
          "code": "<LogoMain variant=\"icon\" />"
        },
        {
          "title": "Dark (Default)",
          "description": "For light backgrounds:",
          "code": "<div class=\"bg-white p-4\">\n  <LogoMain color=\"dark\" />\n</div>"
        },
        {
          "title": "Light",
          "description": "For dark backgrounds:",
          "code": "<div class=\"bg-gray-900 p-4\">\n  <LogoMain color=\"light\" />\n</div>"
        },
        {
          "title": "Mono Dark",
          "description": "Navy monochrome for light backgrounds:",
          "code": "<LogoMain color=\"mono-dark\" />"
        },
        {
          "title": "Mono Light",
          "description": "White monochrome for dark backgrounds:",
          "code": "<div class=\"bg-primary p-4\">\n  <LogoMain color=\"mono-light\" />\n</div>"
        },
        {
          "title": "Header Logo",
          "code": "<header class=\"flex items-center h-16 px-4 border-b\">\n  <a href=\"/\">\n    <LogoMain variant=\"horizontal\" size=\"md\" ariaLabel=\"\" />\n  </a>\n</header>"
        },
        {
          "title": "Footer Logo",
          "code": "<footer class=\"bg-gray-900 py-8\">\n  <LogoMain color=\"light\" size=\"sm\" />\n</footer>"
        },
        {
          "title": "Login Page Logo",
          "code": "<div class=\"flex flex-col items-center py-8\">\n  <LogoMain variant=\"stacked\" size=\"lg\" subtitle=\"MY HOME\" />\n  <h1 class=\"mt-4 text-2xl font-bold\">Sign In</h1>\n</div>"
        },
        {
          "title": "Sidebar Collapsed Logo",
          "code": "<LogoMain variant={sidebarCollapsed ? 'icon' : 'horizontal'} size=\"md\" />"
        },
        {
          "title": "Development Environment",
          "code": "<LogoMain environment={import.meta.env.MODE === 'production' ? undefined : 'dev'} />"
        }
      ],
      "relatedComponents": [
        "LoadingLogo",
        "Header",
        "Sidebar"
      ]
    },
    {
      "name": "MultiSelect",
      "description": "Select multiple options from a dropdown list",
      "category": "form",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "value",
          "type": "string[]",
          "default": "[]",
          "required": false,
          "description": "Selected values (bindable)"
        },
        {
          "name": "options",
          "type": "MultiSelectOption[]",
          "default": "Required",
          "required": false,
          "description": "Array of options"
        },
        {
          "name": "placeholder",
          "type": "string",
          "default": "'Select options...'",
          "required": false,
          "description": "Placeholder text"
        },
        {
          "name": "emptyMessage",
          "type": "string",
          "default": "'No results found.'",
          "required": false,
          "description": "Message when no options match"
        },
        {
          "name": "disabled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disables the select"
        },
        {
          "name": "required",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Marks as required"
        },
        {
          "name": "name",
          "type": "string",
          "required": false,
          "description": "Name attribute for forms"
        },
        {
          "name": "id",
          "type": "string",
          "required": false,
          "description": "Element ID (auto-generated if empty)"
        },
        {
          "name": "label",
          "type": "string",
          "required": false,
          "description": "Label text"
        },
        {
          "name": "hint",
          "type": "string",
          "required": false,
          "description": "Hint text below the input"
        },
        {
          "name": "error",
          "type": "string",
          "required": false,
          "description": "Error message"
        },
        {
          "name": "max",
          "type": "number",
          "required": false,
          "description": "Maximum selections allowed"
        },
        {
          "name": "loading",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Shows loading state"
        },
        {
          "name": "debounceMs",
          "type": "number",
          "default": "300",
          "required": false,
          "description": "Debounce time for search"
        },
        {
          "name": "onValueChange",
          "type": "(value: string[]) => void",
          "required": false,
          "description": "Callback when value changes"
        },
        {
          "name": "onSearch",
          "type": "(query: string) => void",
          "required": false,
          "description": "Callback for search queries"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let selected = $state([]);\n\n  const options = [\n    { value: 'react', label: 'React' },\n    { value: 'svelte', label: 'Svelte' },\n    { value: 'vue', label: 'Vue' },\n    { value: 'angular', label: 'Angular' },\n  ];\n</script>\n\n<MultiSelect {options} bind:value={selected} placeholder=\"Select frameworks...\" />"
        },
        {
          "title": "Tag Selector",
          "code": "<script>\n  let tags = $state([]);\n  const tagOptions = [\n    { value: 'urgent', label: 'Urgent' },\n    { value: 'bug', label: 'Bug' },\n    { value: 'feature', label: 'Feature' },\n    { value: 'docs', label: 'Documentation' },\n  ];\n</script>\n\n<MultiSelect\n  label=\"Tags\"\n  hint=\"Add relevant tags\"\n  options={tagOptions}\n  bind:value={tags}\n  placeholder=\"Add tags...\"\n/>"
        },
        {
          "title": "Team Members",
          "code": "<MultiSelect\n  label=\"Assign to\"\n  hint=\"Select team members to assign this task\"\n  options={teamMembers}\n  bind:value={assignees}\n  max={5}\n  placeholder=\"Select team members...\"\n/>"
        }
      ],
      "relatedComponents": [
        "Select",
        "Combobox",
        "Checkbox"
      ]
    },
    {
      "name": "NoticeBanner",
      "description": "Banner component for displaying scheduled, dismissible announcements",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Maintenance Notice",
          "code": "<script>\n  const notices = [\n    {\n      id: 'maintenance',\n      title: 'Scheduled Maintenance',\n      message: 'The system will be unavailable on Sunday 2-4 AM EST.',\n      variant: 'warning',\n      location: 'banner',\n      dismissible: true,\n      startDate: '2025-01-15',\n      endDate: '2025-01-20',\n    },\n  ];\n\n  initializeNotices(notices);\n</script>\n\n<NoticeBanner />"
        },
        {
          "title": "With Link",
          "code": "<script>\n  const notices = [\n    {\n      id: 'new-feature',\n      message: 'Check out our new dashboard features!',\n      variant: 'info',\n      location: 'banner',\n      link: {\n        text: 'Learn more',\n        href: '/whats-new',\n      },\n    },\n  ];\n</script>\n\n<NoticeBanner />"
        },
        {
          "title": "Page-Specific Notice",
          "code": "<script>\n  const notices = [\n    {\n      id: 'billing-update',\n      message: 'Your payment method will expire soon.',\n      variant: 'warning',\n      location: 'banner',\n      pages: ['/settings', '/billing'],\n    },\n  ];\n\n  initializeNotices(notices);\n  setCurrentPath('/billing'); // Notice will show\n</script>\n\n<NoticeBanner />"
        },
        {
          "title": "In Layout",
          "code": "<!-- +layout.svelte -->\n<NoticeBanner />\n\n<main>\n  <slot />\n</main>"
        }
      ],
      "relatedComponents": [
        "NoticeCard",
        "Alert"
      ]
    },
    {
      "name": "NoticeCard",
      "description": "Card-style component for displaying inline scheduled announcements",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { NoticeCard, initializeNotices, setCurrentPath } from '@classic-homes/theme-svelte';\n\n  const notices = [\n    {\n      id: 'welcome',\n      title: 'Welcome!',\n      message: 'Thanks for signing up. Complete your profile to get started.',\n      variant: 'info',\n      location: 'card',\n      dismissible: true,\n    },\n  ];\n\n  initializeNotices(notices);\n  setCurrentPath('/dashboard');\n</script>\n\n<NoticeCard />"
        },
        {
          "title": "Welcome Message",
          "code": "<script>\n  const notices = [\n    {\n      id: 'onboarding',\n      title: 'Complete Your Profile',\n      message: 'Add a photo and bio to help others recognize you.',\n      variant: 'info',\n      location: 'card',\n      link: {\n        text: 'Edit profile',\n        href: '/settings/profile',\n      },\n    },\n  ];\n</script>\n\n<NoticeCard />"
        },
        {
          "title": "Feature Announcement",
          "code": "<script>\n  const notices = [\n    {\n      id: 'new-charts',\n      title: 'New Charts Available',\n      message: \"We've added 5 new chart types to your dashboard.\",\n      variant: 'success',\n      location: 'card',\n      startDate: '2025-01-01',\n      endDate: '2025-01-31',\n    },\n  ];\n</script>\n\n<NoticeCard />"
        },
        {
          "title": "Warning Notice",
          "code": "<script>\n  const notices = [\n    {\n      id: 'storage-limit',\n      message: \"You're approaching your storage limit. Upgrade to continue uploading.\",\n      variant: 'warning',\n      location: 'card',\n      pages: ['/dashboard', '/files'],\n      link: {\n        text: 'Upgrade plan',\n        href: '/billing/upgrade',\n      },\n    },\n  ];\n</script>\n\n<NoticeCard />"
        },
        {
          "title": "In Page Content",
          "code": "<div class=\"space-y-4\">\n  <NoticeCard />\n\n  <Card>\n    <CardHeader>\n      <CardTitle>Dashboard</CardTitle>\n    </CardHeader>\n    <CardContent>\n      <!-- Main content -->\n    </CardContent>\n  </Card>\n</div>"
        },
        {
          "title": "Custom Styling",
          "code": "<NoticeCard class=\"mb-6\" />"
        }
      ],
      "relatedComponents": [
        "NoticeBanner",
        "Alert"
      ]
    },
    {
      "name": "NotificationBadge",
      "description": "Unread notification count indicator for bell icons and buttons",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte/notifications",
      "props": [
        {
          "name": "count",
          "type": "number",
          "required": false,
          "description": "Number of unread notifications"
        },
        {
          "name": "max",
          "type": "number",
          "default": "99",
          "required": false,
          "description": "Maximum number to display (shows \"N+\" for more)"
        },
        {
          "name": "showZero",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Whether to show the badge when count is 0"
        },
        {
          "name": "size",
          "type": "'sm' | 'md' | 'lg'",
          "default": "'md'",
          "required": false,
          "description": "Size variant"
        },
        {
          "name": "children",
          "type": "Snippet",
          "required": false,
          "description": "Optional children (e.g., button or icon to wrap)"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "size",
          "values": [
            "sm",
            "md",
            "lg"
          ],
          "default": "md"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<NotificationBadge count={5} />"
        },
        {
          "title": "In a Header",
          "code": "<header class=\"flex items-center gap-4\">\n  <NotificationBadge count={unreadCount}>\n    {#snippet children()}\n      <button\n        class=\"p-2 rounded-full hover:bg-muted\"\n        onclick={openNotifications}\n        aria-label=\"View notifications\"\n      >\n        <Icon name=\"bell\" size={24} />\n      </button>\n    {/snippet}\n  </NotificationBadge>\n</header>"
        },
        {
          "title": "Dynamic Updates",
          "code": "<script>\n  import { notificationStore } from '@classic-homes/notifications/svelte';\n\n  const unreadCount = $derived(notificationStore.notifications.filter((n) => !n.read).length);\n</script>\n\n<NotificationBadge count={unreadCount} />"
        }
      ],
      "relatedComponents": [
        "NotificationList",
        "NotificationDialog",
        "Badge"
      ]
    },
    {
      "name": "NotificationBulkActions",
      "description": "Toolbar for selecting and performing bulk actions on notifications",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte/notifications",
      "props": [
        {
          "name": "selectedCount",
          "type": "number",
          "required": false,
          "description": "Number of currently selected notifications"
        },
        {
          "name": "totalCount",
          "type": "number",
          "required": false,
          "description": "Total number of notifications available"
        },
        {
          "name": "allSelected",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Whether all notifications are selected"
        },
        {
          "name": "onSelectAll",
          "type": "(selected: boolean) => void",
          "required": false,
          "description": "Callback to select/deselect all"
        },
        {
          "name": "onMarkRead",
          "type": "() => void",
          "required": false,
          "description": "Callback to mark selected as read"
        },
        {
          "name": "onMarkUnread",
          "type": "() => void",
          "required": false,
          "description": "Callback to mark selected as unread"
        },
        {
          "name": "onDelete",
          "type": "() => void",
          "required": false,
          "description": "Callback to delete selected"
        },
        {
          "name": "onClearSelection",
          "type": "() => void",
          "required": false,
          "description": "Callback to clear selection"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let selectedIds = $state(new Set<string>());\n  const notifications = [...];\n</script>\n\n<NotificationBulkActions\n  selectedCount={selectedIds.size}\n  totalCount={notifications.length}\n  allSelected={selectedIds.size === notifications.length}\n  onSelectAll={(selected) => {\n    /* ... */\n  }}\n  onMarkRead={() => {\n    /* ... */\n  }}\n  onMarkUnread={() => {\n    /* ... */\n  }}\n  onDelete={() => {\n    /* ... */\n  }}\n/>"
        },
        {
          "title": "No Selection",
          "description": "When no items are selected, only the \"Select all\" checkbox is shown:",
          "code": "<NotificationBulkActions selectedCount={0} totalCount={10} allSelected={false} />"
        },
        {
          "title": "Partial Selection",
          "description": "When some items are selected, action buttons appear:",
          "code": "<NotificationBulkActions selectedCount={3} totalCount={10} allSelected={false} />\n<!-- Shows: \"3 of 10 selected\" with Clear button and action buttons -->"
        },
        {
          "title": "All Selected",
          "description": "When all items are selected:",
          "code": "<NotificationBulkActions selectedCount={10} totalCount={10} allSelected={true} />"
        },
        {
          "title": "Select All",
          "description": "Toggle selection of all visible notifications:",
          "code": "<script>\n  function handleSelectAll(selected: boolean) {\n    if (selected) {\n      selectedIds = new Set(filteredNotifications().map(n => n.id));\n    } else {\n      selectedIds = new Set();\n    }\n  }\n</script>\n\n<NotificationBulkActions\n  onSelectAll={handleSelectAll}\n  {...}\n/>"
        },
        {
          "title": "Mark as Read",
          "description": "Mark all selected notifications as read:",
          "code": "<script>\n  function handleBulkMarkRead() {\n    const count = selectedIds.size;\n    notifications = notifications.map(n =>\n      selectedIds.has(n.id)\n        ? { ...n, read: true, readAt: new Date().toISOString() }\n        : n\n    );\n    selectedIds = new Set();\n    toastStore.success(`Marked ${count} notifications as read`);\n  }\n</script>\n\n<NotificationBulkActions\n  onMarkRead={handleBulkMarkRead}\n  {...}\n/>"
        },
        {
          "title": "Mark as Unread",
          "description": "Mark all selected notifications as unread:",
          "code": "<script>\n  function handleBulkMarkUnread() {\n    const count = selectedIds.size;\n    notifications = notifications.map(n =>\n      selectedIds.has(n.id)\n        ? { ...n, read: false, readAt: undefined }\n        : n\n    );\n    selectedIds = new Set();\n    toastStore.info(`Marked ${count} notifications as unread`);\n  }\n</script>\n\n<NotificationBulkActions\n  onMarkUnread={handleBulkMarkUnread}\n  {...}\n/>"
        },
        {
          "title": "Delete",
          "description": "Delete all selected notifications:",
          "code": "<script>\n  function handleBulkDelete() {\n    const count = selectedIds.size;\n    notifications = notifications.filter(n => !selectedIds.has(n.id));\n    selectedIds = new Set();\n    toastStore.info(`Deleted ${count} notifications`);\n  }\n</script>\n\n<NotificationBulkActions\n  onDelete={handleBulkDelete}\n  {...}\n/>"
        },
        {
          "title": "Complete Implementation",
          "code": "<script>\n  import { toastStore } from '@classic-homes/theme-svelte';\n  import {\n    NotificationList,\n    NotificationFilters,\n    NotificationBulkActions,\n  } from '@classic-homes/theme-svelte/notifications';\n  import type { Notification, NotificationFilter, NotificationCounts } from '@classic-homes/notifications/core';\n\n  let notifications = $state<Notification[]>([...]);\n  let filter = $state<NotificationFilter>('all');\n  let selectedIds = $state(new Set<string>());\n\n  const filteredNotifications = $derived(() => {\n    switch (filter) {\n      case 'unread': return notifications.filter(n => !n.read);\n      case 'read': return notifications.filter(n => n.read);\n      default: return notifications;\n    }\n  });\n\n  const counts = $derived<NotificationCounts>({\n    all: notifications.length,\n    unread: notifications.filter(n => !n.read).length,\n    read: notifications.filter(n => n.read).length,\n  });\n\n  const allSelected = $derived(\n    filteredNotifications().length > 0 &&\n    filteredNotifications().every(n => selectedIds.has(n.id))\n  );\n\n  function handleToggleSelect(id: string, selected: boolean) {\n    const newSet = new Set(selectedIds);\n    if (selected) newSet.add(id);\n    else newSet.delete(id);\n    selectedIds = newSet;\n  }\n\n  function handleSelectAll(selected: boolean) {\n    if (selected) {\n      selectedIds = new Set(filteredNotifications().map(n => n.id));\n    } else {\n      selectedIds = new Set();\n    }\n  }\n\n  function handleClearSelection() {\n    selectedIds = new Set();\n  }\n\n  function handleBulkMarkRead() {\n    notifications = notifications.map(n =>\n      selectedIds.has(n.id)\n        ? { ...n, read: true, readAt: new Date().toISOString() }\n        : n\n    );\n    selectedIds = new Set();\n    toastStore.success('Marked as read');\n  }\n\n  function handleBulkMarkUnread() {\n    notifications = notifications.map(n =>\n      selectedIds.has(n.id)\n        ? { ...n, read: false, readAt: undefined }\n        : n\n    );\n    selectedIds = new Set();\n    toastStore.info('Marked as unread');\n  }\n\n  function handleBulkDelete() {\n    notifications = notifications.filter(n => !selectedIds.has(n.id));\n    selectedIds = new Set();\n    toastStore.info('Deleted');\n  }\n</script>\n\n<NotificationFilters bind:filter {counts} onFilterChange={() => (selectedIds = new Set())} />\n\n{#if filteredNotifications().length > 0}\n  <NotificationBulkActions\n    selectedCount={selectedIds.size}\n    totalCount={filteredNotifications().length}\n    {allSelected}\n    onSelectAll={handleSelectAll}\n    onMarkRead={handleBulkMarkRead}\n    onMarkUnread={handleBulkMarkUnread}\n    onDelete={handleBulkDelete}\n    onClearSelection={handleClearSelection}\n  />\n{/if}\n\n<NotificationList\n  notifications={filteredNotifications()}\n  {selectedIds}\n  selectable={true}\n  onToggleSelect={handleToggleSelect}\n/>"
        }
      ],
      "relatedComponents": [
        "NotificationList",
        "NotificationFilters",
        "NotificationItem"
      ]
    },
    {
      "name": "NotificationDialog",
      "description": "Modal dialog for viewing full notification details with actions",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte/notifications",
      "props": [
        {
          "name": "notification",
          "type": "Notification | null",
          "required": false,
          "description": "The notification to display"
        },
        {
          "name": "open",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Whether the dialog is open (bindable)"
        },
        {
          "name": "onClose",
          "type": "() => void",
          "required": false,
          "description": "Callback when the dialog should close"
        },
        {
          "name": "onMarkRead",
          "type": "(id: string) => void",
          "required": false,
          "description": "Callback to mark notification as read"
        },
        {
          "name": "onMarkUnread",
          "type": "(id: string) => void",
          "required": false,
          "description": "Callback to mark as unread"
        },
        {
          "name": "onDelete",
          "type": "(id: string) => void",
          "required": false,
          "description": "Callback to delete notification"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import type { Notification } from '@classic-homes/notifications/core';\n\n  let notification = $state<Notification | null>(null);\n  let open = $state(false);\n</script>\n\n<NotificationDialog {notification} bind:open />"
        },
        {
          "title": "Mark as Read/Unread",
          "description": "The dialog shows contextual buttons based on read status:",
          "code": "<NotificationDialog\n  {notification}\n  bind:open\n  onMarkRead={(id) => {\n    notifications = notifications.map((n) =>\n      n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n\n    );\n  }}\n  onMarkUnread={(id) => {\n    notifications = notifications.map((n) =>\n      n.id === id ? { ...n, read: false, readAt: undefined } : n\n    );\n  }}\n/>"
        },
        {
          "title": "Delete",
          "code": "<NotificationDialog\n  {notification}\n  bind:open\n  onDelete={(id) => {\n    notifications = notifications.filter((n) => n.id !== id);\n    open = false;\n  }}\n/>"
        },
        {
          "title": "Complete Implementation",
          "code": "<script>\n  import { toastStore } from '@classic-homes/theme-svelte';\n  import { NotificationList, NotificationDialog } from '@classic-homes/theme-svelte/notifications';\n  import type { Notification } from '@classic-homes/notifications/core';\n\n  let notifications = $state<Notification[]>([...]);\n  let selectedNotification = $state<Notification | null>(null);\n  let dialogOpen = $state(false);\n\n  function handleNotificationClick(notification: Notification) {\n    selectedNotification = notification;\n    dialogOpen = true;\n  }\n\n  function handleDialogClose() {\n    dialogOpen = false;\n    selectedNotification = null;\n  }\n\n  function markAsRead(id: string) {\n    notifications = notifications.map(n =>\n      n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n\n    );\n    toastStore.success('Notification marked as read');\n  }\n\n  function markAsUnread(id: string) {\n    notifications = notifications.map(n =>\n      n.id === id ? { ...n, read: false, readAt: undefined } : n\n    );\n    toastStore.info('Notification marked as unread');\n  }\n\n  function deleteNotification(id: string) {\n    notifications = notifications.filter(n => n.id !== id);\n    handleDialogClose();\n    toastStore.info('Notification deleted');\n  }\n</script>\n\n<NotificationList {notifications} onNotificationClick={handleNotificationClick} />\n\n<NotificationDialog\n  notification={selectedNotification}\n  bind:open={dialogOpen}\n  onClose={handleDialogClose}\n  onMarkRead={markAsRead}\n  onMarkUnread={markAsUnread}\n  onDelete={deleteNotification}\n/>"
        },
        {
          "title": "With Action Links",
          "description": "Notifications with action URLs display a primary action button:",
          "code": "<!-- Notification with actionUrl and actionLabel -->\n<NotificationDialog\n  notification={{\n    ...notification,\n    actionUrl: '/settings/security',\n    actionLabel: 'Review Activity',\n  }}\n  bind:open\n/>"
        }
      ],
      "relatedComponents": [
        "NotificationList",
        "NotificationItem",
        "Dialog"
      ]
    },
    {
      "name": "NotificationFilters",
      "description": "Filter tabs for notification lists with counts",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte/notifications",
      "props": [
        {
          "name": "filter",
          "type": "NotificationFilter",
          "default": "'all'",
          "required": false,
          "description": "Current active filter (bindable)"
        },
        {
          "name": "counts",
          "type": "NotificationCounts",
          "required": false,
          "description": "Counts for each filter tab"
        },
        {
          "name": "onFilterChange",
          "type": "(filter: NotificationFilter) => void",
          "required": false,
          "description": "Callback when filter changes"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import type { NotificationFilter, NotificationCounts } from '@classic-homes/notifications/core';\n\n  let filter = $state<NotificationFilter>('all');\n  const counts: NotificationCounts = {\n    all: 10,\n    unread: 3,\n    read: 7,\n  };\n</script>\n\n<NotificationFilters bind:filter {counts} />"
        },
        {
          "title": "With Notification List",
          "code": "<script>\n  import {\n    NotificationFilters,\n    NotificationList,\n  } from '@classic-homes/theme-svelte/notifications';\n  import type { Notification, NotificationFilter, NotificationCounts } from '@classic-homes/notifications/core';\n\n  let notifications = $state<Notification[]>([...]);\n  let filter = $state<NotificationFilter>('all');\n\n  const counts = $derived<NotificationCounts>({\n    all: notifications.length,\n    unread: notifications.filter(n => !n.read).length,\n    read: notifications.filter(n => n.read).length,\n  });\n\n  const filteredNotifications = $derived(() => {\n    switch (filter) {\n      case 'unread': return notifications.filter(n => !n.read);\n      case 'read': return notifications.filter(n => n.read);\n      default: return notifications;\n    }\n  });\n</script>\n\n<NotificationFilters bind:filter {counts} />\n\n<NotificationList\n  notifications={filteredNotifications()}\n  emptyTitle={filter === 'unread'\n    ? 'No unread notifications'\n    : filter === 'read'\n      ? 'No read notifications'\n      : 'No notifications'}\n/>"
        },
        {
          "title": "Reset Selection on Filter Change",
          "code": "<script>\n  let selectedIds = $state(new Set<string>());\n\n  function handleFilterChange(newFilter: NotificationFilter) {\n    filter = newFilter;\n    selectedIds = new Set(); // Clear selection\n  }\n</script>\n\n<NotificationFilters\n  bind:filter\n  {counts}\n  onFilterChange={handleFilterChange}\n/>\n\n<NotificationBulkActions\n  selectedCount={selectedIds.size}\n  totalCount={filteredNotifications().length}\n  {...}\n/>"
        }
      ],
      "relatedComponents": [
        "NotificationList",
        "NotificationBulkActions",
        "Tabs"
      ]
    },
    {
      "name": "NotificationItem",
      "description": "Single notification card with icon, title, message, and actions",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte/notifications",
      "props": [
        {
          "name": "notification",
          "type": "Notification",
          "required": false,
          "description": "The notification object to display"
        },
        {
          "name": "isSelected",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Whether this notification is selected"
        },
        {
          "name": "selectable",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Whether selection mode is enabled"
        },
        {
          "name": "onToggleSelect",
          "type": "(id: string, selected: boolean) => void",
          "required": false,
          "description": "Callback when selection state changes"
        },
        {
          "name": "onClick",
          "type": "(notification: Notification) => void",
          "required": false,
          "description": "Callback when notification is clicked"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import type { Notification } from '@classic-homes/notifications/core';\n\n  const notification: Notification = {\n    id: '1',\n    userId: 'user-1',\n    type: 'info',\n    category: 'general',\n    title: 'Welcome!',\n    message: 'Thanks for signing up.',\n    priority: 'normal',\n    read: false,\n    dismissed: false,\n    createdAt: new Date().toISOString(),\n  };\n</script>\n\n<NotificationItem {notification} />"
        },
        {
          "title": "Unread",
          "description": "Unread notifications have a light blue background and blue border:",
          "code": "<NotificationItem notification={{ ...notification, read: false }} />"
        },
        {
          "title": "Read",
          "description": "Read notifications have a neutral card background:",
          "code": "<NotificationItem notification={{ ...notification, read: true }} />"
        },
        {
          "title": "Priority Badges",
          "description": "Non-normal priorities display a badge:",
          "code": "<!-- Shows \"urgent\" badge -->\n<NotificationItem notification={{ ...notification, priority: 'urgent' }} />\n\n<!-- Shows \"high\" badge -->\n<NotificationItem notification={{ ...notification, priority: 'high' }} />"
        },
        {
          "title": "In a List",
          "code": "{#each notifications as notification (notification.id)}\n  <NotificationItem\n    {notification}\n    selectable={true}\n    isSelected={selectedIds.has(notification.id)}\n    onToggleSelect={handleToggleSelect}\n    onClick={handleNotificationClick}\n  />\n{/each}"
        }
      ],
      "relatedComponents": [
        "NotificationList",
        "NotificationDialog",
        "NotificationBadge"
      ]
    },
    {
      "name": "NotificationList",
      "description": "Container for displaying a list of notifications with empty states and loading skeletons",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte/notifications",
      "props": [
        {
          "name": "notifications",
          "type": "Notification[]",
          "required": false,
          "description": "List of notifications to display"
        },
        {
          "name": "selectedIds",
          "type": "Set<string>",
          "default": "new Set()",
          "required": false,
          "description": "Set of selected notification IDs"
        },
        {
          "name": "selectable",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Whether selection mode is enabled"
        },
        {
          "name": "onToggleSelect",
          "type": "(id: string, selected: boolean) => void",
          "required": false,
          "description": "Callback when a notification's selection changes"
        },
        {
          "name": "onNotificationClick",
          "type": "(notification: Notification) => void",
          "required": false,
          "description": "Callback when a notification is clicked"
        },
        {
          "name": "emptyState",
          "type": "Snippet",
          "required": false,
          "description": "Custom empty state snippet"
        },
        {
          "name": "emptyTitle",
          "type": "string",
          "default": "'No notifications'",
          "required": false,
          "description": "Empty state title"
        },
        {
          "name": "emptyDescription",
          "type": "string",
          "default": "\"You're all caught up!\"",
          "required": false,
          "description": "Empty state description"
        },
        {
          "name": "loading",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Whether the list is loading"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import type { Notification } from '@classic-homes/notifications/core';\n\n  let notifications = $state<Notification[]>([\n    // ... notification objects\n  ]);\n</script>\n\n<NotificationList {notifications} />"
        },
        {
          "title": "Custom Empty State",
          "description": "Provide a completely custom empty state:",
          "code": "<NotificationList notifications={[]}>\n  {#snippet emptyState()}\n    <div class=\"text-center py-12\">\n      <img src=\"/empty-inbox.svg\" alt=\"\" class=\"mx-auto mb-4\" />\n      <h3 class=\"text-lg font-medium\">Inbox Zero!</h3>\n      <p class=\"text-muted-foreground\">You have no notifications</p>\n    </div>\n  {/snippet}\n</NotificationList>"
        },
        {
          "title": "Complete Notification Center",
          "code": "<script>\n  import {\n    NotificationList,\n    NotificationFilters,\n    NotificationBulkActions,\n    NotificationDialog,\n  } from '@classic-homes/theme-svelte/notifications';\n  import type { Notification, NotificationFilter, NotificationCounts } from '@classic-homes/notifications/core';\n\n  let notifications = $state<Notification[]>([...]);\n  let filter = $state<NotificationFilter>('all');\n  let selectedIds = $state(new Set<string>());\n  let dialogOpen = $state(false);\n  let selectedNotification = $state<Notification | null>(null);\n\n  const filteredNotifications = $derived(() => {\n    switch (filter) {\n      case 'unread': return notifications.filter(n => !n.read);\n      case 'read': return notifications.filter(n => n.read);\n      default: return notifications;\n    }\n  });\n\n  const counts: NotificationCounts = $derived({\n    all: notifications.length,\n    unread: notifications.filter(n => !n.read).length,\n    read: notifications.filter(n => n.read).length,\n  });\n</script>\n\n<NotificationFilters bind:filter {counts} />\n\n<NotificationBulkActions\n  selectedCount={selectedIds.size}\n  totalCount={filteredNotifications().length}\n  onSelectAll={(selected) => {\n    /* ... */\n  }}\n  onMarkRead={() => {\n    /* ... */\n  }}\n  onDelete={() => {\n    /* ... */\n  }}\n/>\n\n<NotificationList\n  notifications={filteredNotifications()}\n  {selectedIds}\n  selectable={true}\n  onToggleSelect={handleToggleSelect}\n  onNotificationClick={handleNotificationClick}\n/>\n\n<NotificationDialog notification={selectedNotification} bind:open={dialogOpen} />"
        },
        {
          "title": "With Filter-Specific Empty States",
          "code": "<NotificationList\n  notifications={filteredNotifications()}\n  emptyTitle={filter === 'unread'\n    ? 'No unread notifications'\n    : filter === 'read'\n      ? 'No read notifications'\n      : 'No notifications'}\n  emptyDescription={filter === 'unread'\n    ? 'All caught up!'\n    : filter === 'read'\n      ? 'No notifications have been read yet'\n      : \"You're all caught up!\"}\n/>"
        }
      ],
      "relatedComponents": [
        "NotificationItem",
        "NotificationFilters",
        "NotificationBulkActions",
        "NotificationDialog"
      ]
    },
    {
      "name": "NumberInput",
      "description": "Numeric input with increment/decrement controls",
      "category": "form",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "value",
          "type": "number | null",
          "default": "null",
          "required": false,
          "description": "Current value (bindable)"
        },
        {
          "name": "min",
          "type": "number",
          "required": false,
          "description": "Minimum allowed value"
        },
        {
          "name": "max",
          "type": "number",
          "required": false,
          "description": "Maximum allowed value"
        },
        {
          "name": "step",
          "type": "number",
          "default": "1",
          "required": false,
          "description": "Increment/decrement step"
        },
        {
          "name": "disabled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disables the input"
        },
        {
          "name": "readonly",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Makes input read-only"
        },
        {
          "name": "required",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Marks field as required"
        },
        {
          "name": "placeholder",
          "type": "string",
          "required": false,
          "description": "Placeholder text"
        },
        {
          "name": "name",
          "type": "string",
          "required": false,
          "description": "Name attribute for form submission"
        },
        {
          "name": "id",
          "type": "string",
          "required": false,
          "description": "Element ID (auto-generated if empty)"
        },
        {
          "name": "showStepper",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show +/- stepper buttons"
        },
        {
          "name": "label",
          "type": "string",
          "required": false,
          "description": "Label text"
        },
        {
          "name": "hint",
          "type": "string",
          "required": false,
          "description": "Hint text below the input"
        },
        {
          "name": "error",
          "type": "string",
          "required": false,
          "description": "Error message"
        },
        {
          "name": "onValueChange",
          "type": "(value: number | null) => void",
          "required": false,
          "description": "Callback when value changes"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let quantity = $state(1);\n</script>\n\n<NumberInput bind:value={quantity} />"
        },
        {
          "title": "Quantity Selector",
          "code": "<NumberInput label=\"Quantity\" bind:value={quantity} min={1} max={10} />"
        },
        {
          "title": "Price Input",
          "code": "<NumberInput label=\"Price\" bind:value={price} min={0} step={0.01} />"
        },
        {
          "title": "Age Input",
          "code": "<NumberInput label=\"Age\" bind:value={age} min={0} max={150} />"
        }
      ],
      "relatedComponents": [
        "Slider",
        "Input"
      ]
    },
    {
      "name": "OTPInput",
      "description": "One-time password input for verification codes",
      "category": "form",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "value",
          "type": "string",
          "default": "''",
          "required": false,
          "description": "Current value (bindable)"
        },
        {
          "name": "length",
          "type": "number",
          "default": "6",
          "required": false,
          "description": "Number of digits"
        },
        {
          "name": "disabled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disables the input"
        },
        {
          "name": "required",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Marks field as required"
        },
        {
          "name": "type",
          "type": "'numeric' | 'alphanumeric'",
          "default": "'numeric'",
          "required": false,
          "description": "Input type"
        },
        {
          "name": "name",
          "type": "string",
          "required": false,
          "description": "Name attribute for form submission"
        },
        {
          "name": "id",
          "type": "string",
          "required": false,
          "description": "Element ID (auto-generated if empty)"
        },
        {
          "name": "label",
          "type": "string",
          "required": false,
          "description": "Label text"
        },
        {
          "name": "hint",
          "type": "string",
          "required": false,
          "description": "Hint text below the input"
        },
        {
          "name": "error",
          "type": "string",
          "required": false,
          "description": "Error message"
        },
        {
          "name": "onComplete",
          "type": "(value: string) => void",
          "required": false,
          "description": "Callback when all digits entered"
        },
        {
          "name": "onValueChange",
          "type": "(value: string) => void",
          "required": false,
          "description": "Callback when value changes"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "type",
          "values": [
            "numeric",
            "alphanumeric"
          ],
          "default": "numeric"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let code = $state('');\n</script>\n\n<OTPInput bind:value={code} />"
        },
        {
          "title": "Verification Flow",
          "code": "<script>\n  let code = $state('');\n  let error = $state('');\n  let verifying = $state(false);\n\n  async function handleComplete(value: string) {\n    verifying = true;\n    error = '';\n\n    try {\n      await verifyOTP(value);\n      // Success - redirect\n    } catch {\n      error = 'Invalid verification code';\n      code = '';\n    } finally {\n      verifying = false;\n    }\n  }\n</script>\n\n<div class=\"text-center space-y-4\">\n  <OTPInput\n    label=\"Verification Code\"\n    hint=\"Enter the 6-digit code sent to your email\"\n    bind:value={code}\n    {error}\n    onComplete={handleComplete}\n  />\n  {#if verifying}\n    <Spinner label=\"Verifying...\" />\n  {/if}\n</div>"
        },
        {
          "title": "Two-Factor Authentication",
          "code": "<OTPInput\n  label=\"Authentication Code\"\n  hint=\"Enter code from your authenticator app\"\n  bind:value={twoFactorCode}\n  length={6}\n  onComplete={verify2FA}\n/>"
        }
      ],
      "relatedComponents": [
        "Input",
        "FormField"
      ]
    },
    {
      "name": "PageHeader",
      "description": "Hero and page title component with multiple variants",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "title",
          "type": "string",
          "required": false,
          "description": "Main title text"
        },
        {
          "name": "subtitle",
          "type": "string",
          "required": false,
          "description": "Optional subtitle"
        },
        {
          "name": "variant",
          "type": "'default' | 'hero' | 'centered' | 'form'",
          "default": "'default'",
          "required": false,
          "description": "Visual variant"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional classes"
        },
        {
          "name": "actions",
          "type": "Snippet",
          "required": false,
          "description": "Action buttons"
        }
      ],
      "variants": [
        {
          "name": "variant",
          "values": [
            "default",
            "hero",
            "centered",
            "form"
          ],
          "default": "default"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<PageHeader title=\"Welcome\" subtitle=\"Get started with our platform\" />"
        },
        {
          "title": "Default",
          "description": "Standard page header:",
          "code": "<PageHeader title=\"Dashboard\" subtitle=\"Welcome back, John\" />"
        },
        {
          "title": "Hero",
          "description": "Bold, uppercase styling for landing pages:",
          "code": "<PageHeader\n  title=\"Build Something Great\"\n  subtitle=\"The modern way to create applications\"\n  variant=\"hero\"\n/>"
        },
        {
          "title": "Centered",
          "description": "Centered text for content pages:",
          "code": "<PageHeader\n  title=\"About Us\"\n  subtitle=\"Learn more about our mission and values\"\n  variant=\"centered\"\n/>"
        },
        {
          "title": "Form",
          "description": "Light, centered styling for form pages:",
          "code": "<PageHeader title=\"Create Account\" subtitle=\"Join thousands of users today\" variant=\"form\" />"
        },
        {
          "title": "Landing Page Hero",
          "code": "<section class=\"py-24 bg-gradient-to-b from-primary/5 to-background\">\n  <div class=\"container mx-auto px-4\">\n    <PageHeader\n      title=\"Ship Faster\"\n      subtitle=\"The complete toolkit for modern web development\"\n      variant=\"hero\"\n    >\n      {#snippet actions()}\n        <Button size=\"lg\">Get Started Free</Button>\n        <Button size=\"lg\" variant=\"outline\">View Demo</Button>\n      {/snippet}\n    </PageHeader>\n  </div>\n</section>"
        },
        {
          "title": "Dashboard Page",
          "code": "<PageHeader title=\"Analytics\" subtitle=\"Track your performance metrics\">\n  {#snippet actions()}\n    <Button variant=\"outline\">Export</Button>\n    <Button>Add Widget</Button>\n  {/snippet}\n</PageHeader>"
        },
        {
          "title": "Content Page",
          "code": "<div class=\"py-16\">\n  <PageHeader\n    title=\"Documentation\"\n    subtitle=\"Everything you need to build amazing applications with our component library.\"\n    variant=\"centered\"\n  >\n    {#snippet actions()}\n      <Button>Get Started</Button>\n      <Button variant=\"outline\">Browse Components</Button>\n    {/snippet}\n  </PageHeader>\n</div>"
        },
        {
          "title": "Form Page",
          "code": "<PageHeader\n  title=\"Contact Us\"\n  subtitle=\"Fill out the form below and we'll get back to you within 24 hours.\"\n  variant=\"form\"\n/>\n\n<form class=\"max-w-md mx-auto mt-8\">\n  <!-- Form fields -->\n</form>"
        },
        {
          "title": "Settings Page",
          "code": "<PageHeader\n  title=\"Account Settings\"\n  subtitle=\"Manage your account preferences and security settings\"\n>\n  {#snippet actions()}\n    <Button variant=\"outline\">Cancel</Button>\n    <Button>Save Changes</Button>\n  {/snippet}\n</PageHeader>\n\n<Separator class=\"my-6\" />\n\n<div class=\"space-y-6\">\n  <Section title=\"Profile\">...</Section>\n  <Section title=\"Security\">...</Section>\n</div>"
        },
        {
          "title": "Blog Post",
          "code": "<article>\n  <PageHeader\n    title=\"Getting Started with Svelte 5\"\n    subtitle=\"Learn the fundamentals of Svelte's new runes system\"\n    variant=\"centered\"\n  />\n\n  <div class=\"prose mx-auto mt-8\">\n    <!-- Article content -->\n  </div>\n</article>"
        }
      ],
      "relatedComponents": [
        "Section",
        "Button",
        "Separator"
      ]
    },
    {
      "name": "PieChart",
      "description": "Show proportional data with interactive labels",
      "category": "chart",
      "importPath": "@classic-homes/charts-svelte",
      "props": [
        {
          "name": "data",
          "type": "PieSliceData[]",
          "required": true,
          "description": "Array of slice data with name and value"
        },
        {
          "name": "showLabels",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Show labels on slices"
        },
        {
          "name": "labelPosition",
          "type": "'inside' | 'outside'",
          "default": "'outside'",
          "required": false,
          "description": "Position of slice labels"
        },
        {
          "name": "innerRadius",
          "type": "number | string",
          "default": "0",
          "required": false,
          "description": "Inner radius (0 for solid pie)"
        },
        {
          "name": "onClick",
          "type": "(params: SliceEventParams) => void",
          "required": false,
          "description": "Callback when a slice is clicked"
        },
        {
          "name": "title",
          "type": "string",
          "required": true,
          "description": "Chart title for accessibility"
        },
        {
          "name": "height",
          "type": "number | string",
          "default": "400",
          "required": false,
          "description": "Chart height"
        },
        {
          "name": "theme",
          "type": "'light' | 'dark' | 'auto'",
          "default": "'auto'",
          "required": false,
          "description": "Theme preference"
        },
        {
          "name": "showLegend",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show chart legend"
        },
        {
          "name": "showTooltip",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show tooltip on hover"
        }
      ],
      "variants": [
        {
          "name": "labelPosition",
          "values": [
            "inside",
            "outside"
          ],
          "default": "outside"
        },
        {
          "name": "theme",
          "values": [
            "light",
            "dark",
            "auto"
          ],
          "default": "auto"
        }
      ],
      "slots": [],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { PieChart } from '@classic-homes/charts-svelte';\n\n  const data = [\n    { name: 'Search Engine', value: 1048 },\n    { name: 'Direct', value: 735 },\n    { name: 'Email', value: 580 },\n    { name: 'Social Media', value: 484 },\n  ];\n</script>\n\n<PieChart title=\"Traffic Sources\" {data} showLabels height={400} />"
        },
        {
          "title": "With Outside Labels",
          "code": "<PieChart title=\"Market Share\" {data} showLabels labelPosition=\"outside\" height={400} />"
        },
        {
          "title": "With Inside Labels",
          "code": "<PieChart title=\"Market Share\" {data} showLabels labelPosition=\"inside\" height={400} />"
        },
        {
          "title": "Handling Click Events",
          "code": "<script>\n  function handleClick(params) {\n    console.log('Clicked slice:', params.name, params.value);\n  }\n</script>\n\n<PieChart title=\"Interactive Pie\" {data} onClick={handleClick} />"
        }
      ],
      "relatedComponents": [
        "DonutChart",
        "FunnelChart"
      ]
    },
    {
      "name": "ProgressBar",
      "description": "Determinate and indeterminate progress indicator bar",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "value",
          "type": "number | undefined",
          "default": "undefined",
          "required": false,
          "description": "Progress 0–100. Omit for indeterminate"
        },
        {
          "name": "max",
          "type": "number",
          "default": "100",
          "required": false,
          "description": "Maximum value"
        },
        {
          "name": "size",
          "type": "'sm' | 'md' | 'lg'",
          "default": "'md'",
          "required": false,
          "description": "Bar height"
        },
        {
          "name": "variant",
          "type": "'default' | 'success' | 'warning' | 'destructive'",
          "default": "'default'",
          "required": false,
          "description": "Fill color"
        },
        {
          "name": "label",
          "type": "string",
          "default": "'Progress'",
          "required": false,
          "description": "Accessible label (aria-label)"
        },
        {
          "name": "showValue",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Show percentage text next to the bar"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "size",
          "values": [
            "sm",
            "md",
            "lg"
          ],
          "default": "md"
        },
        {
          "name": "variant",
          "values": [
            "default",
            "success",
            "warning",
            "destructive"
          ],
          "default": "default"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<!-- Determinate -->\n<ProgressBar value={45} />\n\n<!-- Indeterminate -->\n<ProgressBar />"
        },
        {
          "title": "File Upload",
          "code": "<script>\n  let uploadProgress = $state(0);\n</script>\n\n<div class=\"space-y-2\">\n  <div class=\"flex justify-between text-sm\">\n    <span>Uploading document.pdf</span>\n    <span>{uploadProgress}%</span>\n  </div>\n  <ProgressBar value={uploadProgress} variant={uploadProgress >= 100 ? 'success' : 'default'} />\n</div>"
        },
        {
          "title": "Multi-Step Form",
          "code": "<script>\n  let currentStep = $state(2);\n  const totalSteps = 5;\n</script>\n\n<ProgressBar value={(currentStep / totalSteps) * 100} size=\"sm\" label=\"Form progress\" />\n<p class=\"text-sm text-muted-foreground mt-1\">\n  Step {currentStep} of {totalSteps}\n</p>"
        },
        {
          "title": "Storage Usage",
          "code": "<script>\n  const used = 7.2;\n  const total = 10;\n  const percentage = (used / total) * 100;\n</script>\n\n<ProgressBar\n  value={percentage}\n  variant={percentage > 90 ? 'destructive' : percentage > 75 ? 'warning' : 'default'}\n  showValue\n/>\n<p class=\"text-sm text-muted-foreground\">{used} GB of {total} GB used</p>"
        },
        {
          "title": "Loading with Indeterminate",
          "code": "<div class=\"space-y-2\">\n  <p class=\"text-sm text-muted-foreground\">Processing your request...</p>\n  <ProgressBar label=\"Processing\" />\n</div>"
        }
      ],
      "relatedComponents": [
        "Spinner",
        "Skeleton",
        "LoadingOverlay",
        "LoadingPage"
      ]
    },
    {
      "name": "PublicLayout",
      "description": "Layout for public-facing marketing and content pages",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "navigation",
          "type": "NavItem[]",
          "default": "[]",
          "required": false,
          "description": "Header navigation items"
        },
        {
          "name": "showFooter",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Whether to show footer"
        },
        {
          "name": "footerLinks",
          "type": "NavSection[]",
          "default": "[]",
          "required": false,
          "description": "Footer link sections"
        },
        {
          "name": "copyright",
          "type": "string",
          "required": false,
          "description": "Footer copyright text"
        },
        {
          "name": "logo",
          "type": "Snippet",
          "required": false,
          "description": "Custom logo"
        },
        {
          "name": "logoSubtitle",
          "type": "string",
          "required": false,
          "description": "Subtitle for default logo"
        },
        {
          "name": "logoEnvironment",
          "type": "'local' | 'dev' | 'demo'",
          "required": false,
          "description": "Environment indicator"
        },
        {
          "name": "headerEnd",
          "type": "Snippet",
          "required": false,
          "description": "Content at end of header"
        },
        {
          "name": "strongFooterBorder",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Strong accent border on footer"
        },
        {
          "name": "darkFooter",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Dark footer variant"
        },
        {
          "name": "headerSearch",
          "type": "HeaderSearchConfig",
          "required": false,
          "description": "Header search config"
        },
        {
          "name": "children",
          "type": "Snippet",
          "required": false,
          "description": "Main content"
        }
      ],
      "variants": [
        {
          "name": "logoEnvironment",
          "values": [
            "local",
            "dev",
            "demo"
          ]
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  const navigation = [\n    { id: 'home', name: 'Home', href: '/', active: true },\n    { id: 'about', name: 'About', href: '/about' },\n    { id: 'contact', name: 'Contact', href: '/contact' },\n  ];\n</script>\n\n<PublicLayout {navigation}>\n  <section class=\"py-20\">\n    <h1>Welcome</h1>\n  </section>\n</PublicLayout>"
        },
        {
          "title": "Landing Page",
          "code": "<PublicLayout {navigation} {footerLinks}>\n  <section class=\"py-24 text-center\">\n    <h1 class=\"text-5xl font-bold\">Build Better Products</h1>\n    <p class=\"mt-4 text-xl text-muted-foreground\">The modern way to build applications.</p>\n    <div class=\"mt-8 flex justify-center gap-4\">\n      <Button size=\"lg\">Get Started</Button>\n      <Button size=\"lg\" variant=\"outline\">Learn More</Button>\n    </div>\n  </section>\n</PublicLayout>"
        },
        {
          "title": "Documentation Site",
          "code": "<PublicLayout\n  {navigation}\n  showFooter={false}\n  headerSearch={{ enabled: true, placeholder: 'Search docs...' }}\n>\n  <div class=\"flex\">\n    <aside class=\"w-64\">Sidebar</aside>\n    <main class=\"flex-1\">Documentation content</main>\n  </div>\n</PublicLayout>"
        }
      ],
      "relatedComponents": [
        "Header",
        "Footer",
        "DashboardLayout"
      ]
    },
    {
      "name": "QuickLinks",
      "description": "Quick navigation links component for sidebars",
      "category": "navigation",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "links",
          "type": "QuickLink[]",
          "required": false,
          "description": "Array of quick link items (required)"
        },
        {
          "name": "display",
          "type": "'list' | 'icons'",
          "default": "'list'",
          "required": false,
          "description": "Display mode"
        },
        {
          "name": "variant",
          "type": "'light' | 'dark'",
          "default": "'light'",
          "required": false,
          "description": "Visual variant"
        },
        {
          "name": "icon",
          "type": "Snippet<[QuickLink]>",
          "required": false,
          "description": "Custom icon renderer"
        },
        {
          "name": "ariaLabel",
          "type": "string",
          "default": "'Quick links'",
          "required": false,
          "description": "Accessible navigation label"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "display",
          "values": [
            "list",
            "icons"
          ],
          "default": "list"
        },
        {
          "name": "variant",
          "values": [
            "light",
            "dark"
          ],
          "default": "light"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  const links: QuickLink[] = [\n    { id: 'docs', label: 'Documentation', href: '/docs', icon: 'book' },\n    { id: 'support', label: 'Support', href: '/support', icon: 'help' },\n    { id: 'github', label: 'GitHub', href: 'https://github.com', icon: 'github', external: true },\n  ];\n</script>\n\n<QuickLinks {links} />"
        },
        {
          "title": "List Mode (Default)",
          "description": "Shows icons with labels stacked vertically:",
          "code": "<QuickLinks {links} display=\"list\" />"
        },
        {
          "title": "Icons Mode",
          "description": "Shows only icons, centered:",
          "code": "<QuickLinks {links} display=\"icons\" />"
        },
        {
          "title": "Light (Default)",
          "description": "For light backgrounds:",
          "code": "<QuickLinks {links} variant=\"light\" />"
        },
        {
          "title": "Dark",
          "description": "For dark sidebars:",
          "code": "<QuickLinks {links} variant=\"dark\" />"
        },
        {
          "title": "With Badges",
          "code": "<script>\n  const links = [\n    { id: 'inbox', label: 'Inbox', href: '/inbox', icon: 'mail', badge: 5 },\n    { id: 'tasks', label: 'Tasks', href: '/tasks', icon: 'check', badge: '3 new' },\n  ];\n</script>\n\n<QuickLinks {links} />"
        },
        {
          "title": "External Links",
          "code": "<script>\n  const links = [{ id: 'docs', label: 'Docs', href: 'https://docs.example.com', external: true }];\n</script>\n\n<QuickLinks {links} />"
        },
        {
          "title": "Custom Icons",
          "code": "<QuickLinks {links}>\n  {#snippet icon(link)}\n    <Icon name={link.icon} class=\"h-5 w-5\" />\n  {/snippet}\n</QuickLinks>"
        },
        {
          "title": "In Sidebar",
          "code": "<Sidebar>\n  <!-- Main navigation -->\n  <div slot=\"footer\">\n    <QuickLinks {links} display=\"icons\" variant=\"dark\" />\n  </div>\n</Sidebar>"
        }
      ],
      "relatedComponents": [
        "Sidebar",
        "DashboardLayout"
      ]
    },
    {
      "name": "RadarChart",
      "description": "Multi-dimensional comparison across categories",
      "category": "chart",
      "importPath": "@classic-homes/charts-svelte",
      "props": [
        {
          "name": "data",
          "type": "RadarChartData",
          "required": true,
          "description": "Chart data with series values"
        },
        {
          "name": "indicators",
          "type": "RadarIndicator[]",
          "required": true,
          "description": "Dimension definitions"
        },
        {
          "name": "shape",
          "type": "'polygon' | 'circle'",
          "default": "'polygon'",
          "required": false,
          "description": "Shape of the radar grid"
        },
        {
          "name": "title",
          "type": "string",
          "required": true,
          "description": "Chart title for accessibility"
        },
        {
          "name": "height",
          "type": "number | string",
          "default": "400",
          "required": false,
          "description": "Chart height"
        },
        {
          "name": "theme",
          "type": "'light' | 'dark' | 'auto'",
          "default": "'auto'",
          "required": false,
          "description": "Theme preference"
        },
        {
          "name": "showLegend",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show chart legend"
        },
        {
          "name": "showTooltip",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show tooltip on hover"
        }
      ],
      "variants": [
        {
          "name": "shape",
          "values": [
            "polygon",
            "circle"
          ],
          "default": "polygon"
        },
        {
          "name": "theme",
          "values": [
            "light",
            "dark",
            "auto"
          ],
          "default": "auto"
        }
      ],
      "slots": [],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { RadarChart } from '@classic-homes/charts-svelte';\n\n  const indicators = [\n    { name: 'Sales', max: 6500 },\n    { name: 'Administration', max: 16000 },\n    { name: 'IT', max: 30000 },\n    { name: 'Support', max: 38000 },\n    { name: 'Development', max: 52000 },\n  ];\n\n  const data = {\n    series: [\n      { name: 'Budget', data: [4200, 3000, 20000, 35000, 50000] },\n      { name: 'Actual', data: [5000, 14000, 28000, 26000, 42000] },\n    ],\n  };\n</script>\n\n<RadarChart title=\"Budget vs Actual\" {data} {indicators} shape=\"polygon\" height={400} />"
        },
        {
          "title": "Circle Shape",
          "code": "<RadarChart title=\"Skill Assessment\" {data} {indicators} shape=\"circle\" />"
        },
        {
          "title": "Single Series",
          "code": "<RadarChart\n  title=\"Performance Metrics\"\n  data={{\n    series: [{ name: 'Score', data: [80, 90, 70, 85, 95] }],\n  }}\n  indicators={[\n    { name: 'Speed', max: 100 },\n    { name: 'Quality', max: 100 },\n    { name: 'Teamwork', max: 100 },\n    { name: 'Innovation', max: 100 },\n    { name: 'Communication', max: 100 },\n  ]}\n/>"
        }
      ],
      "relatedComponents": [
        "BarChart",
        "GaugeChart"
      ]
    },
    {
      "name": "RadioGroup",
      "description": "Radio button group for single selection from multiple options",
      "category": "form",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "value",
          "type": "string",
          "default": "''",
          "required": false,
          "description": "Current selected value (bindable)"
        },
        {
          "name": "options",
          "type": "RadioOption[]",
          "default": "Required",
          "required": false,
          "description": "Array of radio options"
        },
        {
          "name": "label",
          "type": "string",
          "required": false,
          "description": "Group label"
        },
        {
          "name": "hint",
          "type": "string",
          "required": false,
          "description": "Hint text below the radio group"
        },
        {
          "name": "orientation",
          "type": "'horizontal' | 'vertical'",
          "default": "'vertical'",
          "required": false,
          "description": "Layout direction"
        },
        {
          "name": "disabled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disables all options"
        },
        {
          "name": "required",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Marks as required"
        },
        {
          "name": "name",
          "type": "string",
          "required": false,
          "description": "Name attribute for forms (auto-generated if empty)"
        },
        {
          "name": "error",
          "type": "string",
          "required": false,
          "description": "Error message to display"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        },
        {
          "name": "onValueChange",
          "type": "(value: string) => void",
          "required": false,
          "description": "Callback when value changes"
        }
      ],
      "variants": [
        {
          "name": "orientation",
          "values": [
            "horizontal",
            "vertical"
          ],
          "default": "vertical"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let value = $state('');\n\n  const options = [\n    { value: 'option1', label: 'Option 1' },\n    { value: 'option2', label: 'Option 2' },\n    { value: 'option3', label: 'Option 3' },\n  ];\n</script>\n\n<RadioGroup {options} bind:value />"
        },
        {
          "title": "Vertical (Default)",
          "code": "<RadioGroup {options} bind:value orientation=\"vertical\" />"
        },
        {
          "title": "Horizontal",
          "code": "<RadioGroup {options} bind:value orientation=\"horizontal\" />"
        },
        {
          "title": "Shipping Method",
          "code": "<script>\n  let shipping = $state('');\n\n  const shippingOptions = [\n    {\n      value: 'standard',\n      label: 'Standard',\n      description: '5-7 business days - Free',\n    },\n    {\n      value: 'express',\n      label: 'Express',\n      description: '2-3 business days - $9.99',\n    },\n    {\n      value: 'overnight',\n      label: 'Overnight',\n      description: 'Next business day - $24.99',\n    },\n  ];\n</script>\n\n<RadioGroup\n  label=\"Shipping Method\"\n  hint=\"Free shipping on orders over $50\"\n  name=\"shipping\"\n  options={shippingOptions}\n  bind:value={shipping}\n  required\n/>"
        },
        {
          "title": "Payment Method",
          "code": "<RadioGroup\n  label=\"Payment Method\"\n  name=\"payment\"\n  options={[\n    { value: 'card', label: 'Credit Card' },\n    { value: 'paypal', label: 'PayPal' },\n    { value: 'bank', label: 'Bank Transfer' },\n  ]}\n  bind:value={payment}\n  orientation=\"horizontal\"\n/>"
        },
        {
          "title": "Yes/No Question",
          "code": "<RadioGroup\n  label=\"Would you recommend this product?\"\n  name=\"recommend\"\n  options={[\n    { value: 'yes', label: 'Yes' },\n    { value: 'no', label: 'No' },\n  ]}\n  bind:value={recommend}\n  orientation=\"horizontal\"\n/>"
        },
        {
          "title": "Survey Scale",
          "code": "<RadioGroup\n  label=\"How satisfied are you?\"\n  hint=\"1 = Very Dissatisfied, 5 = Very Satisfied\"\n  name=\"satisfaction\"\n  options={[\n    { value: '1', label: 'Very Dissatisfied' },\n    { value: '2', label: 'Dissatisfied' },\n    { value: '3', label: 'Neutral' },\n    { value: '4', label: 'Satisfied' },\n    { value: '5', label: 'Very Satisfied' },\n  ]}\n  bind:value={satisfaction}\n/>"
        }
      ],
      "relatedComponents": [
        "Checkbox",
        "Select",
        "Switch"
      ]
    },
    {
      "name": "SankeyChart",
      "description": "Flow relationships between entities",
      "category": "chart",
      "importPath": "@classic-homes/charts-svelte",
      "props": [
        {
          "name": "data",
          "type": "SankeyData",
          "required": true,
          "description": "Nodes and links defining the flow"
        },
        {
          "name": "orient",
          "type": "'horizontal' | 'vertical'",
          "default": "'horizontal'",
          "required": false,
          "description": "Flow direction"
        },
        {
          "name": "onClick",
          "type": "(params: SankeyEventParams) => void",
          "required": false,
          "description": "Callback when clicked"
        },
        {
          "name": "title",
          "type": "string",
          "required": true,
          "description": "Chart title for accessibility"
        },
        {
          "name": "height",
          "type": "number | string",
          "default": "400",
          "required": false,
          "description": "Chart height"
        },
        {
          "name": "theme",
          "type": "'light' | 'dark' | 'auto'",
          "default": "'auto'",
          "required": false,
          "description": "Theme preference"
        },
        {
          "name": "showTooltip",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show tooltip on hover"
        }
      ],
      "variants": [
        {
          "name": "orient",
          "values": [
            "horizontal",
            "vertical"
          ],
          "default": "horizontal"
        },
        {
          "name": "theme",
          "values": [
            "light",
            "dark",
            "auto"
          ],
          "default": "auto"
        }
      ],
      "slots": [],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { SankeyChart } from '@classic-homes/charts-svelte';\n\n  const data = {\n    nodes: [{ name: 'Source A' }, { name: 'Source B' }, { name: 'Process' }, { name: 'Output' }],\n    links: [\n      { source: 'Source A', target: 'Process', value: 50 },\n      { source: 'Source B', target: 'Process', value: 30 },\n      { source: 'Process', target: 'Output', value: 80 },\n    ],\n  };\n</script>\n\n<SankeyChart title=\"Energy Flow\" {data} orient=\"horizontal\" height={400} />"
        },
        {
          "title": "Website User Flow",
          "code": "<SankeyChart\n  title=\"User Journey\"\n  data={{\n    nodes: [\n      { name: 'Homepage' },\n      { name: 'Products' },\n      { name: 'Cart' },\n      { name: 'Checkout' },\n      { name: 'Purchase' },\n      { name: 'Exit' },\n    ],\n    links: [\n      { source: 'Homepage', target: 'Products', value: 1000 },\n      { source: 'Homepage', target: 'Exit', value: 300 },\n      { source: 'Products', target: 'Cart', value: 500 },\n      { source: 'Products', target: 'Exit', value: 200 },\n      { source: 'Cart', target: 'Checkout', value: 400 },\n      { source: 'Cart', target: 'Exit', value: 100 },\n      { source: 'Checkout', target: 'Purchase', value: 350 },\n      { source: 'Checkout', target: 'Exit', value: 50 },\n    ],\n  }}\n/>"
        },
        {
          "title": "Budget Allocation",
          "code": "<SankeyChart\n  title=\"Budget Distribution\"\n  data={{\n    nodes: [\n      { name: 'Revenue' },\n      { name: 'Operations' },\n      { name: 'Marketing' },\n      { name: 'R&D' },\n      { name: 'Profit' },\n    ],\n    links: [\n      { source: 'Revenue', target: 'Operations', value: 40 },\n      { source: 'Revenue', target: 'Marketing', value: 25 },\n      { source: 'Revenue', target: 'R&D', value: 20 },\n      { source: 'Revenue', target: 'Profit', value: 15 },\n    ],\n  }}\n/>"
        },
        {
          "title": "Vertical Orientation",
          "code": "<SankeyChart title=\"Process Flow\" {data} orient=\"vertical\" />"
        }
      ],
      "relatedComponents": [
        "FunnelChart",
        "TreemapChart"
      ]
    },
    {
      "name": "ScatterChart",
      "description": "Visualize X/Y relationships with optional trend lines",
      "category": "chart",
      "importPath": "@classic-homes/charts-svelte",
      "props": [
        {
          "name": "data",
          "type": "ScatterChartData",
          "required": true,
          "description": "Chart data with coordinate pairs"
        },
        {
          "name": "showTrendLine",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Display regression/trend line"
        },
        {
          "name": "symbolSize",
          "type": "number | ((value: number[]) => number)",
          "default": "10",
          "required": false,
          "description": "Point size (fixed or dynamic)"
        },
        {
          "name": "onClick",
          "type": "(params: DataPointEventParams) => void",
          "required": false,
          "description": "Callback when clicked"
        },
        {
          "name": "title",
          "type": "string",
          "required": true,
          "description": "Chart title for accessibility"
        },
        {
          "name": "height",
          "type": "number | string",
          "default": "400",
          "required": false,
          "description": "Chart height"
        },
        {
          "name": "theme",
          "type": "'light' | 'dark' | 'auto'",
          "default": "'auto'",
          "required": false,
          "description": "Theme preference"
        },
        {
          "name": "showLegend",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show chart legend"
        },
        {
          "name": "showTooltip",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show tooltip on hover"
        }
      ],
      "variants": [
        {
          "name": "theme",
          "values": [
            "light",
            "dark",
            "auto"
          ],
          "default": "auto"
        }
      ],
      "slots": [],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { ScatterChart } from '@classic-homes/charts-svelte';\n\n  const data = {\n    series: [\n      {\n        name: 'Dataset A',\n        data: [\n          [10.0, 8.04],\n          [8.07, 6.95],\n          [13.0, 7.58],\n          [9.05, 8.81],\n          [11.0, 8.33],\n          [14.0, 7.66],\n        ],\n      },\n      {\n        name: 'Dataset B',\n        data: [\n          [8.0, 9.04],\n          [6.07, 7.95],\n          [11.0, 8.58],\n          [7.05, 9.81],\n          [9.0, 9.33],\n          [12.0, 8.66],\n        ],\n      },\n    ],\n  };\n</script>\n\n<ScatterChart title=\"Data Correlation\" {data} showTrendLine height={400} />"
        },
        {
          "title": "With Trend Line",
          "code": "<ScatterChart title=\"Correlation Analysis\" {data} showTrendLine height={400} />"
        },
        {
          "title": "Dynamic Point Size",
          "code": "<ScatterChart title=\"Sized Points\" {data} symbolSize={(value) => Math.sqrt(value[1]) * 3} />"
        }
      ],
      "relatedComponents": [
        "LineChart",
        "HeatmapChart"
      ]
    },
    {
      "name": "Section",
      "description": "Content section container for page organization",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "title",
          "type": "string",
          "required": false,
          "description": "Section title (h2)"
        },
        {
          "name": "description",
          "type": "string",
          "required": false,
          "description": "Description text"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional classes"
        },
        {
          "name": "children",
          "type": "Snippet",
          "required": false,
          "description": "Section content"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Section>\n  <p>Your content here</p>\n</Section>"
        },
        {
          "title": "Settings Page",
          "code": "<div class=\"max-w-2xl mx-auto space-y-6\">\n  <Section title=\"Personal Information\" description=\"Update your personal details.\">\n    <form class=\"space-y-4\">\n      <div class=\"grid grid-cols-2 gap-4\">\n        <Input label=\"First Name\" value=\"John\" />\n        <Input label=\"Last Name\" value=\"Doe\" />\n      </div>\n      <Input label=\"Email\" type=\"email\" value=\"john@example.com\" />\n      <Input label=\"Phone\" type=\"tel\" />\n      <Button>Save Changes</Button>\n    </form>\n  </Section>\n\n  <Section title=\"Password\" description=\"Update your password regularly for security.\">\n    <form class=\"space-y-4\">\n      <Input label=\"Current Password\" type=\"password\" />\n      <Input label=\"New Password\" type=\"password\" />\n      <Input label=\"Confirm Password\" type=\"password\" />\n      <Button>Update Password</Button>\n    </form>\n  </Section>\n\n  <Section title=\"Preferences\">\n    <div class=\"space-y-4\">\n      <Select label=\"Language\">\n        <option value=\"en\">English</option>\n        <option value=\"es\">Spanish</option>\n        <option value=\"fr\">French</option>\n      </Select>\n      <Select label=\"Timezone\">\n        <option value=\"utc\">UTC</option>\n        <option value=\"est\">Eastern Time</option>\n        <option value=\"pst\">Pacific Time</option>\n      </Select>\n    </div>\n  </Section>\n</div>"
        },
        {
          "title": "Dashboard Sections",
          "code": "<div class=\"space-y-6\">\n  <Section title=\"Quick Actions\">\n    <div class=\"flex gap-4\">\n      <Button>New Project</Button>\n      <Button variant=\"outline\">Import Data</Button>\n      <Button variant=\"outline\">Generate Report</Button>\n    </div>\n  </Section>\n\n  <Section title=\"Recent Projects\" description=\"Your most recently accessed projects.\">\n    <DataTable data={recentProjects} columns={projectColumns} />\n  </Section>\n\n  <Section title=\"Team Activity\">\n    <div class=\"space-y-3\">\n      {#each activities as activity}\n        <div class=\"flex items-center gap-3 p-3 rounded-lg bg-muted/50\">\n          <Avatar size=\"sm\">{activity.user.initials}</Avatar>\n          <div>\n            <p class=\"text-sm font-medium\">{activity.user.name}</p>\n            <p class=\"text-xs text-muted-foreground\">{activity.action}</p>\n          </div>\n          <span class=\"ml-auto text-xs text-muted-foreground\">\n            {activity.time}\n          </span>\n        </div>\n      {/each}\n    </div>\n  </Section>\n</div>"
        }
      ],
      "relatedComponents": [
        "Card",
        "PageHeader",
        "Separator"
      ]
    },
    {
      "name": "Select",
      "description": "Dropdown for selecting from a list of options with grouping support",
      "category": "form",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "value",
          "type": "string",
          "default": "''",
          "required": false,
          "description": "Current selected value (bindable)"
        },
        {
          "name": "options",
          "type": "(SelectOption | SelectGroup)[]",
          "default": "Required",
          "required": false,
          "description": "Array of options or grouped options"
        },
        {
          "name": "placeholder",
          "type": "string",
          "default": "'Select an option...'",
          "required": false,
          "description": "Placeholder text"
        },
        {
          "name": "disabled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disables the select"
        },
        {
          "name": "required",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Marks as required"
        },
        {
          "name": "name",
          "type": "string",
          "required": false,
          "description": "Name attribute for forms"
        },
        {
          "name": "id",
          "type": "string",
          "required": false,
          "description": "Element ID (auto-generated if empty)"
        },
        {
          "name": "label",
          "type": "string",
          "required": false,
          "description": "Label text"
        },
        {
          "name": "hint",
          "type": "string",
          "required": false,
          "description": "Hint text below the select"
        },
        {
          "name": "error",
          "type": "string",
          "required": false,
          "description": "Error message to display"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        },
        {
          "name": "onValueChange",
          "type": "(value: string) => void",
          "required": false,
          "description": "Callback when value changes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let value = $state('');\n\n  const options = [\n    { value: 'apple', label: 'Apple' },\n    { value: 'banana', label: 'Banana' },\n    { value: 'orange', label: 'Orange' },\n  ];\n</script>\n\n<Select {options} bind:value placeholder=\"Select a fruit\" />"
        },
        {
          "title": "Disabled",
          "code": "<Select {options} disabled value=\"apple\" />"
        },
        {
          "title": "Required",
          "code": "<Select {options} bind:value label=\"Required Field\" required />"
        },
        {
          "title": "With Error",
          "code": "<Select {options} bind:value label=\"Category\" error=\"Please select a category\" />"
        },
        {
          "title": "Form Example",
          "code": "<script>\n  let department = $state('');\n  let departmentError = $state('');\n\n  const departments = [\n    { value: 'sales', label: 'Sales' },\n    { value: 'marketing', label: 'Marketing' },\n    { value: 'engineering', label: 'Engineering' },\n    { value: 'support', label: 'Support' },\n  ];\n\n  function validate() {\n    if (!department) {\n      departmentError = 'Please select a department';\n      return false;\n    }\n    departmentError = '';\n    return true;\n  }\n</script>\n\n<Select\n  label=\"Department\"\n  options={departments}\n  bind:value={department}\n  error={departmentError}\n  required\n/>"
        },
        {
          "title": "Controlled Select",
          "code": "<script>\n  let value = $state('');\n\n  function handleChange(newValue: string) {\n    console.log('Selected:', newValue);\n    value = newValue;\n  }\n</script>\n\n<Select {options} {value} onValueChange={handleChange} />"
        },
        {
          "title": "Country Selector",
          "code": "<script>\n  const countries = [\n    {\n      label: 'North America',\n      options: [\n        { value: 'us', label: 'United States' },\n        { value: 'ca', label: 'Canada' },\n        { value: 'mx', label: 'Mexico' },\n      ],\n    },\n    {\n      label: 'Europe',\n      options: [\n        { value: 'gb', label: 'United Kingdom' },\n        { value: 'de', label: 'Germany' },\n        { value: 'fr', label: 'France' },\n      ],\n    },\n  ];\n</script>\n\n<Select label=\"Country\" options={countries} bind:value placeholder=\"Select your country\" />"
        }
      ],
      "relatedComponents": [
        "Combobox",
        "MultiSelect",
        "RadioGroup"
      ]
    },
    {
      "name": "Separator",
      "description": "Visual divider for separating content",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "orientation",
          "type": "'horizontal' | 'vertical'",
          "default": "'horizontal'",
          "required": false,
          "description": "Line direction"
        },
        {
          "name": "decorative",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "If decorative (no semantic meaning)"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional classes"
        }
      ],
      "variants": [
        {
          "name": "orientation",
          "values": [
            "horizontal",
            "vertical"
          ],
          "default": "horizontal"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<div>Content above</div>\n<Separator />\n<div>Content below</div>"
        },
        {
          "title": "Form Sections",
          "code": "<form class=\"space-y-6\">\n  <div class=\"space-y-4\">\n    <h3 class=\"font-medium\">Personal Information</h3>\n    <Input label=\"Name\" />\n    <Input label=\"Email\" type=\"email\" />\n  </div>\n\n  <Separator />\n\n  <div class=\"space-y-4\">\n    <h3 class=\"font-medium\">Address</h3>\n    <Input label=\"Street\" />\n    <Input label=\"City\" />\n  </div>\n\n  <Separator />\n\n  <Button type=\"submit\">Submit</Button>\n</form>"
        },
        {
          "title": "Dropdown Menu",
          "code": "<DropdownMenu>\n  <DropdownMenu.Item>Profile</DropdownMenu.Item>\n  <DropdownMenu.Item>Settings</DropdownMenu.Item>\n  <Separator class=\"my-1\" />\n  <DropdownMenu.Item>Help</DropdownMenu.Item>\n  <DropdownMenu.Item>Feedback</DropdownMenu.Item>\n  <Separator class=\"my-1\" />\n  <DropdownMenu.Item>Log out</DropdownMenu.Item>\n</DropdownMenu>"
        },
        {
          "title": "Login Divider",
          "code": "<div class=\"space-y-4\">\n  <Button class=\"w-full\">\n    <GoogleIcon class=\"mr-2\" />\n    Continue with Google\n  </Button>\n\n  <div class=\"flex items-center gap-4\">\n    <Separator class=\"flex-1\" />\n    <span class=\"text-xs text-muted-foreground uppercase\">or continue with email</span>\n    <Separator class=\"flex-1\" />\n  </div>\n\n  <form class=\"space-y-4\">\n    <Input label=\"Email\" type=\"email\" />\n    <Input label=\"Password\" type=\"password\" />\n    <Button type=\"submit\" class=\"w-full\">Sign In</Button>\n  </form>\n</div>"
        },
        {
          "title": "Toolbar",
          "code": "<div class=\"flex items-center gap-2 p-2 border rounded-lg\">\n  <Button variant=\"ghost\" size=\"icon\"><BoldIcon /></Button>\n  <Button variant=\"ghost\" size=\"icon\"><ItalicIcon /></Button>\n  <Button variant=\"ghost\" size=\"icon\"><UnderlineIcon /></Button>\n\n  <Separator orientation=\"vertical\" class=\"h-6\" />\n\n  <Button variant=\"ghost\" size=\"icon\"><AlignLeftIcon /></Button>\n  <Button variant=\"ghost\" size=\"icon\"><AlignCenterIcon /></Button>\n  <Button variant=\"ghost\" size=\"icon\"><AlignRightIcon /></Button>\n\n  <Separator orientation=\"vertical\" class=\"h-6\" />\n\n  <Button variant=\"ghost\" size=\"icon\"><LinkIcon /></Button>\n  <Button variant=\"ghost\" size=\"icon\"><ImageIcon /></Button>\n</div>"
        },
        {
          "title": "Card Footer",
          "code": "<Card>\n  <Card.Header>\n    <Card.Title>Subscription</Card.Title>\n  </Card.Header>\n  <Card.Content>\n    <p>You are currently on the Pro plan.</p>\n  </Card.Content>\n  <Separator />\n  <Card.Footer>\n    <Button variant=\"outline\">Change Plan</Button>\n    <Button variant=\"ghost\" class=\"text-destructive\">Cancel</Button>\n  </Card.Footer>\n</Card>"
        }
      ],
      "relatedComponents": [
        "Card",
        "Section",
        "DropdownMenu"
      ]
    },
    {
      "name": "Sidebar",
      "description": "Responsive sidebar navigation with collapsible sections",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "navigation",
          "type": "NavSection[]",
          "required": false,
          "description": "Navigation sections"
        },
        {
          "name": "userRoles",
          "type": "string[]",
          "required": false,
          "description": "User roles for filtering"
        },
        {
          "name": "collapsed",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Desktop collapsed state"
        },
        {
          "name": "isMobile",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Mobile mode"
        },
        {
          "name": "mobileOpen",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Mobile drawer open"
        },
        {
          "name": "onClose",
          "type": "() => void",
          "required": false,
          "description": "Mobile close callback"
        },
        {
          "name": "onNavigate",
          "type": "(item: NavItem) => void",
          "required": false,
          "description": "Navigation callback"
        },
        {
          "name": "logo",
          "type": "Snippet",
          "required": false,
          "description": "Custom logo"
        },
        {
          "name": "icon",
          "type": "Snippet<[NavItem]>",
          "required": false,
          "description": "Custom icon renderer"
        },
        {
          "name": "quickLinks",
          "type": "QuickLink[]",
          "required": false,
          "description": "Quick links at bottom"
        },
        {
          "name": "quickLinksDisplay",
          "type": "'list' | 'icons'",
          "default": "'list'",
          "required": false,
          "description": "Quick links display"
        },
        {
          "name": "quickLinkIcon",
          "type": "Snippet<[QuickLink]>",
          "required": false,
          "description": "Quick link icon renderer"
        },
        {
          "name": "footer",
          "type": "Snippet",
          "required": false,
          "description": "Footer content"
        },
        {
          "name": "variant",
          "type": "'light' | 'dark'",
          "default": "'light'",
          "required": false,
          "description": "Visual variant"
        },
        {
          "name": "strongBorder",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Thicker border"
        },
        {
          "name": "expandedWidth",
          "type": "number",
          "default": "256",
          "required": false,
          "description": "Expanded width (px)"
        },
        {
          "name": "collapsedWidth",
          "type": "number",
          "default": "64",
          "required": false,
          "description": "Collapsed width (px)"
        },
        {
          "name": "searchable",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Enable search"
        },
        {
          "name": "searchPlaceholder",
          "type": "string",
          "default": "'Search...'",
          "required": false,
          "description": "Search placeholder"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional classes"
        }
      ],
      "variants": [
        {
          "name": "quickLinksDisplay",
          "values": [
            "list",
            "icons"
          ],
          "default": "list"
        },
        {
          "name": "variant",
          "values": [
            "light",
            "dark"
          ],
          "default": "light"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  const navigation = [\n    {\n      id: 'main',\n      title: 'Main',\n      items: [\n        { id: 'home', name: 'Home', href: '/', icon: 'home' },\n        { id: 'projects', name: 'Projects', href: '/projects', icon: 'folder' },\n      ],\n    },\n  ];\n</script>\n\n<Sidebar {navigation} />"
        },
        {
          "title": "Sections with Titles",
          "code": "<script>\n  const navigation = [\n    {\n      id: 'main',\n      title: 'Main',\n      items: [\n        { id: 'dashboard', name: 'Dashboard', href: '/dashboard' },\n        { id: 'projects', name: 'Projects', href: '/projects' },\n      ],\n    },\n    {\n      id: 'settings',\n      title: 'Settings',\n      collapsible: true,\n      items: [\n        { id: 'profile', name: 'Profile', href: '/profile' },\n        { id: 'account', name: 'Account', href: '/account' },\n      ],\n    },\n  ];\n</script>"
        },
        {
          "title": "Nested Navigation",
          "code": "<script>\n  const navigation = [\n    {\n      id: 'docs',\n      items: [\n        {\n          id: 'components',\n          name: 'Components',\n          children: [\n            { id: 'button', name: 'Button', href: '/components/button' },\n            { id: 'input', name: 'Input', href: '/components/input' },\n            { id: 'card', name: 'Card', href: '/components/card' },\n          ],\n        },\n      ],\n    },\n  ];\n</script>"
        },
        {
          "title": "Dashboard Sidebar",
          "code": "<script>\n  import { page } from '$app/stores';\n\n  const navigation = [\n    {\n      id: 'overview',\n      items: [\n        { id: 'dashboard', name: 'Dashboard', href: '/dashboard', icon: 'home' },\n        { id: 'analytics', name: 'Analytics', href: '/analytics', icon: 'chart' },\n      ],\n    },\n    {\n      id: 'content',\n      title: 'Content',\n      items: [\n        { id: 'posts', name: 'Posts', href: '/posts', icon: 'file' },\n        { id: 'media', name: 'Media', href: '/media', icon: 'image' },\n        { id: 'pages', name: 'Pages', href: '/pages', icon: 'document' },\n      ],\n    },\n    {\n      id: 'settings',\n      title: 'Settings',\n      collapsible: true,\n      expanded: false,\n      items: [\n        { id: 'general', name: 'General', href: '/settings' },\n        { id: 'team', name: 'Team', href: '/settings/team' },\n        { id: 'billing', name: 'Billing', href: '/settings/billing' },\n      ],\n    },\n  ];\n</script>\n\n<Sidebar\n  {navigation}\n  variant=\"dark\"\n  searchable\n  quickLinks={[{ id: 'help', name: 'Help Center', href: '/help', icon: 'help' }]}\n/>"
        }
      ],
      "relatedComponents": [
        "Header",
        "DashboardLayout",
        "AppShell"
      ]
    },
    {
      "name": "SidebarFlyout",
      "description": "Flyout menu for collapsed sidebar items with children",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "item",
          "type": "NavItem",
          "required": false,
          "description": "The item to show in the flyout"
        },
        {
          "name": "top",
          "type": "number",
          "required": false,
          "description": "Top position in pixels"
        },
        {
          "name": "left",
          "type": "number",
          "default": "64",
          "required": false,
          "description": "Left position in pixels"
        },
        {
          "name": "isLight",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Whether using light variant"
        },
        {
          "name": "onNavigate",
          "type": "(item: NavItem) => void",
          "required": false,
          "description": "Callback when a nav item is clicked"
        },
        {
          "name": "onMouseEnter",
          "type": "() => void",
          "required": false,
          "description": "Callback when mouse enters flyout"
        },
        {
          "name": "onMouseLeave",
          "type": "() => void",
          "required": false,
          "description": "Callback when mouse leaves flyout"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { SidebarFlyout } from '@classic-homes/theme-svelte';\n\n  const item = {\n    id: 'settings',\n    name: 'Settings',\n    href: '/settings',\n    children: [\n      { id: 'profile', name: 'Profile', href: '/settings/profile' },\n      { id: 'account', name: 'Account', href: '/settings/account' },\n    ],\n  };\n\n  let showFlyout = $state(false);\n  let flyoutTop = $state(0);\n</script>\n\n{#if showFlyout}\n  <SidebarFlyout\n    {item}\n    top={flyoutTop}\n    onMouseEnter={() => (showFlyout = true)}\n    onMouseLeave={() => (showFlyout = false)}\n  />\n{/if}"
        },
        {
          "title": "With Children",
          "code": "<script>\n  const item = {\n    id: 'components',\n    name: 'Components',\n    children: [\n      { id: 'button', name: 'Button', href: '/components/button' },\n      { id: 'input', name: 'Input', href: '/components/input' },\n      { id: 'card', name: 'Card', href: '/components/card' },\n    ],\n  };\n</script>\n\n<SidebarFlyout\n  {item}\n  top={100}\n  left={64}\n  onNavigate={(item) => console.log('Navigate to:', item.href)}\n/>"
        },
        {
          "title": "Dark Variant",
          "code": "<SidebarFlyout {item} top={100} isLight={false} />"
        },
        {
          "title": "Custom Position",
          "code": "<SidebarFlyout {item} top={200} left={80} />"
        },
        {
          "title": "Integration with Collapsed Sidebar",
          "code": "<script>\n  import { SidebarNavItem, SidebarFlyout } from '@classic-homes/theme-svelte';\n\n  let flyoutItem = $state(null);\n  let flyoutTop = $state(0);\n\n  function handleItemMouseEnter(item, event) {\n    flyoutItem = item;\n    flyoutTop = event.currentTarget.getBoundingClientRect().top;\n  }\n\n  function handleItemMouseLeave() {\n    flyoutItem = null;\n  }\n</script>\n\n{#each items as item}\n  <SidebarNavItem\n    {item}\n    collapsed\n    onMouseEnter={handleItemMouseEnter}\n    onMouseLeave={handleItemMouseLeave}\n  />\n{/each}\n\n{#if flyoutItem}\n  <SidebarFlyout\n    item={flyoutItem}\n    top={flyoutTop}\n    onMouseEnter={() => {\n      /* keep open */\n    }}\n    onMouseLeave={handleItemMouseLeave}\n    onNavigate={(item) => goto(item.href)}\n  />\n{/if}"
        }
      ],
      "relatedComponents": [
        "Sidebar",
        "SidebarNavItem",
        "SidebarSection"
      ]
    },
    {
      "name": "SidebarNavItem",
      "description": "Individual navigation item component for custom sidebar implementations",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "item",
          "type": "NavItem",
          "required": false,
          "description": "The navigation item to render"
        },
        {
          "name": "isLight",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Whether using light variant"
        },
        {
          "name": "collapsed",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Whether sidebar is collapsed"
        },
        {
          "name": "isMobile",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Whether currently on mobile"
        },
        {
          "name": "icon",
          "type": "Snippet<[NavItem]>",
          "required": false,
          "description": "Custom icon renderer"
        },
        {
          "name": "searchQuery",
          "type": "string",
          "default": "''",
          "required": false,
          "description": "Search query for text highlighting"
        },
        {
          "name": "enablePinning",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Enable pinning feature"
        },
        {
          "name": "pinIcon",
          "type": "Snippet<[{ isPinned: boolean }]>",
          "required": false,
          "description": "Custom pin icon renderer"
        },
        {
          "name": "isPinnedSection",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Whether this item is in the pinned section"
        },
        {
          "name": "onNavigate",
          "type": "(item: NavItem) => void",
          "required": false,
          "description": "Callback when item is clicked"
        },
        {
          "name": "onMouseEnter",
          "type": "(item: NavItem, event: MouseEvent) => void",
          "required": false,
          "description": "Callback when mouse enters item"
        },
        {
          "name": "onMouseLeave",
          "type": "() => void",
          "required": false,
          "description": "Callback when mouse leaves item"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { SidebarNavItem } from '@classic-homes/theme-svelte';\n\n  const item = {\n    id: 'dashboard',\n    name: 'Dashboard',\n    href: '/dashboard',\n    icon: 'home',\n  };\n</script>\n\n<SidebarNavItem {item} />"
        },
        {
          "title": "With Children (Expandable)",
          "code": "<script>\n  import { SidebarNavItem } from '@classic-homes/theme-svelte';\n\n  const item = {\n    id: 'settings',\n    name: 'Settings',\n    icon: 'settings',\n    children: [\n      { id: 'profile', name: 'Profile', href: '/settings/profile' },\n      { id: 'account', name: 'Account', href: '/settings/account' },\n    ],\n  };\n</script>\n\n<SidebarNavItem {item} />"
        },
        {
          "title": "With Pinning",
          "code": "<script>\n  import { SidebarNavItem } from '@classic-homes/theme-svelte';\n\n  const item = {\n    id: 'dashboard',\n    name: 'Dashboard',\n    href: '/dashboard',\n    pinnable: true,\n  };\n</script>\n\n<SidebarNavItem {item} enablePinning />"
        },
        {
          "title": "Dark Variant",
          "code": "<SidebarNavItem {item} isLight={false} />"
        },
        {
          "title": "With Search Highlighting",
          "code": "<SidebarNavItem {item} searchQuery=\"dash\" />"
        }
      ],
      "relatedComponents": [
        "Sidebar",
        "SidebarSection",
        "SidebarFlyout"
      ]
    },
    {
      "name": "SidebarSearch",
      "description": "Search input component for sidebar navigation filtering",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "value",
          "type": "string",
          "required": false,
          "description": "Current search value"
        },
        {
          "name": "placeholder",
          "type": "string",
          "default": "'Search...'",
          "required": false,
          "description": "Placeholder text"
        },
        {
          "name": "isLight",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Whether using light variant"
        },
        {
          "name": "debounceMs",
          "type": "number",
          "default": "150",
          "required": false,
          "description": "Debounce delay in milliseconds"
        },
        {
          "name": "onInput",
          "type": "(value: string) => void",
          "required": false,
          "description": "Callback when value changes (debounced)"
        },
        {
          "name": "onClear",
          "type": "() => void",
          "required": false,
          "description": "Callback when search is cleared"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { SidebarSearch } from '@classic-homes/theme-svelte';\n\n  let searchValue = $state('');\n\n  function handleInput(value) {\n    searchValue = value;\n    // Filter navigation based on searchValue\n  }\n\n  function handleClear() {\n    searchValue = '';\n    // Reset navigation filter\n  }\n</script>\n\n<SidebarSearch value={searchValue} onInput={handleInput} onClear={handleClear} />"
        },
        {
          "title": "Custom Placeholder",
          "code": "<SidebarSearch\n  value={searchValue}\n  placeholder=\"Filter navigation...\"\n  onInput={handleInput}\n  onClear={handleClear}\n/>"
        },
        {
          "title": "Dark Variant",
          "code": "<SidebarSearch value={searchValue} isLight={false} onInput={handleInput} onClear={handleClear} />"
        },
        {
          "title": "Custom Debounce",
          "code": "<SidebarSearch value={searchValue} debounceMs={300} onInput={handleInput} onClear={handleClear} />"
        },
        {
          "title": "In a Custom Sidebar",
          "code": "<script>\n  import { SidebarSearch, SidebarSection } from '@classic-homes/theme-svelte';\n\n  let searchQuery = $state('');\n  const navigation = [\n    /* ... */\n  ];\n\n  function filterNavigation(items, query) {\n    if (!query) return items;\n    return items.filter((item) => item.name.toLowerCase().includes(query.toLowerCase()));\n  }\n\n  const filteredNavigation = $derived(\n    navigation.map((section) => ({\n      ...section,\n      items: filterNavigation(section.items, searchQuery),\n    }))\n  );\n</script>\n\n<SidebarSearch\n  value={searchQuery}\n  onInput={(v) => (searchQuery = v)}\n  onClear={() => (searchQuery = '')}\n/>\n\n{#each filteredNavigation as section}\n  <SidebarSection {section} {searchQuery} />\n{/each}"
        }
      ],
      "relatedComponents": [
        "Sidebar",
        "SidebarSection",
        "SidebarNavItem"
      ]
    },
    {
      "name": "SidebarSection",
      "description": "Navigation section component with optional collapsible header",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "section",
          "type": "NavSection",
          "required": false,
          "description": "The navigation section to render"
        },
        {
          "name": "isLight",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Whether using light variant"
        },
        {
          "name": "collapsed",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Whether sidebar is collapsed"
        },
        {
          "name": "isMobile",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Whether currently on mobile"
        },
        {
          "name": "icon",
          "type": "Snippet<[NavItem]>",
          "required": false,
          "description": "Custom icon renderer for nav items"
        },
        {
          "name": "searchQuery",
          "type": "string",
          "default": "''",
          "required": false,
          "description": "Search query for text highlighting"
        },
        {
          "name": "enablePinning",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Enable pinning feature for items"
        },
        {
          "name": "pinIcon",
          "type": "Snippet<[{ isPinned: boolean }]>",
          "required": false,
          "description": "Custom pin icon renderer"
        },
        {
          "name": "isPinnedSection",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Whether this is the special pinned section"
        },
        {
          "name": "onNavigate",
          "type": "(item: NavItem) => void",
          "required": false,
          "description": "Callback when a navigation item is clicked"
        },
        {
          "name": "onItemMouseEnter",
          "type": "(item: NavItem, event: MouseEvent) => void",
          "required": false,
          "description": "Callback when mouse enters an item"
        },
        {
          "name": "onItemMouseLeave",
          "type": "() => void",
          "required": false,
          "description": "Callback when mouse leaves an item"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { SidebarSection } from '@classic-homes/theme-svelte';\n\n  const section = {\n    id: 'main',\n    title: 'Main Navigation',\n    items: [\n      { id: 'home', name: 'Home', href: '/' },\n      { id: 'about', name: 'About', href: '/about' },\n    ],\n  };\n</script>\n\n<SidebarSection {section} />"
        },
        {
          "title": "Collapsible Section",
          "code": "<script>\n  import { SidebarSection } from '@classic-homes/theme-svelte';\n\n  const section = {\n    id: 'settings',\n    title: 'Settings',\n    collapsible: true,\n    items: [\n      { id: 'profile', name: 'Profile', href: '/settings/profile' },\n      { id: 'account', name: 'Account', href: '/settings/account' },\n      { id: 'security', name: 'Security', href: '/settings/security' },\n    ],\n  };\n</script>\n\n<SidebarSection {section} />"
        },
        {
          "title": "Without Title",
          "code": "<script>\n  const section = {\n    id: 'main',\n    items: [\n      { id: 'dashboard', name: 'Dashboard', href: '/dashboard' },\n      { id: 'projects', name: 'Projects', href: '/projects' },\n    ],\n  };\n</script>\n\n<SidebarSection {section} />"
        },
        {
          "title": "Dark Variant",
          "code": "<SidebarSection {section} isLight={false} />"
        },
        {
          "title": "With Navigation Callback",
          "code": "<SidebarSection\n  {section}\n  onNavigate={(item) => {\n    console.log('Navigated to:', item.name);\n  }}\n/>"
        }
      ],
      "relatedComponents": [
        "Sidebar",
        "SidebarNavItem",
        "SidebarFlyout"
      ]
    },
    {
      "name": "Signature",
      "description": "Digital signature capture with draw and type modes",
      "category": "form",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "value",
          "type": "SignatureData | null",
          "default": "null",
          "required": false,
          "description": "Signature data (bindable)"
        },
        {
          "name": "mode",
          "type": "'draw' | 'type'",
          "default": "'draw'",
          "required": false,
          "description": "Capture mode"
        },
        {
          "name": "disabled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disables capture"
        },
        {
          "name": "required",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Marks as required"
        },
        {
          "name": "label",
          "type": "string",
          "required": false,
          "description": "Label text"
        },
        {
          "name": "error",
          "type": "string",
          "required": false,
          "description": "Error message"
        },
        {
          "name": "strokeColor",
          "type": "string",
          "default": "'#000000'",
          "required": false,
          "description": "Drawing stroke color"
        },
        {
          "name": "strokeWidth",
          "type": "number",
          "default": "2",
          "required": false,
          "description": "Drawing stroke width"
        },
        {
          "name": "fonts",
          "type": "SignatureFont[]",
          "required": false,
          "description": "Available fonts for type mode"
        },
        {
          "name": "onValidate",
          "type": "(data: SignatureData) => SignatureValidationResult",
          "required": false,
          "description": "Validation handler"
        }
      ],
      "variants": [
        {
          "name": "mode",
          "values": [
            "draw",
            "type"
          ],
          "default": "draw"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let signature = $state(null);\n</script>\n\n<Signature bind:value={signature} />"
        },
        {
          "title": "Agreement Form",
          "code": "<script>\n  let signature = $state(null);\n\n  function handleSubmit() {\n    if (!signature) {\n      // Handle missing signature\n      return;\n    }\n    // Submit form with signature\n  }\n</script>\n\n<div class=\"space-y-4\">\n  <p class=\"text-sm text-muted-foreground\">\n    By signing below, you agree to the terms and conditions.\n  </p>\n  <Signature label=\"Your Signature\" bind:value={signature} required />\n  <Button onclick={handleSubmit} disabled={!signature}>Submit Agreement</Button>\n</div>"
        },
        {
          "title": "Clear and Retry",
          "code": "<div class=\"space-y-4\">\n  <Signature bind:value={signature} />\n  <Button variant=\"outline\" onclick={() => (signature = null)} disabled={!signature}>\n    Clear Signature\n  </Button>\n</div>"
        }
      ],
      "relatedComponents": [
        "Input",
        "Button"
      ]
    },
    {
      "name": "Skeleton",
      "description": "Placeholder loading component that mimics content shape",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "variant",
          "type": "'default' | 'text' | 'avatar' | 'card' | 'button' | 'title'",
          "default": "'default'",
          "required": false,
          "description": "Predefined shape variant"
        },
        {
          "name": "animation",
          "type": "'pulse' | 'shimmer' | 'none'",
          "default": "'pulse'",
          "required": false,
          "description": "Animation style"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "variant",
          "values": [
            "default",
            "text",
            "avatar",
            "card",
            "button",
            "title"
          ],
          "default": "default"
        },
        {
          "name": "animation",
          "values": [
            "pulse",
            "shimmer",
            "none"
          ],
          "default": "pulse"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Skeleton class=\"h-4 w-full\" />"
        },
        {
          "title": "Default",
          "description": "Custom sizing with classes:",
          "code": "<Skeleton class=\"h-4 w-full\" />\n<Skeleton class=\"h-8 w-64\" />"
        },
        {
          "title": "Text",
          "description": "Single line of text:",
          "code": "<Skeleton variant=\"text\" /> <!-- h-4 w-full -->"
        },
        {
          "title": "Title",
          "description": "Larger heading text:",
          "code": "<Skeleton variant=\"title\" /> <!-- h-6 w-3/4 -->"
        },
        {
          "title": "Avatar",
          "description": "Circular avatar placeholder:",
          "code": "<Skeleton variant=\"avatar\" /> <!-- h-12 w-12 rounded-full -->"
        },
        {
          "title": "Card",
          "description": "Card-sized placeholder:",
          "code": "<Skeleton variant=\"card\" /> <!-- h-32 w-full -->"
        },
        {
          "title": "Button",
          "description": "Button-sized placeholder:",
          "code": "<Skeleton variant=\"button\" /> <!-- h-10 w-24 -->"
        },
        {
          "title": "Text Content Loading",
          "code": "<div class=\"space-y-2\">\n  <Skeleton variant=\"title\" />\n  <Skeleton variant=\"text\" />\n  <Skeleton variant=\"text\" />\n  <Skeleton class=\"h-4 w-2/3\" />\n</div>"
        },
        {
          "title": "User Card Loading",
          "code": "<div class=\"flex items-center gap-4\">\n  <Skeleton variant=\"avatar\" />\n  <div class=\"space-y-2 flex-1\">\n    <Skeleton class=\"h-4 w-32\" />\n    <Skeleton class=\"h-3 w-24\" />\n  </div>\n</div>"
        },
        {
          "title": "Card Loading",
          "code": "<Card>\n  <CardHeader>\n    <Skeleton variant=\"title\" />\n    <Skeleton variant=\"text\" class=\"mt-2\" />\n  </CardHeader>\n  <CardContent>\n    <Skeleton variant=\"card\" />\n  </CardContent>\n  <CardFooter>\n    <Skeleton variant=\"button\" />\n  </CardFooter>\n</Card>"
        },
        {
          "title": "Table Row Loading",
          "code": "<tr>\n  <td><Skeleton class=\"h-4 w-8\" /></td>\n  <td><Skeleton class=\"h-4 w-32\" /></td>\n  <td><Skeleton class=\"h-4 w-48\" /></td>\n  <td><Skeleton class=\"h-4 w-24\" /></td>\n</tr>"
        },
        {
          "title": "Grid Loading",
          "code": "<div class=\"grid grid-cols-3 gap-4\">\n  {#each Array(6) as _}\n    <div class=\"space-y-2\">\n      <Skeleton class=\"h-32 w-full\" />\n      <Skeleton variant=\"title\" />\n      <Skeleton variant=\"text\" />\n    </div>\n  {/each}\n</div>"
        },
        {
          "title": "Profile Page Loading",
          "code": "<div class=\"space-y-6\">\n  <!-- Header -->\n  <div class=\"flex items-center gap-6\">\n    <Skeleton class=\"h-24 w-24 rounded-full\" />\n    <div class=\"space-y-2\">\n      <Skeleton class=\"h-8 w-48\" />\n      <Skeleton class=\"h-4 w-32\" />\n    </div>\n  </div>\n\n  <!-- Stats -->\n  <div class=\"grid grid-cols-3 gap-4\">\n    {#each Array(3) as _}\n      <Skeleton class=\"h-20\" />\n    {/each}\n  </div>\n\n  <!-- Content -->\n  <div class=\"space-y-2\">\n    <Skeleton variant=\"text\" />\n    <Skeleton variant=\"text\" />\n    <Skeleton class=\"h-4 w-3/4\" />\n  </div>\n</div>"
        },
        {
          "title": "Conditional Loading",
          "code": "{#if loading}\n  <div class=\"space-y-2\">\n    <Skeleton variant=\"title\" />\n    <Skeleton variant=\"text\" />\n    <Skeleton variant=\"text\" />\n  </div>\n{:else}\n  <div>\n    <h2>{title}</h2>\n    <p>{content}</p>\n  </div>\n{/if}"
        },
        {
          "title": "Image Loading",
          "code": "<div class=\"relative\">\n  {#if imageLoading}\n    <Skeleton class=\"h-64 w-full\" />\n  {/if}\n  <img\n    src={imageUrl}\n    onload={() => (imageLoading = false)}\n    class={imageLoading ? 'invisible absolute' : ''}\n  />\n</div>"
        },
        {
          "title": "Pulse (default)",
          "description": "Fades opacity in and out. Subtle and widely used:",
          "code": "<Skeleton animation=\"pulse\" class=\"h-4 w-full\" />"
        },
        {
          "title": "Shimmer",
          "description": "A sweeping gradient highlight that moves across the element. More visually distinctive:",
          "code": "<Skeleton animation=\"shimmer\" class=\"h-4 w-full\" />"
        },
        {
          "title": "None",
          "description": "Static placeholder with no animation:",
          "code": "<Skeleton animation=\"none\" class=\"h-4 w-full\" />"
        }
      ],
      "relatedComponents": [
        "Spinner",
        "ProgressBar",
        "LoadingOverlay",
        "LoadingSection",
        "Card"
      ]
    },
    {
      "name": "Slider",
      "description": "Range slider for selecting numeric values",
      "category": "form",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "value",
          "type": "number[]",
          "default": "[50]",
          "required": false,
          "description": "Current value(s) (bindable)"
        },
        {
          "name": "min",
          "type": "number",
          "default": "0",
          "required": false,
          "description": "Minimum value"
        },
        {
          "name": "max",
          "type": "number",
          "default": "100",
          "required": false,
          "description": "Maximum value"
        },
        {
          "name": "step",
          "type": "number",
          "default": "1",
          "required": false,
          "description": "Step increment"
        },
        {
          "name": "disabled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disables the slider"
        },
        {
          "name": "orientation",
          "type": "'horizontal' | 'vertical'",
          "default": "'horizontal'",
          "required": false,
          "description": "Slider direction"
        },
        {
          "name": "size",
          "type": "'sm' | 'md' | 'lg'",
          "default": "'md'",
          "required": false,
          "description": "Size variant"
        },
        {
          "name": "label",
          "type": "string",
          "required": false,
          "description": "Label text"
        },
        {
          "name": "hint",
          "type": "string",
          "required": false,
          "description": "Hint text below the slider"
        },
        {
          "name": "error",
          "type": "string",
          "required": false,
          "description": "Error message"
        },
        {
          "name": "showValue",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Shows current value"
        },
        {
          "name": "formatValue",
          "type": "(value: number) => string",
          "required": false,
          "description": "Value formatter"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        },
        {
          "name": "onValueChange",
          "type": "(value: number[]) => void",
          "required": false,
          "description": "Callback during drag"
        },
        {
          "name": "onValueCommit",
          "type": "(value: number[]) => void",
          "required": false,
          "description": "Callback when drag ends"
        }
      ],
      "variants": [
        {
          "name": "orientation",
          "values": [
            "horizontal",
            "vertical"
          ],
          "default": "horizontal"
        },
        {
          "name": "size",
          "values": [
            "sm",
            "md",
            "lg"
          ],
          "default": "md"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let value = $state([50]);\n</script>\n\n<Slider bind:value />"
        },
        {
          "title": "Volume Control",
          "code": "<script>\n  let volume = $state([70]);\n</script>\n\n<Slider\n  label=\"Volume\"\n  hint=\"Adjust speaker volume\"\n  bind:value={volume}\n  min={0}\n  max={100}\n  showValue\n  formatValue={(v) => `${v}%`}\n/>"
        },
        {
          "title": "Price Filter",
          "code": "<script>\n  let priceRange = $state([0, 500]);\n</script>\n\n<Slider\n  label=\"Price Range\"\n  hint=\"Filter products by price\"\n  bind:value={priceRange}\n  min={0}\n  max={1000}\n  step={10}\n  showValue\n  formatValue={(v) => `$${v}`}\n/>"
        },
        {
          "title": "Temperature",
          "code": "<script>\n  let temp = $state([72]);\n</script>\n\n<Slider\n  label=\"Temperature\"\n  hint=\"Set thermostat temperature\"\n  bind:value={temp}\n  min={60}\n  max={85}\n  showValue\n  formatValue={(v) => `${v}°F`}\n/>"
        },
        {
          "title": "Opacity Control",
          "code": "<script>\n  let opacity = $state([100]);\n</script>\n\n<Slider\n  label=\"Opacity\"\n  bind:value={opacity}\n  min={0}\n  max={100}\n  showValue\n  formatValue={(v) => `${v}%`}\n  onValueChange={(val) => {\n    element.style.opacity = val[0] / 100;\n  }}\n/>"
        },
        {
          "title": "Discrete Steps",
          "code": "<script>\n  let rating = $state([3]);\n  const labels = ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];\n</script>\n\n<Slider\n  label=\"Rating\"\n  hint=\"Rate your experience\"\n  bind:value={rating}\n  min={1}\n  max={5}\n  step={1}\n  showValue\n  formatValue={(v) => labels[v - 1]}\n/>"
        },
        {
          "title": "Commit on Release",
          "description": "Trigger action only when user finishes dragging:",
          "code": "<Slider\n  bind:value\n  onValueCommit={(val) => {\n    savePreference(val[0]);\n  }}\n/>"
        }
      ],
      "relatedComponents": [
        "NumberInput",
        "Input"
      ]
    },
    {
      "name": "Spinner",
      "description": "Loading indicator for async operations",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "size",
          "type": "'sm' | 'md' | 'lg' | 'xl'",
          "default": "'md'",
          "required": false,
          "description": "Size of the spinner"
        },
        {
          "name": "label",
          "type": "string",
          "default": "'Loading...'",
          "required": false,
          "description": "Accessible label for screen readers"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "size",
          "values": [
            "sm",
            "md",
            "lg",
            "xl"
          ],
          "default": "md"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Spinner />"
        },
        {
          "title": "Button Loading State",
          "code": "<Button disabled={loading}>\n  {#if loading}\n    <Spinner size=\"sm\" label=\"Submitting...\" class=\"mr-2\" />\n    Submitting...\n  {:else}\n    Submit\n  {/if}\n</Button>"
        },
        {
          "title": "Centered Loading",
          "code": "<div class=\"flex items-center justify-center h-64\">\n  <Spinner size=\"lg\" label=\"Loading content...\" />\n</div>"
        },
        {
          "title": "Full Page Loading",
          "code": "<div class=\"fixed inset-0 flex items-center justify-center bg-background/80 z-50\">\n  <div class=\"text-center\">\n    <Spinner size=\"xl\" label=\"Loading application...\" />\n    <p class=\"mt-4 text-muted-foreground\">Loading...</p>\n  </div>\n</div>"
        },
        {
          "title": "Inline Loading",
          "code": "<p>\n  Checking availability\n  <Spinner size=\"sm\" class=\"inline ml-2\" label=\"Checking...\" />\n</p>"
        },
        {
          "title": "Card Loading State",
          "code": "<Card>\n  <CardContent class=\"flex items-center justify-center h-32\">\n    {#if loading}\n      <Spinner label=\"Loading data...\" />\n    {:else}\n      <p>{data}</p>\n    {/if}\n  </CardContent>\n</Card>"
        },
        {
          "title": "With Loading Text",
          "code": "<div class=\"flex items-center gap-3\">\n  <Spinner size=\"md\" />\n  <span class=\"text-muted-foreground\">Loading your dashboard...</span>\n</div>"
        },
        {
          "title": "Table Loading",
          "code": "<table>\n  <tbody>\n    {#if loading}\n      <tr>\n        <td colspan=\"4\" class=\"text-center py-8\">\n          <Spinner label=\"Loading data...\" />\n        </td>\n      </tr>\n    {:else}\n      {#each rows as row}\n        <tr><!-- row content --></tr>\n      {/each}\n    {/if}\n  </tbody>\n</table>"
        }
      ],
      "relatedComponents": [
        "Skeleton",
        "ProgressBar",
        "LoadingOverlay",
        "LoadingSection",
        "LoadingPage",
        "Button"
      ]
    },
    {
      "name": "Switch",
      "description": "Toggle switch for binary on/off settings",
      "category": "form",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "checked",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Whether the switch is on (bindable)"
        },
        {
          "name": "disabled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disables the switch"
        },
        {
          "name": "required",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Marks as required"
        },
        {
          "name": "name",
          "type": "string",
          "required": false,
          "description": "Name attribute for forms"
        },
        {
          "name": "value",
          "type": "string",
          "required": false,
          "description": "Value attribute for forms"
        },
        {
          "name": "id",
          "type": "string",
          "required": false,
          "description": "Element ID for label association"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        },
        {
          "name": "onchange",
          "type": "(checked: boolean) => void",
          "required": false,
          "description": "Callback when checked state changes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  let enabled = $state(false);\n</script>\n\n<Switch bind:checked={enabled} />"
        },
        {
          "title": "On (Checked)",
          "code": "<Switch checked />"
        },
        {
          "title": "Off (Unchecked)",
          "code": "<Switch />"
        },
        {
          "title": "Disabled",
          "code": "<Switch disabled />\n<Switch disabled checked />"
        },
        {
          "title": "Feature Toggle",
          "code": "<script>\n  let betaFeatures = $state(false);\n</script>\n\n<div class=\"p-4 border rounded-lg\">\n  <div class=\"flex items-center justify-between\">\n    <div>\n      <h3 class=\"font-medium\">Beta Features</h3>\n      <p class=\"text-sm text-muted-foreground\">Enable experimental features</p>\n    </div>\n    <Switch bind:checked={betaFeatures} />\n  </div>\n\n  {#if betaFeatures}\n    <p class=\"mt-3 text-sm text-warning\">Beta features may be unstable.</p>\n  {/if}\n</div>"
        },
        {
          "title": "Privacy Controls",
          "code": "<script>\n  let privacy = $state({\n    analytics: true,\n    personalization: false,\n    thirdParty: false,\n  });\n</script>\n\n<div class=\"space-y-4\">\n  <h2 class=\"text-lg font-semibold\">Privacy Settings</h2>\n\n  <div class=\"space-y-3\">\n    <div class=\"flex items-center justify-between\">\n      <Label for=\"analytics\">Analytics cookies</Label>\n      <Switch id=\"analytics\" bind:checked={privacy.analytics} />\n    </div>\n\n    <div class=\"flex items-center justify-between\">\n      <Label for=\"personalization\">Personalization</Label>\n      <Switch id=\"personalization\" bind:checked={privacy.personalization} />\n    </div>\n\n    <div class=\"flex items-center justify-between\">\n      <Label for=\"third-party\">Third-party cookies</Label>\n      <Switch id=\"third-party\" bind:checked={privacy.thirdParty} />\n    </div>\n  </div>\n</div>"
        },
        {
          "title": "Inline Switch",
          "code": "<p class=\"text-sm\">\n  Show completed tasks\n  <Switch bind:checked={showCompleted} class=\"ml-2 align-middle\" />\n</p>"
        }
      ],
      "relatedComponents": [
        "Checkbox",
        "RadioGroup",
        "Label"
      ]
    },
    {
      "name": "TabPanel",
      "description": "Content panel component for Tabs navigation",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "value",
          "type": "string",
          "required": false,
          "description": "Tab ID this panel belongs to (required)"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  const tabs = [\n    { id: 'account', label: 'Account' },\n    { id: 'password', label: 'Password' },\n    { id: 'notifications', label: 'Notifications' },\n  ];\n</script>\n\n<Tabs {tabs} defaultValue=\"account\">\n  <TabPanel value=\"account\">\n    <p>Account settings content here.</p>\n  </TabPanel>\n  <TabPanel value=\"password\">\n    <p>Password settings content here.</p>\n  </TabPanel>\n  <TabPanel value=\"notifications\">\n    <p>Notification settings content here.</p>\n  </TabPanel>\n</Tabs>"
        },
        {
          "title": "With Forms",
          "code": "<TabPanel value=\"profile\">\n  <form class=\"space-y-4\">\n    <FormField label=\"Name\" name=\"name\" />\n    <FormField label=\"Email\" name=\"email\" type=\"email\" />\n    <Button type=\"submit\">Save Changes</Button>\n  </form>\n</TabPanel>"
        },
        {
          "title": "With Cards",
          "code": "<TabPanel value=\"billing\">\n  <div class=\"grid gap-4 md:grid-cols-2\">\n    <Card>\n      <CardHeader>\n        <CardTitle>Current Plan</CardTitle>\n      </CardHeader>\n      <CardContent>Pro - $29/month</CardContent>\n    </Card>\n    <Card>\n      <CardHeader>\n        <CardTitle>Payment Method</CardTitle>\n      </CardHeader>\n      <CardContent>Visa ending in 4242</CardContent>\n    </Card>\n  </div>\n</TabPanel>"
        },
        {
          "title": "Custom Styling",
          "code": "<TabPanel value=\"settings\" class=\"p-6 bg-muted rounded-lg\">\n  <h3 class=\"font-semibold mb-4\">Advanced Settings</h3>\n  <!-- Content -->\n</TabPanel>"
        }
      ],
      "relatedComponents": [
        "Tabs"
      ]
    },
    {
      "name": "Tabs",
      "description": "Tabbed interface for organizing content",
      "category": "layout",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "tabs",
          "type": "Tab[]",
          "required": false,
          "description": "Array of tab definitions"
        },
        {
          "name": "value",
          "type": "string",
          "default": "First tab ID",
          "required": false,
          "description": "Active tab ID (bindable)"
        },
        {
          "name": "id",
          "type": "string",
          "default": "Auto-generated",
          "required": false,
          "description": "Unique ID for ARIA"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional classes"
        },
        {
          "name": "children",
          "type": "Snippet",
          "required": false,
          "description": "Tab panel content"
        },
        {
          "name": "onValueChange",
          "type": "(value: string) => void",
          "required": false,
          "description": "Change callback"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  const tabs = [\n    { id: 'account', label: 'Account' },\n    { id: 'security', label: 'Security' },\n    { id: 'notifications', label: 'Notifications' },\n  ];\n</script>\n\n<Tabs {tabs}>\n  <TabPanel id=\"account\">\n    <p>Account settings content</p>\n  </TabPanel>\n  <TabPanel id=\"security\">\n    <p>Security settings content</p>\n  </TabPanel>\n  <TabPanel id=\"notifications\">\n    <p>Notification preferences</p>\n  </TabPanel>\n</Tabs>"
        },
        {
          "title": "Settings Page",
          "code": "<script>\n  const tabs = [\n    { id: 'profile', label: 'Profile' },\n    { id: 'account', label: 'Account' },\n    { id: 'security', label: 'Security' },\n    { id: 'billing', label: 'Billing' },\n  ];\n</script>\n\n<Tabs {tabs}>\n  <TabPanel id=\"profile\">\n    <Section title=\"Personal Information\">\n      <form class=\"space-y-4\">\n        <Input label=\"Display Name\" />\n        <Textarea label=\"Bio\" />\n        <Button>Save Changes</Button>\n      </form>\n    </Section>\n  </TabPanel>\n\n  <TabPanel id=\"account\">\n    <Section title=\"Email Settings\">\n      <Input label=\"Email Address\" type=\"email\" />\n      <Button>Update Email</Button>\n    </Section>\n  </TabPanel>\n\n  <TabPanel id=\"security\">\n    <Section title=\"Password\">\n      <form class=\"space-y-4\">\n        <Input label=\"Current Password\" type=\"password\" />\n        <Input label=\"New Password\" type=\"password\" />\n        <Button>Change Password</Button>\n      </form>\n    </Section>\n  </TabPanel>\n\n  <TabPanel id=\"billing\">\n    <Section title=\"Payment Methods\">\n      <p>Your payment methods will appear here.</p>\n    </Section>\n  </TabPanel>\n</Tabs>"
        },
        {
          "title": "Product Details",
          "code": "<script>\n  const tabs = [\n    { id: 'description', label: 'Description' },\n    { id: 'specs', label: 'Specifications' },\n    { id: 'reviews', label: 'Reviews' },\n  ];\n</script>\n\n<Tabs {tabs}>\n  <TabPanel id=\"description\">\n    <div class=\"prose\">\n      <p>{product.description}</p>\n    </div>\n  </TabPanel>\n\n  <TabPanel id=\"specs\">\n    <DataTable data={product.specs} columns={specColumns} />\n  </TabPanel>\n\n  <TabPanel id=\"reviews\">\n    {#each reviews as review}\n      <Card class=\"mb-4\">\n        <Card.Header>\n          <div class=\"flex items-center gap-2\">\n            <Avatar size=\"sm\">{review.author[0]}</Avatar>\n            <span>{review.author}</span>\n          </div>\n        </Card.Header>\n        <Card.Content>\n          <p>{review.content}</p>\n        </Card.Content>\n      </Card>\n    {/each}\n  </TabPanel>\n</Tabs>"
        },
        {
          "title": "Code Examples",
          "code": "<script>\n  const tabs = [\n    { id: 'svelte', label: 'Svelte' },\n    { id: 'react', label: 'React' },\n    { id: 'vue', label: 'Vue' },\n  ];\n</script>\n\n<Tabs {tabs}>\n  <TabPanel id=\"svelte\">\n    <pre><code>{svelteCode}</code></pre>\n  </TabPanel>\n  <TabPanel id=\"react\">\n    <pre><code>{reactCode}</code></pre>\n  </TabPanel>\n  <TabPanel id=\"vue\">\n    <pre><code>{vueCode}</code></pre>\n  </TabPanel>\n</Tabs>"
        }
      ],
      "relatedComponents": [
        "Card",
        "Section",
        "Separator"
      ]
    },
    {
      "name": "Textarea",
      "description": "Multi-line text input for longer content like messages and descriptions",
      "category": "form",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "value",
          "type": "string",
          "default": "''",
          "required": false,
          "description": "Textarea value (bindable)"
        },
        {
          "name": "placeholder",
          "type": "string",
          "required": false,
          "description": "Placeholder text"
        },
        {
          "name": "disabled",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Disables the textarea"
        },
        {
          "name": "readonly",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Makes the textarea read-only"
        },
        {
          "name": "required",
          "type": "boolean",
          "default": "false",
          "required": false,
          "description": "Marks as required"
        },
        {
          "name": "name",
          "type": "string",
          "required": false,
          "description": "Textarea name for forms"
        },
        {
          "name": "id",
          "type": "string",
          "required": false,
          "description": "Textarea ID for label association"
        },
        {
          "name": "rows",
          "type": "number",
          "default": "3",
          "required": false,
          "description": "Number of visible text rows"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        },
        {
          "name": "onValueChange",
          "type": "(value: string) => void",
          "required": false,
          "description": "Callback when value changes"
        }
      ],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Textarea placeholder=\"Enter your message...\" />"
        },
        {
          "title": "Disabled",
          "code": "<Textarea disabled value=\"Cannot edit this content\" />"
        },
        {
          "title": "Read-only",
          "code": "<Textarea readonly value=\"This content is read-only\" />"
        },
        {
          "title": "Required",
          "code": "<Textarea required placeholder=\"Required field\" />"
        },
        {
          "title": "Character Counter",
          "code": "<script>\n  import { Textarea, Label } from '@classic-homes/theme-svelte';\n\n  let bio = $state('');\n  const maxLength = 280;\n</script>\n\n<div class=\"space-y-2\">\n  <Label for=\"bio\">Bio</Label>\n  <Textarea id=\"bio\" bind:value={bio} rows={4} placeholder=\"Tell us about yourself...\" />\n  <p class=\"text-sm text-muted-foreground text-right\">\n    {bio.length}/{maxLength}\n  </p>\n</div>"
        },
        {
          "title": "With Help Text",
          "code": "<div class=\"space-y-2\">\n  <Label for=\"description\">Description</Label>\n  <Textarea id=\"description\" rows={4} placeholder=\"Describe your item...\" />\n  <p class=\"text-sm text-muted-foreground\">\n    Include key details to help buyers understand your item.\n  </p>\n</div>"
        },
        {
          "title": "Fixed Height",
          "code": "<Textarea class=\"resize-none h-32\" placeholder=\"Fixed height textarea\" />"
        },
        {
          "title": "Full Width in Container",
          "code": "<div class=\"max-w-md\">\n  <Textarea placeholder=\"Constrained to container width\" />\n</div>"
        }
      ],
      "relatedComponents": [
        "Input",
        "Label",
        "FormField"
      ]
    },
    {
      "name": "Toast",
      "description": "Brief notification messages for user feedback",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte",
      "props": [],
      "variants": [],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Success",
          "description": "For successful operations:",
          "code": "toastStore.success('Changes saved successfully!');"
        },
        {
          "title": "Error",
          "description": "For errors and failures:",
          "code": "toastStore.error('Failed to save changes. Please try again.');"
        },
        {
          "title": "Warning",
          "description": "For warnings and cautions:",
          "code": "toastStore.warning('Your session will expire in 5 minutes.');"
        },
        {
          "title": "Info",
          "description": "For informational messages:",
          "code": "toastStore.info('New version available. Refresh to update.');"
        },
        {
          "title": "Custom",
          "description": "For custom styled toasts:",
          "code": "toastStore.show({\n  message: 'Custom message',\n  type: 'success',\n  duration: 5000,\n});"
        },
        {
          "title": "Form Submission",
          "code": "<script>\n  async function handleSubmit() {\n    try {\n      await saveForm();\n      toastStore.success('Form submitted successfully!');\n    } catch (error) {\n      toastStore.error('Failed to submit form. Please try again.');\n    }\n  }\n</script>"
        },
        {
          "title": "Action with Undo",
          "code": "<script>\n  function handleDelete(item) {\n    deleteItem(item.id);\n    toastStore.show({\n      message: `\"${item.name}\" deleted`,\n      type: 'info',\n      action: {\n        label: 'Undo',\n        onClick: () => restoreItem(item),\n      },\n    });\n  }\n</script>"
        },
        {
          "title": "Network Status",
          "code": "<script>\n  $effect(() => {\n    if (!navigator.onLine) {\n      toastStore.warning('You are offline. Changes will sync when connected.');\n    } else {\n      toastStore.success('Back online!');\n    }\n  });\n</script>"
        },
        {
          "title": "Async Operation",
          "code": "<script>\n  async function handleExport() {\n    toastStore.info('Starting export...');\n    try {\n      await exportData();\n      toastStore.success('Export completed! Check your downloads.');\n    } catch {\n      toastStore.error('Export failed. Please try again.');\n    }\n  }\n</script>"
        },
        {
          "title": "Copy to Clipboard",
          "code": "<script>\n  async function copyLink() {\n    await navigator.clipboard.writeText(shareUrl);\n    toastStore.success('Link copied to clipboard!');\n  }\n</script>\n\n<Button onclick={copyLink}>Copy Link</Button>"
        },
        {
          "title": "Multiple Toasts",
          "description": "Multiple toasts stack and are dismissed independently:",
          "code": "<Button\n  onclick={() => {\n    toastStore.success('First message');\n    toastStore.info('Second message');\n    toastStore.warning('Third message');\n  }}\n>\n  Show Multiple\n</Button>"
        }
      ],
      "relatedComponents": [
        "Alert",
        "Dialog"
      ]
    },
    {
      "name": "ToastContainer",
      "description": "Container component for displaying toast notifications",
      "category": "feedback",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "position",
          "type": "'top-left' | 'top-right' | 'top-center' | 'bottom-left' | 'bottom-right' | 'bottom-center'",
          "default": "'bottom-right'",
          "required": false,
          "description": "Position on screen"
        },
        {
          "name": "class",
          "type": "string",
          "required": false,
          "description": "Additional CSS classes"
        }
      ],
      "variants": [
        {
          "name": "position",
          "values": [
            "top-left",
            "top-right",
            "top-center",
            "bottom-left",
            "bottom-right",
            "bottom-center"
          ],
          "default": "bottom-right"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Bottom Right (Default)",
          "code": "<ToastContainer position=\"bottom-right\" />"
        },
        {
          "title": "Top Right",
          "code": "<ToastContainer position=\"top-right\" />"
        },
        {
          "title": "Top Center",
          "code": "<ToastContainer position=\"top-center\" />"
        },
        {
          "title": "Bottom Center",
          "code": "<ToastContainer position=\"bottom-center\" />"
        },
        {
          "title": "Custom Position",
          "code": "<ToastContainer position=\"top-center\" class=\"mt-16\" />"
        }
      ],
      "relatedComponents": [
        "Toast",
        "AppShell"
      ]
    },
    {
      "name": "Tooltip",
      "description": "Contextual information displayed on hover",
      "category": "overlay",
      "importPath": "@classic-homes/theme-svelte",
      "props": [
        {
          "name": "content",
          "type": "string",
          "default": "Required",
          "required": false,
          "description": "Text content of the tooltip"
        },
        {
          "name": "side",
          "type": "'top' | 'right' | 'bottom' | 'left'",
          "default": "'top'",
          "required": false,
          "description": "Preferred side for placement"
        },
        {
          "name": "align",
          "type": "'start' | 'center' | 'end'",
          "default": "'center'",
          "required": false,
          "description": "Alignment along the side"
        },
        {
          "name": "delayDuration",
          "type": "number",
          "required": false,
          "description": "Delay before showing (ms)"
        }
      ],
      "variants": [
        {
          "name": "side",
          "values": [
            "top",
            "right",
            "bottom",
            "left"
          ],
          "default": "top"
        },
        {
          "name": "align",
          "values": [
            "start",
            "center",
            "end"
          ],
          "default": "center"
        }
      ],
      "slots": [
        {
          "name": "children",
          "description": "Main content",
          "required": false
        }
      ],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<Tooltip content=\"This is a tooltip\">\n  {#snippet children()}\n    <Button>Hover me</Button>\n  {/snippet}\n</Tooltip>"
        },
        {
          "title": "Side",
          "description": "Control which side the tooltip appears on:",
          "code": "<Tooltip content=\"Top tooltip\" side=\"top\">\n  {#snippet children()}<Button>Top</Button>{/snippet}\n</Tooltip>\n\n<Tooltip content=\"Right tooltip\" side=\"right\">\n  {#snippet children()}<Button>Right</Button>{/snippet}\n</Tooltip>\n\n<Tooltip content=\"Bottom tooltip\" side=\"bottom\">\n  {#snippet children()}<Button>Bottom</Button>{/snippet}\n</Tooltip>\n\n<Tooltip content=\"Left tooltip\" side=\"left\">\n  {#snippet children()}<Button>Left</Button>{/snippet}\n</Tooltip>"
        },
        {
          "title": "Alignment",
          "description": "Adjust alignment along the side:",
          "code": "<Tooltip content=\"Aligned to start\" side=\"top\" align=\"start\">\n  {#snippet children()}<Button>Start</Button>{/snippet}\n</Tooltip>\n\n<Tooltip content=\"Aligned to center\" side=\"top\" align=\"center\">\n  {#snippet children()}<Button>Center</Button>{/snippet}\n</Tooltip>\n\n<Tooltip content=\"Aligned to end\" side=\"top\" align=\"end\">\n  {#snippet children()}<Button>End</Button>{/snippet}\n</Tooltip>"
        },
        {
          "title": "Icon Button with Tooltip",
          "code": "<Tooltip content=\"Edit item\">\n  {#snippet children()}\n    <Button variant=\"ghost\" size=\"icon\" aria-label=\"Edit\">\n      <svg class=\"h-4 w-4\"><!-- edit icon --></svg>\n    </Button>\n  {/snippet}\n</Tooltip>"
        },
        {
          "title": "Form Field Help",
          "code": "<div class=\"flex items-center gap-2\">\n  <Label for=\"password\">Password</Label>\n  <Tooltip content=\"Password must be at least 8 characters with one uppercase letter\">\n    {#snippet children()}\n      <svg class=\"h-4 w-4 text-muted-foreground cursor-help\">\n        <circle cx=\"12\" cy=\"12\" r=\"10\" stroke=\"currentColor\" stroke-width=\"2\" fill=\"none\" />\n        <path d=\"M12 16v-4M12 8h.01\" stroke=\"currentColor\" stroke-width=\"2\" />\n      </svg>\n    {/snippet}\n  </Tooltip>\n</div>"
        },
        {
          "title": "Truncated Text",
          "code": "<Tooltip content={fullText}>\n  {#snippet children()}\n    <span class=\"truncate max-w-xs block\">{fullText}</span>\n  {/snippet}\n</Tooltip>"
        },
        {
          "title": "Disabled Button Explanation",
          "code": "<Tooltip content=\"You need admin permissions to perform this action\">\n  {#snippet children()}\n    <span>\n      <Button disabled>Delete</Button>\n    </span>\n  {/snippet}\n</Tooltip>"
        },
        {
          "title": "Status Indicator",
          "code": "<Tooltip content=\"Online - Last seen 2 minutes ago\" side=\"right\">\n  {#snippet children()}\n    <span class=\"inline-flex h-2 w-2 rounded-full bg-green-500\"></span>\n  {/snippet}\n</Tooltip>"
        },
        {
          "title": "Keyboard Shortcut",
          "code": "<Tooltip content=\"Save (Ctrl+S)\">\n  {#snippet children()}\n    <Button>Save</Button>\n  {/snippet}\n</Tooltip>"
        },
        {
          "title": "Multiple Tooltips",
          "code": "<div class=\"flex gap-2\">\n  <Tooltip content=\"Bold\">\n    {#snippet children()}<Button variant=\"ghost\" size=\"icon\">B</Button>{/snippet}\n  </Tooltip>\n  <Tooltip content=\"Italic\">\n    {#snippet children()}<Button variant=\"ghost\" size=\"icon\">I</Button>{/snippet}\n  </Tooltip>\n  <Tooltip content=\"Underline\">\n    {#snippet children()}<Button variant=\"ghost\" size=\"icon\">U</Button>{/snippet}\n  </Tooltip>\n</div>"
        }
      ],
      "relatedComponents": [
        "Dialog",
        "DropdownMenu"
      ]
    },
    {
      "name": "TreemapChart",
      "description": "Hierarchical data as nested rectangles",
      "category": "chart",
      "importPath": "@classic-homes/charts-svelte",
      "props": [
        {
          "name": "data",
          "type": "TreemapNode[]",
          "required": true,
          "description": "Hierarchical tree data"
        },
        {
          "name": "levels",
          "type": "TreemapLevel[]",
          "required": false,
          "description": "Visual configuration for each level"
        },
        {
          "name": "onClick",
          "type": "(params: TreemapEventParams) => void",
          "required": false,
          "description": "Callback when clicked"
        },
        {
          "name": "title",
          "type": "string",
          "required": true,
          "description": "Chart title for accessibility"
        },
        {
          "name": "height",
          "type": "number | string",
          "default": "400",
          "required": false,
          "description": "Chart height"
        },
        {
          "name": "theme",
          "type": "'light' | 'dark' | 'auto'",
          "default": "'auto'",
          "required": false,
          "description": "Theme preference"
        },
        {
          "name": "showTooltip",
          "type": "boolean",
          "default": "true",
          "required": false,
          "description": "Show tooltip on hover"
        }
      ],
      "variants": [
        {
          "name": "theme",
          "values": [
            "light",
            "dark",
            "auto"
          ],
          "default": "auto"
        }
      ],
      "slots": [],
      "events": [],
      "examples": [
        {
          "title": "Basic Usage",
          "code": "<script>\n  import { TreemapChart } from '@classic-homes/charts-svelte';\n\n  const data = [\n    {\n      name: 'Frontend',\n      children: [\n        { name: 'React', value: 40 },\n        { name: 'Vue', value: 30 },\n        { name: 'Svelte', value: 20 },\n      ],\n    },\n    {\n      name: 'Backend',\n      children: [\n        { name: 'Node.js', value: 35 },\n        { name: 'Python', value: 25 },\n      ],\n    },\n  ];\n</script>\n\n<TreemapChart title=\"Tech Stack\" {data} height={400} />"
        },
        {
          "title": "File System",
          "code": "<TreemapChart\n  title=\"Disk Usage\"\n  data={[\n    {\n      name: 'Documents',\n      children: [\n        { name: 'PDFs', value: 500 },\n        { name: 'Images', value: 300 },\n        { name: 'Videos', value: 2000 },\n      ],\n    },\n    {\n      name: 'Applications',\n      children: [\n        { name: 'Development', value: 1500 },\n        { name: 'Productivity', value: 800 },\n      ],\n    },\n  ]}\n/>"
        },
        {
          "title": "Budget Breakdown",
          "code": "<TreemapChart\n  title=\"Department Budget\"\n  data={[\n    {\n      name: 'Engineering',\n      children: [\n        { name: 'Salaries', value: 500000 },\n        { name: 'Equipment', value: 100000 },\n        { name: 'Training', value: 50000 },\n      ],\n    },\n    {\n      name: 'Marketing',\n      children: [\n        { name: 'Advertising', value: 200000 },\n        { name: 'Events', value: 75000 },\n      ],\n    },\n  ]}\n/>"
        }
      ],
      "relatedComponents": [
        "PieChart",
        "SankeyChart"
      ]
    }
  ]
}